import React, { useState, useEffect, useRef } from 'react';
// Remove jsPDF import for PDF download
// Remove jsPDF, add PDFKit
import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import { useAuth } from '../../context/auth/AuthContext';
import { API_BASE_URL } from '../../config';
import { RetellWebClient } from 'retell-client-js-sdk';
import CustomSlider from './CustomSlider';
import ReactDOM from 'react-dom';
import AIAssistant from '../../components/AIAssistant'; // COMMENTED OUT - Component disabled but import needed for code parsing

const accordionItems = [
  'Functions',
  'Knowledge Base',
  'Speech Settings',
  'Transcription Settings',
  'Call Settings',
  'Post-Call Analysis',
  'Verify Numbers',
  // 'MCP Server', // Commented out as requested
  'Security & Fallback Settings',
  'Webhook Settings',
];

// Add icon mapping for each accordion item
const accordionIcons = {
  'Functions': 'fas fa-th-large',
  'Knowledge Base': 'fas fa-book',
  'Multi-Prompt Backend': 'fas fa-sitemap',
  'Speech Settings': 'fas fa-volume-up',
  'Transcription Settings': 'fas fa-language',
  'Call Settings': 'fas fa-headphones',
  'Post-Call Analysis': 'fas fa-chart-line',
  'Verify Numbers': 'fas fa-check-circle',
  // 'MCP Server': 'fas fa-server', // Commented out as requested
  'Security & Fallback Settings': 'fas fa-shield-alt',
  'Webhook Settings': 'fas fa-cube',
};

const AgentDetails = (props) => {
  const { userEmail, userId, userName } = useAuth();
  // Try to get agent from props or fetch by agentId from URL
  const [agent, setAgent] = useState(props.agent || null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState('');
  // If using react-router, get agentId from URL
  // const { agentId } = useParams();
  // If you use window.location, parse agentId from path
  const agentId = window.location.pathname.startsWith('/agent/') ? window.location.pathname.split('/agent/')[1] : null;

  useEffect(() => {
    if (!agent && agentId) {
      setAgentLoading(true);
      fetch(`${API_BASE_URL}/api/agents`)
        .then(res => res.json())
        .then(data => {
          const found = Array.isArray(data) ? data.find(a => a.agent_id === agentId) : null;
          if (found) setAgent(found);
          else setAgentError('Agent not found');
        })
        .catch(() => setAgentError('Failed to fetch agent info'))
        .finally(() => setAgentLoading(false));
    }
  }, [agent, agentId]);

  // Fetch verified phone numbers and connection status when agent is loaded
  useEffect(() => {
    if (agent?.agent_id && userId) {
      fetchVerifiedPhoneNumbers();
      fetchVerifiedNumbersConnectionStatus();
      fetchAgentPhoneNumbers();
    }
  }, [agent?.agent_id, userId]);

  const [openIndex, setOpenIndex] = useState(null);
  // Ref for model dropdown
  const modelDropdownRef = useRef(null);
  // Ref for voice dropdown
  const voiceDropdownRef = useRef(null);
  // Refs for tool dropdowns (one per state)
  const toolDropdownRefs = useRef({});
  // Ref for language dropdown
  const languageDropdownRef = useRef(null);
  const [selectedVoice, setSelectedVoice] = useState('Anthony');
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  // Add state for open inner transcription
  const [openTranscription, setOpenTranscription] = useState(null);
  
  // Add state for real model data
  const [availableModels, setAvailableModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(null);
  const [updatingModel, setUpdatingModel] = useState(false);
  
  // Add state for real voice data
  const [availableVoices, setAvailableVoices] = useState([]);
  const [currentVoice, setCurrentVoice] = useState(null);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicesError, setVoicesError] = useState(null);
  const [updatingVoice, setUpdatingVoice] = useState(false);
  
  // Add state for real language data
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState(null);
  const [updatingLanguage, setUpdatingLanguage] = useState(false);
  
  // Add state for verified numbers connection
  const [verifiedNumbersConnection, setVerifiedNumbersConnection] = useState('disconnected');
  const [verifiedNumbersLoading, setVerifiedNumbersLoading] = useState(false);
  
  // Add state for inbound agent phone number
  const [inboundAgentPhoneNumber, setInboundAgentPhoneNumber] = useState(null);
  const [inboundAgentPhoneLoading, setInboundAgentPhoneLoading] = useState(false);
  
  // Add state for verified phone numbers list
  const [verifiedPhoneNumbersList, setVerifiedPhoneNumbersList] = useState([]);
  const [verifiedPhoneNumbersListLoading, setVerifiedPhoneNumbersListLoading] = useState(false);
  const [verifiedPhoneNumbersListError, setVerifiedPhoneNumbersListError] = useState(null);
  
  // Add state for agent phone numbers
  const [agentPhoneNumbers, setAgentPhoneNumbers] = useState([]);
  const [agentPhoneNumbersLoading, setAgentPhoneNumbersLoading] = useState(false);
  const [agentPhoneNumbersError, setAgentPhoneNumbersError] = useState(null);
  
  // Add state for function dropdown
  const [functionDropdownOpen, setFunctionDropdownOpen] = useState(false);
  
  // Shared dropdown options for both Add Function and Add Tool
  const sharedDropdownOptions = [
    { label: 'End Call', value: 'end_call', functionType: 'end_call', toolType: 'end_call' },
    { label: 'Call Transfer', value: 'call_transfer', functionType: 'call_transfer', toolType: 'transfer_call' },
    { label: 'Agent Transfer', value: 'agent_transfer', functionType: 'agent_transfer', toolType: 'agent_transfer' },
    { label: 'Check Calendar Availability (Cal.com)', value: 'check_calendar', functionType: 'check_calendar', toolType: 'check_calendar' },
    { label: 'Book on the Calendar (Cal.com)', value: 'book_calendar', functionType: 'book_calendar', toolType: 'book_calendar' },
    { label: 'Press Digit (IVR Navigation)', value: 'press_digit', functionType: 'press_digit', toolType: 'press_digit' },
    { label: 'Send SMS', value: 'send_sms', functionType: 'send_sms', toolType: 'send_sms' },
    { label: 'Extract Dynamic Variable', value: 'extract_variable', functionType: 'extract_variable', toolType: 'extract_variable' },
    { label: 'Custom Function', value: 'custom', functionType: 'custom', toolType: 'custom' }
  ];
  
  // Add state for function configuration modal
  const [functionConfigModalOpen, setFunctionConfigModalOpen] = useState(false);
  // Unified modal mode: 'function' or 'tool'
  const [unifiedModalMode, setUnifiedModalMode] = useState(null); // 'function' or 'tool'
  const [unifiedModalStateIndex, setUnifiedModalStateIndex] = useState(null); // For tool mode
  const [selectedFunctionType, setSelectedFunctionType] = useState('');
  const [functionConfig, setFunctionConfig] = useState({
    name: '',
    description: '',
    transfer_to: 'static', // static, dynamic
    destination_number: '',
    transfer_type: 'cold', // cold, warm
    displayed_phone_number: 'retell_agent', // retell_agent, transferee
    custom_sip_headers: [],
    tool_name: 'transfer_call',
    tool_description: 'Transfer the call to support when the user needs human assistance.',
    pause_detection_delay: '1000', // Default pause detection delay for press_digit
    sms_content: '' // SMS content for send_sms
  });

  // Add state for editing existing functions
  const [editFunctionModalOpen, setEditFunctionModalOpen] = useState(false);
  const [editingFunctionIndex, setEditingFunctionIndex] = useState(null);
  const [editFunctionConfig, setEditFunctionConfig] = useState({
    name: '',
    description: '',
    transfer_to: 'static',
    destination_number: '',
    transfer_type: 'cold',
    displayed_phone_number: 'retell_agent',
    custom_sip_headers: [],
    tool_name: '',
    tool_description: ''
  });

  // Transfer call popup state removed - no longer needed

  // Add state for Agent Transfer specific configuration
  const [agentTransferConfig, setAgentTransferConfig] = useState({
    function_name: 'agent_transfer',
    description: '',
    selected_agent: '',
    speak_during_execution: false,
    post_call_analysis: 'transferred_agent_only' // 'transferred_agent_only', 'both_agents'
  });

  // Add state for available agents (for dropdown)
  const [availableAgents, setAvailableAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Add state for Multi-Prompt Backend tool configuration modal
  const [toolConfigModalOpen, setToolConfigModalOpen] = useState(false);
  const [selectedToolType, setSelectedToolType] = useState('');
  const [selectedStateIndex, setSelectedStateIndex] = useState(null);
  const [toolConfig, setToolConfig] = useState({
    name: '',
    description: '',
    transfer_to: 'static', // static, dynamic
    destination_number: '',
    transfer_type: 'cold', // cold, warm
    displayed_phone_number: 'retell_agent', // retell_agent, transferee
    custom_sip_headers: [],
    tool_name: 'transfer_call',
    tool_description: 'Transfer the call to support when the user needs human assistance.'
  });
  
  // Add protection against modal auto-close
  const [modalOpenTime, setModalOpenTime] = useState(null);
  const [isModalProtected, setIsModalProtected] = useState(false);

  // Add state for transfer call results
  const [transferCallResult, setTransferCallResult] = useState(null);
  const [showTransferResult, setShowTransferResult] = useState(false);
  
  // Add state for transfer call phone number
  const [transferPhoneNumber, setTransferPhoneNumber] = useState(null);
  const [transferPhoneNumberLoading, setTransferPhoneNumberLoading] = useState(false);

  // Add state for editing agent name
  const [isEditingAgentName, setIsEditingAgentName] = useState(false);
  const [editedAgentName, setEditedAgentName] = useState('');
  const [updatingAgentName, setUpdatingAgentName] = useState(false);

  // Function to refresh LLM data after transfer call tool addition
  const refreshLLMData = async () => {
    if (!agent?.agent_id) return;
    
    try {
      console.log('[Transfer Call] Refreshing LLM data after tool addition');
      const response = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/llm`);
      
      if (!response.ok) {
        throw new Error('Failed to refresh LLM data');
      }
      
      const data = await response.json();
      console.log('[Transfer Call] Refreshed LLM data:', data);
      
      // Update the multi-prompt data with fresh data from backend
      setMultiPromptData({
        starting_state: data.starting_state || 'intro',
        general_prompt: data.general_prompt || '',
        general_tools: data.general_tools || [],
        states: data.states || []
      });
      
      console.log('[Transfer Call] Multi-prompt data updated with fresh LLM configuration');
      
    } catch (error) {
      console.error('[Transfer Call] Error refreshing LLM data:', error);
    }
  };

  // Function to fetch connection status for verified numbers
  const fetchVerifiedNumbersConnectionStatus = async () => {
    if (!agent?.agent_id || !userId) {
      console.log('Cannot fetch connection status: missing agent_id or userId');
      return;
    }

    try {
      console.log(`ðŸ”— Fetching connection status for agent: ${agent.agent_id}, user: ${userId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/verified-numbers/status?user_id=${encodeURIComponent(userId)}`);
      
      if (!response.ok) {
        console.log('Connection status check failed, assuming disconnected');
        setVerifiedNumbersConnection('disconnected');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        const isConnected = (data.is_connected ?? data.isConnected) ? true : false;
        setVerifiedNumbersConnection(isConnected ? 'connected' : 'disconnected');
        console.log(`ðŸ”— Connection status: ${isConnected ? 'connected' : 'disconnected'}`);
      } else {
        setVerifiedNumbersConnection('disconnected');
      }
      
    } catch (error) {
      console.error('âŒ Error fetching connection status:', error);
      setVerifiedNumbersConnection('disconnected');
    }
  };

  // Function to fetch verified phone numbers for the current agent
  const fetchVerifiedPhoneNumbers = async () => {
    if (!agent?.agent_id || !userId) {
      console.log('Cannot fetch verified phone numbers: missing agent_id or userId');
      return;
    }

    setVerifiedPhoneNumbersListLoading(true);
    setVerifiedPhoneNumbersListError(null);

    try {
      console.log(`ðŸ“ž Fetching verified phone numbers for agent: ${agent.agent_id}, user: ${userId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/verified-numbers?user_id=${encodeURIComponent(userId)}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch verified phone numbers: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // The /api/verified-numbers endpoint returns an array of verified numbers directly
      if (Array.isArray(data)) {
        setVerifiedPhoneNumbersList(data);
        console.log(`ðŸ“ž Successfully fetched ${data.length} verified phone numbers:`, data);
      } else {
        throw new Error('Invalid response format from verified numbers API');
      }
      
    } catch (error) {
      console.error('âŒ Error fetching verified phone numbers:', error);
      setVerifiedPhoneNumbersListError(error.message);
      setVerifiedPhoneNumbersList([]);
    } finally {
      setVerifiedPhoneNumbersListLoading(false);
    }
  };

  const fetchAgentPhoneNumbers = async () => {
    if (!agent?.agent_id || !userId) {
      console.log('Cannot fetch agent phone numbers: missing agent_id or userId');
      return;
    }

    setAgentPhoneNumbersLoading(true);
    setAgentPhoneNumbersError(null);

    try {
      console.log(`📞 Fetching agent phone numbers for agent: ${agent.agent_id}, user: ${userId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/phone-numbers?user_id=${encodeURIComponent(userId)}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch agent phone numbers: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // The endpoint returns an array of phone numbers with type
      if (Array.isArray(data)) {
        setAgentPhoneNumbers(data);
        console.log(`📞 Successfully fetched ${data.length} agent phone numbers:`, data);
      } else {
        throw new Error('Invalid response format from agent phone numbers API');
      }
      
    } catch (error) {
      console.error('❌ Error fetching agent phone numbers:', error);
      setAgentPhoneNumbersError(error.message);
      setAgentPhoneNumbers([]);
    } finally {
      setAgentPhoneNumbersLoading(false);
    }
  };

  // Protected modal state setter to prevent unauthorized closing
  const setToolConfigModalOpenProtected = (isOpen) => {
    if (!isOpen && isModalProtected && modalOpenTime) {
      const timeSinceOpen = Date.now() - modalOpenTime;
      if (timeSinceOpen < 3000) {
        console.warn('[Transfer Modal] Attempted to close modal too early. Time since open:', timeSinceOpen + 'ms');
        console.warn('[Transfer Modal] Modal close request blocked by protection mechanism');
        return; // Block the close attempt
      }
    }
    
    console.log('[Transfer Modal] Setting modal state to:', isOpen);
    setToolConfigModalOpen(isOpen);
  };
  
  // Add state for speech settings
  const [speechSettings, setSpeechSettings] = useState({
    responsiveness: 1.0,
    interruption_sensitivity: 1.0,
    enable_backchannel: false,
    enable_speech_normalization: false,
    reminder_message_frequency: { interval: 10, times: 1 },
    pronunciation: null
  });
  const [speechSettingsLoading, setSpeechSettingsLoading] = useState(false);
  const [speechSettingsError, setSpeechSettingsError] = useState(null);
  const [updatingSpeechSettings, setUpdatingSpeechSettings] = useState(false);
  
  // Add state for transcription settings
  const [transcriptionSettings, setTranscriptionSettings] = useState({
    denoising_mode: 'noise-cancellation',
    stt_mode: 'fast',
    vocab_specialization: 'general',
    boosted_keywords: []
  });
  const [transcriptionSettingsLoading, setTranscriptionSettingsLoading] = useState(false);
  const [transcriptionSettingsError, setTranscriptionSettingsError] = useState(null);
  const [updatingTranscriptionSettings, setUpdatingTranscriptionSettings] = useState(false);
  
  // Add state for call settings
  const [callSettings, setCallSettings] = useState({
    voicemail_detection: false,
    keypad_input_detection: false,
    keypad_input_timeout: 2.5,
    keypad_input_termination_key: false,
    keypad_input_digit_limit: false,
    silence_timeout: 600, // 10 minutes in seconds
    max_call_duration: 3600, // 1 hour in seconds
    pause_before_speaking: 0,
    ring_duration: 30
  });
  const [callSettingsLoading, setCallSettingsLoading] = useState(false);
  const [callSettingsError, setCallSettingsError] = useState(null);
  const [updatingCallSettings, setUpdatingCallSettings] = useState(false);
  
  // Add state for assistant panel
  const [assistantPanelOpen, setAssistantPanelOpen] = useState(false);
  
  // Add state for real transcript data
  const [transcripts, setTranscripts] = useState([]);
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptsError, setTranscriptsError] = useState(null);

  // Remove modal state
  // Add inline web call state
  const [callStatus, setCallStatus] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const retellClient = useRef(null);
  const [attachedKBs, setAttachedKBs] = useState([]);
  const [loadingKBs, setLoadingKBs] = useState(false);
  const [kbError, setKbError] = useState('');
  
  // Knowledge base dropdown state
  const [kbDropdownOpen, setKbDropdownOpen] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const kbDropdownRef = useRef(null);
  
  // Admin KB modal state
  const [adminKBModal, setAdminKBModal] = useState(false);
  const [selectedAgentForKB, setSelectedAgentForKB] = useState('');
  const [adminKBForm, setAdminKBForm] = useState({
    topic: '',
    content: '',
    tags: '',
    source_url: ''
  });
  const [adminKBSaving, setAdminKBSaving] = useState(false);
  const [adminKBError, setAdminKBError] = useState('');
  const [adminKBSuccess, setAdminKBSuccess] = useState('');
  const [adminKBFileUpload, setAdminKBFileUpload] = useState(false);
  const [adminKBUploadFiles, setAdminKBUploadFiles] = useState([]);
  const [adminKBUploading, setAdminKBUploading] = useState(false);
  const [userAgents, setUserAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);
  const [callHistoryError, setCallHistoryError] = useState(null);

  // Add state for post-call analysis config
  const [postCallAnalysisConfig, setPostCallAnalysisConfig] = useState({
    post_call_analysis_data: [],
    post_call_analysis_model: ''
  });
  const [postCallAnalysisLoading, setPostCallAnalysisLoading] = useState(false);
  const [postCallAnalysisError, setPostCallAnalysisError] = useState(null);
  const [updatingPostCallAnalysis, setUpdatingPostCallAnalysis] = useState(false);

  // Add state for Multi-Prompt Backend
  const [multiPromptData, setMultiPromptData] = useState({
    starting_state: 'intro',
    general_prompt: '',
    general_tools: [],
    states: []
  });
  const [multiPromptLoading, setMultiPromptLoading] = useState(false);
  const [multiPromptError, setMultiPromptError] = useState(null);
  const [updatingMultiPrompt, setUpdatingMultiPrompt] = useState(false);

  // Add state for pronunciation modal
  const [pronunciationModalOpen, setPronunciationModalOpen] = useState(false);
  const [pronunciationWord, setPronunciationWord] = useState('');
  const [pronunciationAlphabet, setPronunciationAlphabet] = useState('ipa');
  const [pronunciationPhoneme, setPronunciationPhoneme] = useState('');

  // Add state for agent estimates
  const [agentEstimates, setAgentEstimates] = useState(null);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const [estimatesError, setEstimatesError] = useState(null);

  // Tooltip state for cost breakdown
  const [showCostTooltip, setShowCostTooltip] = useState(false);

  // Add after other useState hooks
  const [openMainSection, setOpenMainSection] = useState('multiPrompt');
  
  // Add ref for function dropdown
  const functionDropdownRef = useRef(null);

  // Speech settings local state for debounce
  const [localResponsiveness, setLocalResponsiveness] = useState(speechSettings.responsiveness);
  const [localInterruption, setLocalInterruption] = useState(speechSettings.interruption_sensitivity);
  const [localBackchanneling, setLocalBackchanneling] = useState(speechSettings.enable_backchannel);
  const [localSpeechNormalization, setLocalSpeechNormalization] = useState(speechSettings.enable_speech_normalization);
  const [localReminder, setLocalReminder] = useState({
    interval: speechSettings.reminder_message_frequency?.interval || 10,
    times: (speechSettings.reminder_message_frequency?.times ?? 1).toString()
  });
  const [reminderDirty, setReminderDirty] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);

  // Add local state for call settings sliders
  const [localSilenceTimeout, setLocalSilenceTimeout] = useState(callSettings.silence_timeout);
  const [localMaxCallDuration, setLocalMaxCallDuration] = useState(callSettings.max_call_duration);
  const [localPauseBeforeSpeaking, setLocalPauseBeforeSpeaking] = useState(callSettings.pause_before_speaking);
  const [localRingDuration, setLocalRingDuration] = useState(callSettings.ring_duration);

  // Add local state for call settings checkboxes
  const [localVoicemailDetection, setLocalVoicemailDetection] = useState(callSettings.voicemail_detection);
  const [localKeypadInputDetection, setLocalKeypadInputDetection] = useState(callSettings.keypad_input_detection);

  // Sync local state with backend when backend value changes
  useEffect(() => { setLocalSilenceTimeout(callSettings.silence_timeout); }, [callSettings.silence_timeout]);
  useEffect(() => { setLocalMaxCallDuration(callSettings.max_call_duration); }, [callSettings.max_call_duration]);
  useEffect(() => { setLocalPauseBeforeSpeaking(callSettings.pause_before_speaking); }, [callSettings.pause_before_speaking]);
  useEffect(() => { setLocalRingDuration(callSettings.ring_duration); }, [callSettings.ring_duration]);
  useEffect(() => { setLocalVoicemailDetection(callSettings.voicemail_detection); }, [callSettings.voicemail_detection]);
  useEffect(() => { setLocalKeypadInputDetection(callSettings.keypad_input_detection); }, [callSettings.keypad_input_detection]);

  // Debounce sync to backend
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSilenceTimeout !== callSettings.silence_timeout) {
        handleCallSettingsUpdate({ silence_timeout: localSilenceTimeout });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localSilenceTimeout]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localMaxCallDuration !== callSettings.max_call_duration) {
        handleCallSettingsUpdate({ max_call_duration: localMaxCallDuration });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localMaxCallDuration]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localPauseBeforeSpeaking !== callSettings.pause_before_speaking) {
        handleCallSettingsUpdate({ pause_before_speaking: localPauseBeforeSpeaking });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localPauseBeforeSpeaking]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localRingDuration !== callSettings.ring_duration) {
        handleCallSettingsUpdate({ ring_duration: localRingDuration });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localRingDuration]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localVoicemailDetection !== callSettings.voicemail_detection) {
        handleCallSettingsUpdate({ voicemail_detection: localVoicemailDetection });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localVoicemailDetection]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localKeypadInputDetection !== callSettings.keypad_input_detection) {
        handleCallSettingsUpdate({ keypad_input_detection: localKeypadInputDetection });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localKeypadInputDetection]);

  const costRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (showCostTooltip && costRef.current) {
      const rect = costRef.current.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + window.scrollY + 8, // 8px below
        left: rect.left + window.scrollX
      });
    }
  }, [showCostTooltip]);

  useEffect(() => {
    if (userEmail && agent?.agent_id) {
      setCallHistoryLoading(true);
      setCallHistoryError(null);
      fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(userEmail)}&agent_id=${encodeURIComponent(agent.agent_id)}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) setCallHistory(data);
          else setCallHistoryError('Invalid response format');
        })
        .catch(() => setCallHistoryError('Failed to fetch call history'))
        .finally(() => setCallHistoryLoading(false));
    }
  }, [userEmail, agent?.agent_id]);

  // Fake voice data
  const voices = [
    { name: 'Adrian', avatar: 'https://randomuser.me/api/portraits/men/1.jpg', traits: ['American', 'Young', 'Retell'], id: '11labs-Adrian' },
    { name: 'Amritanshu (en-IN)', avatar: 'https://randomuser.me/api/portraits/men/2.jpg', traits: ['Indian', 'Middle Aged', 'Provider'], id: '11labs-Amritanshu' },
    { name: 'Amy(UK)', avatar: 'https://randomuser.me/api/portraits/women/1.jpg', traits: ['British', 'Young', 'Provider'], id: '11labs-Amy' },
    { name: 'Andrew', avatar: 'https://randomuser.me/api/portraits/men/3.jpg', traits: ['American', 'Young', 'Retell'], id: '11labs-Andrew' },
    { name: 'Anna', avatar: 'https://randomuser.me/api/portraits/women/2.jpg', traits: ['American', 'Young', 'Retell'], id: '11labs-Anna' },
    { name: 'Anthony', avatar: 'https://randomuser.me/api/portraits/men/4.jpg', traits: ['British', 'Middle Aged', 'Retell'], id: '11labs-Anthony' },
    { name: 'Billy', avatar: 'https://randomuser.me/api/portraits/men/5.jpg', traits: ['American', 'Young', 'Retell'], id: '11labs-Billy' },
  ];


  // Fetch transcripts for this agent
  useEffect(() => {
    if (agent?.agent_id && userEmail) {
      setTranscriptsLoading(true);
      setTranscriptsError(null);
      
      // Use cached-calls endpoint for faster loading
      fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(userEmail)}&agent_id=${encodeURIComponent(agent.agent_id)}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setTranscripts(data);
          } else {
            setTranscriptsError('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching transcripts:', err);
          setTranscriptsError('Failed to fetch transcripts');
        })
        .finally(() => {
          setTranscriptsLoading(false);
        });
    }
  }, [agent?.agent_id, userEmail]);

  // Fetch agent estimates
  useEffect(() => {
    if (agent?.agent_id) {
      setEstimatesLoading(true);
      setEstimatesError(null);
      
      fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/estimates`)
        .then(res => res.json())
        .then(data => {
          if (data.estimates) {
            setAgentEstimates(data);
          } else {
            setEstimatesError('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching agent estimates:', err);
          setEstimatesError('Failed to fetch estimates');
        })
        .finally(() => {
          setEstimatesLoading(false);
        });
    }
  }, [agent?.agent_id]);

  // Fetch Multi-Prompt Backend data
  useEffect(() => {
    if (agent?.agent_id) {
      setMultiPromptLoading(true);
      setMultiPromptError(null);
      
      console.log('[MultiPrompt] Fetching LLM config for agent:', agent.agent_id);
      
      fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/llm`)
        .then(res => res.json())
        .then(async (data) => {
          console.log('[MultiPrompt] Raw LLM data from API:', data);
          
          // Fetch transfer phone number and merge it into transfer_call tools
          let transferPhone = null;
          try {
            const transferResponse = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/transfer-details?user_id=${encodeURIComponent(userId)}`);
            if (transferResponse.ok) {
              const transferData = await transferResponse.json();
              console.log('[MultiPrompt] Transfer data received:', transferData);
              if (transferData.transferTools && transferData.transferTools.length > 0) {
                const transferTool = transferData.transferTools.find(t => t.tool_name === 'transfer_call');
                console.log('[MultiPrompt] Found transfer tool:', transferTool);
                // Check for transfer_destination.number (new structure) or transfer_number (old structure)
                const phoneNumber = transferTool?.transfer_destination?.number || transferTool?.transfer_number;
                if (transferTool && phoneNumber) {
                  transferPhone = phoneNumber;
                  console.log('[MultiPrompt] Found transfer phone number:', transferPhone);
                } else {
                  console.log('[MultiPrompt] No phone number found in transfer tool');
                }
              }
            }
          } catch (err) {
            console.error('[MultiPrompt] Error fetching transfer phone number:', err);
          }
          
          // Merge transfer phone number into transfer_call tools
          const generalTools = (data.general_tools || []).map(tool => {
            if (tool.name === 'transfer_call' || tool.type === 'transfer_call') {
              // Check if tool already has phone number in transfer_destination.number
              const existingNumber = tool.transfer_destination?.number || tool.destination_number;
              // Use existing number if available, otherwise use fetched transferPhone
              const phoneNumber = existingNumber || transferPhone;
              if (phoneNumber) {
                console.log('[MultiPrompt] Merging phone number into tool:', phoneNumber);
                return { ...tool, destination_number: phoneNumber };
              }
              return tool;
            }
            return tool;
          });
          
          setMultiPromptData({
            starting_state: data.starting_state || 'intro',
            general_prompt: data.general_prompt || '',
            general_tools: generalTools,
            states: data.states || []
          });
          console.log('[MultiPrompt] Set multiPromptData:', {
            starting_state: data.starting_state || 'intro',
            general_prompt: data.general_prompt || '',
            general_tools: generalTools,
            states: data.states || []
          });
          
          // Also set the transfer phone number state for backward compatibility
          if (transferPhone) {
            setTransferPhoneNumber(transferPhone);
          }
        })
        .catch(err => {
          console.error('Error fetching multi-prompt data:', err);
          setMultiPromptError('Failed to fetch multi-prompt data');
        })
        .finally(() => {
          setMultiPromptLoading(false);
        });
    }
  }, [agent?.agent_id, userId]);

  // Fetch inbound agent phone number when agent loads
  useEffect(() => {
    if (agent?.agent_id && userId) {
      console.log('ðŸ"„ Fetching inbound agent phone number for agent:', agent.agent_id, 'user:', userId);
      fetchInboundAgentPhoneNumber(agent.agent_id);
    }
  }, [agent?.agent_id, userId]);

  // Fetch transfer phone number when agent loads
  useEffect(() => {
    if (agent?.agent_id && userId) {
      console.log('[Transfer Phone] Fetching transfer phone number for agent:', agent.agent_id);
      fetchTransferPhoneNumber();
    } else {
      setTransferPhoneNumber(null);
    }
  }, [agent?.agent_id, userId]);

  // Add useEffect for function dropdown click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (functionDropdownRef.current && !functionDropdownRef.current.contains(event.target)) {
        setFunctionDropdownOpen(false);
      }
    };

    if (functionDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [functionDropdownOpen]);

  // Add useEffect to monitor Transfer Call modal state and prevent premature closing
  useEffect(() => {
    if (toolConfigModalOpen && selectedToolType === 'transfer_call') {
      console.log('[Transfer Modal] Modal state changed to OPEN at:', new Date().toISOString());
      
      // Set up a timer to remove protection after 5 seconds
      const protectionTimer = setTimeout(() => {
        setIsModalProtected(false);
        console.log('[Transfer Modal] Protection timer expired - modal can now be closed normally');
      }, 5000);

      return () => {
        clearTimeout(protectionTimer);
      };
    } else if (!toolConfigModalOpen && selectedToolType === 'transfer_call') {
      console.log('[Transfer Modal] Modal state changed to CLOSED at:', new Date().toISOString());
      setModalOpenTime(null);
      setIsModalProtected(false);
    }
  }, [toolConfigModalOpen, selectedToolType]);

  // Add useEffect to detect unexpected modal state changes
  useEffect(() => {
    const detectUnexpectedClose = () => {
      if (modalOpenTime && toolConfigModalOpen && selectedToolType === 'transfer_call') {
        const timeSinceOpen = Date.now() - modalOpenTime;
        if (timeSinceOpen < 3000) {
          console.warn('[Transfer Modal] Modal is still open but may be vulnerable to premature closing. Time since open:', timeSinceOpen + 'ms');
        }
      }
    };

    const interval = setInterval(detectUnexpectedClose, 1000);
    return () => clearInterval(interval);
  }, [modalOpenTime, toolConfigModalOpen, selectedToolType]);

  // Function to fetch available agents for Agent Transfer
  const fetchAvailableAgents = async () => {
    if (!userEmail) {
      console.log('No userEmail available for fetching agents');
      return;
    }
    
    console.log('Fetching agents for user:', userEmail);
    setAgentsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents?user_email=${encodeURIComponent(userEmail)}`);
      console.log('Agent fetch response status:', response.status);
      
      if (response.ok) {
        const agents = await response.json();
        console.log('Fetched agents:', agents);
        
        // Filter out the current agent and format for dropdown
        const filteredAgents = agents
          .filter(a => a.agent_id !== agent?.agent_id)
          .map(a => ({
            id: a.agent_id,
            name: a.agent_name || 'Unnamed Agent',
            description: a.agent_description || '',
            voice: a.voice || 'Default Voice'
          }));
        
        console.log('Filtered agents for dropdown:', filteredAgents);
        setAvailableAgents(filteredAgents);
      } else {
        console.error('Failed to fetch agents, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setAvailableAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAvailableAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Try parsing as Unix timestamp first (seconds)
      let date = new Date(timestamp * 1000);
      
      // If that produces an invalid date, try parsing as milliseconds
      if (isNaN(date.getTime())) {
        date = new Date(timestamp);
      }
      
      // If still invalid, try parsing as string
      if (isNaN(date.getTime())) {
        date = new Date(timestamp);
      }
      
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to format call duration
  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // PDF download handler using PDFKit only (no blob-stream)
  const handleDownloadPDF = (call) => {
    const doc = new PDFDocument();
    const chunks = [];
    doc.on('data', (chunk) => chunks.push(chunk));
    doc.on('end', () => {
      const blob = new Blob(chunks, { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${call.call_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    });
    doc.fontSize(14).text(`Call ID: ${call.call_id}`);
    doc.moveDown();
    doc.fontSize(12).text(`Date: ${formatDate(call.start_timestamp)}`);
    doc.moveDown();
    doc.fontSize(12).text(`Duration: ${formatDuration(call.duration_ms)}`);
    doc.moveDown();
    doc.fontSize(12).text(`Status: ${call.call_status}`);
    doc.moveDown();
    doc.fontSize(10).text(call.transcript || 'No transcript available');
    doc.end();
  };

  // Reset call state on agent/user change
  useEffect(() => {
    setCallStatus('');
    setIsCallActive(false);
    setIsConnecting(false);
    setError('');
    // Cleanup previous client
    if (retellClient.current) {
      try { retellClient.current.stopCall(); } catch {}
      retellClient.current = null;
    }
  }, [agent?.agent_id, userEmail]);

  // Setup event listeners for Retell client
  const setupEventListeners = () => {
    const client = retellClient.current;
    client.on('call_started', () => {
      setCallStatus('Call connected! You can now speak.');
      setIsCallActive(true);
      setIsConnecting(false);
    });
    client.on('call_ended', () => {
      setCallStatus('Call ended.');
      setIsCallActive(false);
      setIsConnecting(false);
    });
    client.on('agent_start_talking', () => {
      setCallStatus('Agent is speaking...');
    });
    client.on('agent_stop_talking', () => {
      setCallStatus('Listening... You can speak now.');
    });
    client.on('error', (error) => {
      setError(`Call error: ${error.message || error}`);
      setIsCallActive(false);
      setIsConnecting(false);
    });
    client.on('update', (update) => {
      // Optionally handle real-time updates
    });
  };

  // Start call logic
  const startCall = async () => {
    if (!agent || !userEmail) {
      setError('Missing agent or user information');
      return;
    }
    setIsConnecting(true);
    setError('');
    setCallStatus('Creating call...');
    try {
      const response = await fetch(`${API_BASE_URL}/api/web-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: agent.agent_id, user_email: userEmail })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create web call');
      }
      const webCallData = await response.json();
      setCallStatus('Connecting...');
      retellClient.current = new RetellWebClient();
      setupEventListeners();
      await retellClient.current.startCall({
        accessToken: webCallData.access_token,
        sampleRate: 24000,
        emitRawAudioSamples: false,
      });
    } catch (error) {
      setError(error.message);
      setIsConnecting(false);
    }
  };

  // End call logic
  const endCall = () => {
    if (retellClient.current) {
      try {
        retellClient.current.stopCall();
      } catch (error) {}
    }
    setCallStatus('Call ended.');
    setIsCallActive(false);
    setIsConnecting(false);
  };

  // Handle agent name edit
  const handleStartEditAgentName = () => {
    setEditedAgentName(agent?.agent_name || '');
    setIsEditingAgentName(true);
  };

  const handleCancelEditAgentName = () => {
    setIsEditingAgentName(false);
    setEditedAgentName('');
  };

  const handleSaveAgentName = async () => {
    if (!agent?.agent_id || !editedAgentName.trim()) {
      handleCancelEditAgentName();
      return;
    }

    setUpdatingAgentName(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_name: editedAgentName.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update agent name');
      }

      const updatedAgent = await response.json();
      setAgent(prev => ({ ...prev, agent_name: updatedAgent.agent_name }));
      setIsEditingAgentName(false);
      setEditedAgentName('');
      
      console.log('Agent name updated successfully');
    } catch (error) {
      console.error('Error updating agent name:', error);
      alert(`Failed to update agent name: ${error.message}`);
    } finally {
      setUpdatingAgentName(false);
    }
  };

  // Handle model selection
  const handleModelSelect = async (model) => {
    if (!agent?.agent_id || !userEmail) {
      console.error('Missing agent or user information');
      return;
    }

    setUpdatingModel(true);
    try {
      // Get user ID from email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      const userData = await userResponse.json();

      // Update the agent's model
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/model`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_id: model.id,
          user_id: userData.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update model');
      }

      const result = await response.json();
      setCurrentModel(result.updated_llm);
      setOpenIndex(null); // Close the dropdown
      
      // Show success message (you could add a toast notification here)
      console.log('Model updated successfully:', result.message);
      
    } catch (error) {
      console.error('Error updating model:', error);
      // You could add error handling here (toast notification, etc.)
    } finally {
      setUpdatingModel(false);
    }
  };

  // Handle voice selection
  const handleVoiceSelect = async (voice) => {
    if (!agent?.agent_id || !userEmail) {
      console.error('Missing agent or user information');
      return;
    }

    setUpdatingVoice(true);
    try {
      // Get user ID from email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      const userData = await userResponse.json();

      // Update the agent's voice
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/voice`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: voice.id,
          user_id: userData.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update voice');
      }

      const result = await response.json();
      setCurrentVoice(result.updated_voice);
      setOpenIndex(null); // Close the dropdown
      
      // Show success message
      console.log('Voice updated successfully:', result.message);
      
    } catch (error) {
      console.error('Error updating voice:', error);
      // You could add error handling here (toast notification, etc.)
    } finally {
      setUpdatingVoice(false);
    }
  };

  // Handle language selection
  const handleLanguageSelect = async (language) => {
    if (!agent?.agent_id || !userEmail) {
      console.error('Missing agent or user information');
      return;
    }

    setUpdatingLanguage(true);
    try {
      // Get user ID from email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      const userData = await userResponse.json();

      // Update the agent's language
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/language`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language_code: language.code,
          user_id: userData.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update language');
      }

      const result = await response.json();
      setCurrentLanguage(result.updated_language);
      setOpenIndex(null); // Close the dropdown
      
      // Show success message
      console.log('Language updated successfully:', result.message);
      
    } catch (error) {
      console.error('Error updating language:', error);
      // You could add error handling here (toast notification, etc.)
    } finally {
      setUpdatingLanguage(false);
    }
  };

  // Handle speech settings update
  const handleSpeechSettingsUpdate = async (newSettings) => {
    if (!agent?.agent_id || !userEmail) {
      console.error('Missing agent or user information');
      return;
    }

    setUpdatingSpeechSettings(true);
    try {
      console.log('Updating speech settings with:', newSettings);
      
      // Get user ID from email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      const userData = await userResponse.json();

      const requestBody = {
        user_id: userData.id,
        ...newSettings
      };
      console.log('Sending request to backend:', requestBody);

      // Update the agent's speech settings
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/speech-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update speech settings');
      }

      const result = await response.json();
      console.log('Backend response:', result);
      setSpeechSettings(prev => ({ ...prev, ...newSettings }));
      
      // Show success message
      console.log('Speech settings updated successfully:', result.message);
      
    } catch (error) {
      console.error('Error updating speech settings:', error);
      // You could add error handling here (toast notification, etc.)
    } finally {
      setUpdatingSpeechSettings(false);
    }
  };

  // Handle transcription settings update
  const handleTranscriptionSettingsUpdate = async (newSettings) => {
    if (!agent?.agent_id || !userEmail) {
      console.error('Missing agent or user information');
      return;
    }

    setUpdatingTranscriptionSettings(true);
    try {
      // Get user ID from email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      const userData = await userResponse.json();

      // Update the agent's transcription settings
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/transcription-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userData.id,
          ...newSettings
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update transcription settings');
      }

      const result = await response.json();
      setTranscriptionSettings(prev => ({ ...prev, ...newSettings }));
      
      // Show success message
      console.log('Transcription settings updated successfully:', result.message);
      
    } catch (error) {
      console.error('Error updating transcription settings:', error);
      // You could add error handling here (toast notification, etc.)
    } finally {
      setUpdatingTranscriptionSettings(false);
    }
  };

  // Handle call settings update
  const handleCallSettingsUpdate = async (newSettings) => {
    if (!agent?.agent_id || !userEmail) {
      console.error('Missing agent or user information');
      return;
    }

    setUpdatingCallSettings(true);
    try {
      // Get user ID from email
      const userResponse = await fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`);
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      const userData = await userResponse.json();

      // Update the agent's call settings
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/call-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userData.id,
          ...newSettings
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update call settings');
      }

      const result = await response.json();
      setCallSettings(prev => ({ ...prev, ...newSettings }));
      
      // Show success message
      console.log('Call settings updated successfully:', result.message);
      
    } catch (error) {
      console.error('Error updating call settings:', error);
      // You could add error handling here (toast notification, etc.)
    } finally {
      setUpdatingCallSettings(false);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (openIndex === 'model' && modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setOpenIndex(null);
      }
      if (openIndex === 'voice' && voiceDropdownRef.current && !voiceDropdownRef.current.contains(e.target)) {
        setOpenIndex(null);
      }
      if (openIndex === 'language' && languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) {
        setOpenIndex(null);
      }
      if (kbDropdownOpen && kbDropdownRef.current && !kbDropdownRef.current.contains(e.target)) {
        setKbDropdownOpen(false);
      }
      // Handle tool dropdowns click outside
      if (openIndex && openIndex.startsWith('tool-dropdown-')) {
        const stateIdx = openIndex.replace('tool-dropdown-', '');
        const toolRef = toolDropdownRefs.current[stateIdx];
        if (toolRef && !toolRef.contains(e.target)) {
          setOpenIndex(null);
        }
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openIndex, kbDropdownOpen]);

  // Add isAdmin variable (set to false for now)
  const isAdmin = false;

  useEffect(() => {
    return () => {
      // Cleanup: end any ongoing call when component unmounts
      if (retellClient.current) {
        try { retellClient.current.stopCall(); } catch {}
        retellClient.current = null;
      }
    };
  }, []);

  // Fetch attached knowledge bases for this agent from knowledge_base table
  useEffect(() => {
    if (agent?.agent_id && userId) {
      setLoadingKBs(true);
      setKbError('');
      fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && Array.isArray(data.knowledge_bases)) {
            setAttachedKBs(data.knowledge_bases);
          } else {
            setAttachedKBs([]);
          }
        })
        .catch(() => setKbError('Failed to fetch attached knowledge bases'))
        .finally(() => setLoadingKBs(false));
    }
  }, [agent?.agent_id, userId]);

  // Handle delete knowledge base entry (unassign from agent, set agent_id to null)
  const handleDeleteKnowledgeBase = async (kbId, kbTopic) => {
    if (!userId || !agent?.agent_id) {
      setKbError('Missing required data for removal');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove "${kbTopic}" from this agent? The knowledge base will remain in the database but will no longer be associated with this agent.`)) {
      return;
    }

    setLoadingKBs(true);
    setKbError('');
    
    try {
      // Unassign the knowledge base from the agent by setting agent_id to null
      const response = await fetch(`${API_BASE_URL}/api/knowledge-base/assign-to-agent`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kb_id: kbId,
          user_id: userId,
          agent_id: null  // Set to null to unassign from agent
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to remove knowledge base from agent');
      }
      
      // Refresh the knowledge bases list
      const refreshResponse = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`);
      const refreshData = await refreshResponse.json();
      if (refreshData.success && Array.isArray(refreshData.knowledge_bases)) {
        setAttachedKBs(refreshData.knowledge_bases);
      }
      
      console.log('Knowledge base removed from agent successfully:', data);
      
    } catch (err) {
      console.error('Error removing knowledge base from agent:', err);
      setKbError(err.message || 'Failed to remove knowledge base from agent');
    } finally {
      setLoadingKBs(false);
    }
  };

  // Fetch available files for knowledge base dropdown
  const fetchAvailableFiles = async () => {
    if (!userId) return;
    
    setLoadingFiles(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge-bases/files?user_id=${userId}`);
      const data = await response.json();
      
      if (data.success && Array.isArray(data.files)) {
        setAvailableFiles(data.files);
      } else {
        setAvailableFiles([]);
      }
    } catch (error) {
      console.error('Error fetching available files:', error);
      setAvailableFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Handle knowledge base dropdown toggle
  const handleKbDropdownToggle = () => {
    if (!kbDropdownOpen) {
      fetchAvailableFiles();
    }
    setKbDropdownOpen(!kbDropdownOpen);
  };

  // Handle adding existing knowledge base entry to current agent
  const handleAddExistingFile = async (kbId, topic, content, tags, sourceUrl, currentAgentId) => {
    if (!userId || !agent?.agent_id) {
      setKbError('Missing required data for adding knowledge base');
      return;
    }

    setLoadingKBs(true);
    setKbError('');
    
    try {
      // Check if the knowledge base entry already has an agent_id
      if (currentAgentId && currentAgentId !== null) {
        // If agent_id is not null, create a new record with current agent_id
        const response = await fetch(`${API_BASE_URL}/api/knowledge-base/add-to-agent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            agent_id: agent.agent_id,
            topic: topic,
            content: content,
            tags: tags,
            source_url: sourceUrl
          })
        });

        const data = await response.json();
        
        if (data.success) {
          // Refresh the attached knowledge bases
          const refreshResponse = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`);
          const refreshData = await refreshResponse.json();
          
          if (refreshData.success && Array.isArray(refreshData.knowledge_bases)) {
            setAttachedKBs(refreshData.knowledge_bases);
          }
          
          setKbDropdownOpen(false);
        } else {
          setKbError(data.error || 'Failed to add knowledge base to agent');
        }
      } else {
        // If agent_id is null, update the existing record with current agent_id
        const response = await fetch(`${API_BASE_URL}/api/knowledge-base/assign-to-agent`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            kb_id: kbId,
            user_id: userId,
            agent_id: agent.agent_id
          })
        });

        const data = await response.json();
        
        if (data.success) {
          // Refresh the attached knowledge bases
          const refreshResponse = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`);
          const refreshData = await refreshResponse.json();
          
          if (refreshData.success && Array.isArray(refreshData.knowledge_bases)) {
            setAttachedKBs(refreshData.knowledge_bases);
          }
          
          setKbDropdownOpen(false);
        } else {
          setKbError(data.error || 'Failed to assign knowledge base to agent');
        }
      }
    } catch (error) {
      console.error('Error adding knowledge base:', error);
      setKbError(error.message || 'Failed to add knowledge base');
    } finally {
      setLoadingKBs(false);
    }
  };

  // Handle creating new knowledge base
  const handleCreateNewKnowledgeBase = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setKbDropdownOpen(false);
    // Pre-select current agent if available
    if (agent?.agent_id) {
      setSelectedAgentForKB(agent.agent_id);
    }
    setAdminKBModal(true);
  };

  // Fetch user's agents for Admin KB modal
  useEffect(() => {
    if (!userEmail || !adminKBModal) return;
    setLoadingAgents(true);
    fetch(`${API_BASE_URL}/api/user-agents?user_email=${encodeURIComponent(userEmail)}`)
      .then(res => res.json())
      .then(data => {
        setUserAgents(Array.isArray(data) ? data : []);
      })
      .catch(() => setUserAgents([]))
      .finally(() => setLoadingAgents(false));
  }, [userEmail, adminKBModal]);

  // Parse tags helper
  const parsedTags = React.useMemo(
    () => adminKBForm.tags.split(",").map(t => t.trim()).filter(Boolean),
    [adminKBForm.tags]
  );

  // Handle Admin KB form submission
  const handleAdminKBSave = async (e) => {
    e.preventDefault();
    if (!selectedAgentForKB || !userId) {
      setAdminKBError('Please select an agent first');
      return;
    }

    // Validation
    if (!adminKBForm.topic.trim() || adminKBForm.topic.trim().length < 3 || adminKBForm.topic.trim().length > 120) {
      setAdminKBError('Topic must be between 3-120 characters');
      return;
    }
    if (!adminKBForm.content.trim()) {
      setAdminKBError('Content cannot be empty');
      return;
    }
    if (parsedTags.length > 10) {
      setAdminKBError('Maximum 10 tags allowed');
      return;
    }
    if (parsedTags.some(tag => tag.length > 32)) {
      setAdminKBError('Each tag must be 32 characters or less');
      return;
    }
    if (adminKBForm.source_url && !adminKBForm.source_url.startsWith('https://')) {
      setAdminKBError('Source URL must start with https://');
      return;
    }

    setAdminKBSaving(true);
    setAdminKBError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-kb/upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: selectedAgentForKB,
          user_id: userId,
          topic: adminKBForm.topic.trim(),
          content: adminKBForm.content.trim(),
          tags: parsedTags.length ? parsedTags.map(tag => tag.toLowerCase()) : undefined,
          source_url: adminKBForm.source_url.trim() || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save KB entry');
      }
      
      setAdminKBSuccess('KB entry saved successfully!');
      setAdminKBForm({ topic: '', content: '', tags: '', source_url: '' });
      
      // Refresh attached knowledge bases
      if (agent?.agent_id && userId) {
        setLoadingKBs(true);
        fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && Array.isArray(data.knowledge_bases)) {
              setAttachedKBs(data.knowledge_bases);
            } else {
              setAttachedKBs([]);
            }
          })
          .catch(() => setKbError('Failed to fetch attached knowledge bases'))
          .finally(() => setLoadingKBs(false));
      }
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setAdminKBModal(false);
        setSelectedAgentForKB('');
        setAdminKBForm({ topic: '', content: '', tags: '', source_url: '' });
      }, 2000);
    } catch (err) {
      setAdminKBError(err.message || 'Failed to save KB entry');
    } finally {
      setAdminKBSaving(false);
    }
  };

  // Handle Admin KB file selection
  const handleAdminKBFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      
      const allowedTypes = ['.csv', '.txt', '.pdf', '.doc', '.docx', '.xlsx', '.xls'];
      const validFiles = [];
      const errors = [];
      
      newFiles.forEach(file => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!allowedTypes.includes(extension)) {
          errors.push(`${file.name}: File type not allowed. Only CSV, TXT, PDF, DOC, DOCX, XLSX, and XLS files are allowed.`);
          return;
        }
        
        if (file.size > maxFileSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          errors.push(`${file.name}: File too large (${fileSizeMB}MB). Maximum file size is 10MB. Please upload a smaller file.`);
          return;
        }
        
        validFiles.push(file);
      });
      
      if (errors.length > 0) {
        setAdminKBError(errors.join('\n'));
      } else {
        setAdminKBUploadFiles([...adminKBUploadFiles, ...validFiles]);
        setAdminKBError('');
      }
      
      e.target.value = '';
    }
  };

  // Remove Admin KB file
  const removeAdminKBFile = (idx) => {
    setAdminKBUploadFiles(adminKBUploadFiles.filter((_, i) => i !== idx));
  };

  // Handle Admin KB file upload
  const handleAdminKBFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedAgentForKB || !userId) {
      setAdminKBError('Please select an agent first');
      return;
    }
    if (adminKBUploadFiles.length === 0) {
      setAdminKBError('Please select at least one file');
      return;
    }

    setAdminKBUploading(true);
    setAdminKBError('');
    
    try {
      const formData = new FormData();
      formData.append('agent_id', selectedAgentForKB);
      formData.append('user_id', userId);
      adminKBUploadFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/admin-kb/upload`, {
        method: 'POST',
        body: formData,
      });

      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('❌ Non-JSON response received:', text);
        
        if (text.includes('File too large') || text.includes('LIMIT_FILE_SIZE')) {
          throw new Error('File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          throw new Error('Server error: The file may be too large or the server encountered an error. Please try uploading a smaller file.');
        } else {
          throw new Error(`Upload failed: ${text.substring(0, 200)}`);
        }
      }
      
      if (!response.ok) {
        if (data.code === 'FILE_TOO_LARGE') {
          throw new Error(data.error || 'File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (data.code === 'TOO_MANY_FILES') {
          throw new Error(data.error || 'Too many files. Please upload fewer files at once.');
        } else {
          throw new Error(data.error || 'File upload failed');
        }
      }

      const successMessage = data.results && data.results.length > 0 
        ? `Successfully uploaded and processed ${data.results.length} file(s)! Content has been automatically chunked for better search performance.`
        : `Successfully uploaded ${adminKBUploadFiles.length} file(s)!`;
      
      setAdminKBSuccess(successMessage);
      setAdminKBUploadFiles([]);
      setAdminKBFileUpload(false);
      
      // Refresh attached knowledge bases
      if (agent?.agent_id && userId) {
        setLoadingKBs(true);
        fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && Array.isArray(data.knowledge_bases)) {
              setAttachedKBs(data.knowledge_bases);
            } else {
              setAttachedKBs([]);
            }
          })
          .catch(() => setKbError('Failed to fetch attached knowledge bases'))
          .finally(() => setLoadingKBs(false));
      }
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setAdminKBModal(false);
        setSelectedAgentForKB('');
        setAdminKBUploadFiles([]);
        setAdminKBFileUpload(false);
      }, 2000);
    } catch (err) {
      setAdminKBError(err.message || 'Failed to upload files');
    } finally {
      setAdminKBUploading(false);
    }
  };

  // Clear admin KB success message after 3 seconds
  useEffect(() => {
    if (adminKBSuccess) {
      const timer = setTimeout(() => {
        setAdminKBSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [adminKBSuccess]);

  // Fetch available models from Retell
  useEffect(() => {
    setModelsLoading(true);
    setModelsError(null);
    fetch(`${API_BASE_URL}/api/models`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAvailableModels(data);
        } else {
          setModelsError('Invalid response format');
        }
      })
      .catch(err => {
        console.error('Error fetching models:', err);
        setModelsError('Failed to fetch models');
      })
      .finally(() => setModelsLoading(false));
  }, []);

  // Fetch current model for this agent
  useEffect(() => {
    if (agent?.agent_id) {
      fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/model`)
        .then(res => res.json())
        .then(data => {
          setCurrentModel(data);
        })
        .catch(err => {
          console.error('Error fetching current model:', err);
          // Set a default model if fetch fails
          setCurrentModel({
            id: 'default',
            name: 'GPT 4o',
            provider: 'OpenAI',
            features: []
          });
        });
    }
  }, [agent?.agent_id]);

  // Fetch available voices from Retell
  useEffect(() => {
    setVoicesLoading(true);
    setVoicesError(null);
    fetch(`${API_BASE_URL}/api/voices`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAvailableVoices(data);
        } else {
          setVoicesError('Invalid response format');
        }
      })
      .catch(err => {
        console.error('Error fetching voices:', err);
        setVoicesError('Failed to fetch voices');
      })
      .finally(() => setVoicesLoading(false));
  }, []);

  // Fetch current voice for this agent
  useEffect(() => {
    if (agent?.agent_id) {
      fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/voice`)
        .then(res => res.json())
        .then(data => {
          setCurrentVoice(data);
        })
        .catch(err => {
          console.error('Error fetching current voice:', err);
          // Set a default voice if fetch fails
          setCurrentVoice({
            id: 'default',
            name: 'Anthony',
            provider: '11labs',
            accent: 'British',
            gender: 'male',
            age: 'middle'
          });
        });
    }
  }, [agent?.agent_id]);

  // Fetch available languages from Retell
  useEffect(() => {
    setLanguagesLoading(true);
    setLanguagesError(null);
    fetch(`${API_BASE_URL}/api/languages`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAvailableLanguages(data);
        } else {
          setLanguagesError('Invalid response format');
        }
      })
      .catch(err => {
        console.error('Error fetching languages:', err);
        setLanguagesError('Failed to fetch languages');
      })
      .finally(() => setLanguagesLoading(false));
  }, []);

  // Fetch current language for this agent
  useEffect(() => {
    if (agent?.agent_id) {
      fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/language`)
        .then(res => res.json())
        .then(data => {
          setCurrentLanguage(data);
        })
        .catch(err => {
          console.error('Error fetching current language:', err);
          // Set a default language if fetch fails
          setCurrentLanguage({
            code: 'en-US',
            name: 'English (US)',
            region: 'North America'
          });
        });
    }
  }, [agent?.agent_id]);

  // Fetch current speech settings for this agent
  useEffect(() => {
    if (agent?.agent_id && userEmail) {
      setSpeechSettingsLoading(true);
      setSpeechSettingsError(null);
      
      // Get user ID from email first
      fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`)
        .then(res => res.json())
        .then(userData => {
          return fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/speech-settings?user_id=${userData.id}`);
        })
        .then(res => res.json())
        .then(data => {
          setSpeechSettings(data);
        })
        .catch(err => {
          console.error('Error fetching speech settings:', err);
          setSpeechSettingsError('Failed to fetch speech settings');
          // Keep default settings if fetch fails
        })
        .finally(() => setSpeechSettingsLoading(false));
    }
  }, [agent?.agent_id, userEmail]);

  // Fetch current transcription settings for this agent
  useEffect(() => {
    if (agent?.agent_id && userEmail) {
      setTranscriptionSettingsLoading(true);
      setTranscriptionSettingsError(null);
      
      // Get user ID from email first
      fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`)
        .then(res => res.json())
        .then(userData => {
          return fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/transcription-settings?user_id=${userData.id}`);
        })
        .then(res => res.json())
        .then(data => {
          setTranscriptionSettings(data);
        })
        .catch(err => {
          console.error('Error fetching transcription settings:', err);
          setTranscriptionSettingsError('Failed to fetch transcription settings');
          // Keep default settings if fetch fails
        })
        .finally(() => setTranscriptionSettingsLoading(false));
    }
  }, [agent?.agent_id, userEmail]);

  // Fetch current call settings for this agent
  useEffect(() => {
    if (agent?.agent_id && userEmail) {
      setCallSettingsLoading(true);
      setCallSettingsError(null);
      
      // Get user ID from email first
      fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`)
        .then(res => res.json())
        .then(userData => {
          return fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/call-settings?user_id=${userData.id}`);
        })
        .then(res => res.json())
        .then(data => {
          setCallSettings(data);
        })
        .catch(err => {
          console.error('Error fetching call settings:', err);
          setCallSettingsError('Failed to fetch call settings');
          // Keep default settings if fetch fails
        })
        .finally(() => setCallSettingsLoading(false));
    }
  }, [agent?.agent_id, userEmail]);

  // Fetch post-call analysis config
  useEffect(() => {
    if (agent?.agent_id && userEmail) {
      setPostCallAnalysisLoading(true);
      setPostCallAnalysisError(null);
      fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/post-call-analysis-config`)
        .then(res => res.json())
        .then(data => setPostCallAnalysisConfig(data))
        .catch(err => {
          setPostCallAnalysisError('Failed to fetch post-call analysis config');
        })
        .finally(() => setPostCallAnalysisLoading(false));
    }
  }, [agent?.agent_id, userEmail]);

  // Handler to update config
  const handleSavePostCallAnalysisConfig = async () => {
    if (!agent?.agent_id || !userEmail) return;
    setUpdatingPostCallAnalysis(true);
    setPostCallAnalysisError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/post-call-analysis-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postCallAnalysisConfig)
      });
      if (!res.ok) throw new Error('Failed to update config');
    } catch (err) {
      setPostCallAnalysisError('Failed to update post-call analysis config');
    } finally {
      setUpdatingPostCallAnalysis(false);
    }
  };

  // Handler to add a new field
  const handleAddAnalysisField = () => {
    setPostCallAnalysisConfig(cfg => ({
      ...cfg,
      post_call_analysis_data: [
        ...cfg.post_call_analysis_data,
        { type: 'string', name: '', description: '', examples: [] }
      ]
    }));
  };

  // Handler to update a field
  const handleUpdateAnalysisField = (idx, field) => {
    setPostCallAnalysisConfig(cfg => {
      const arr = [...cfg.post_call_analysis_data];
      arr[idx] = { ...arr[idx], ...field };
      return { ...cfg, post_call_analysis_data: arr };
    });
  };

  // Handler to remove a field
  const handleRemoveAnalysisField = (idx) => {
    setPostCallAnalysisConfig(cfg => {
      const arr = [...cfg.post_call_analysis_data];
      arr.splice(idx, 1);
      return { ...cfg, post_call_analysis_data: arr };
    });
  };

  // Example handler for saving pronunciation from the modal
  const handleSavePronunciation = (pronunciationObj) => {
    // Always send as array
    const pronunciationArray = [pronunciationObj];
    handleSpeechSettingsUpdate({ pronunciation: pronunciationArray });
  };

  // Multi-Prompt Backend handlers
  const handleMultiPromptUpdate = async (newData) => {
    if (!agent?.agent_id || !userEmail) return;
    
    setUpdatingMultiPrompt(true);
    try {
      console.log('[MultiPrompt] Updating LLM config with data:', newData);
      
      const response = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/llm`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update multi-prompt data');
      }
      
      const updatedData = await response.json();
      console.log('[MultiPrompt] LLM update response:', updatedData);
      
      setMultiPromptData({
        starting_state: updatedData.starting_state || 'intro',
        general_prompt: updatedData.general_prompt || '',
        general_tools: updatedData.general_tools || [],
        states: updatedData.states || []
      });
    } catch (err) {
      console.error('Error updating multi-prompt data:', err);
      setMultiPromptError(`Failed to update multi-prompt data: ${err.message}`);
    } finally {
      setUpdatingMultiPrompt(false);
    }
  };

  // Handle saving General Prompt
  const handleSaveGeneralPrompt = async () => {
    if (!agent?.agent_id || !userEmail) {
      alert('Missing agent or user information');
      return;
    }

    setUpdatingMultiPrompt(true);
    setMultiPromptError(null);
    try {
      console.log('[General Prompt] Saving general prompt:', multiPromptData.general_prompt);
      
      const response = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/llm`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          general_prompt: multiPromptData.general_prompt,
          starting_state: multiPromptData.starting_state,
          general_tools: multiPromptData.general_tools || [],
          states: multiPromptData.states || []
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update general prompt');
      }
      
      const updatedData = await response.json();
      console.log('[General Prompt] Update response:', updatedData);
      
      // Update the state with the response
      setMultiPromptData(prev => ({
        ...prev,
        general_prompt: updatedData.general_prompt || prev.general_prompt
      }));
      
      alert('General prompt saved successfully!');
    } catch (err) {
      console.error('Error saving general prompt:', err);
      setMultiPromptError(`Failed to save general prompt: ${err.message}`);
      alert(`Failed to save general prompt: ${err.message}`);
    } finally {
      setUpdatingMultiPrompt(false);
    }
  };

  const handleAddState = () => {
    setMultiPromptData(prev => ({
      ...prev,
      states: [...prev.states, {
        name: `state_${prev.states.length + 1}`,
        state_prompt: '',
        edges: [],
        tools: []
      }]
    }));
  };

  const handleRemoveState = (stateIndex) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.filter((_, index) => index !== stateIndex)
    }));
  };

  const handleUpdateState = (stateIndex, field, value) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? { ...state, [field]: value } : state
      )
    }));
  };

  const handleAddEdge = (stateIndex) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          edges: [...state.edges, {
            destination_state_name: '',
            description: ''
          }]
        } : state
      )
    }));
  };

  const handleRemoveEdge = (stateIndex, edgeIndex) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          edges: state.edges.filter((_, eIndex) => eIndex !== edgeIndex)
        } : state
      )
    }));
  };

  const handleUpdateEdge = (stateIndex, edgeIndex, field, value) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          edges: state.edges.map((edge, eIndex) => 
            eIndex === edgeIndex ? { ...edge, [field]: value } : edge
          )
        } : state
      )
    }));
  };

  const handleAddTool = (stateIndex) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          tools: [...state.tools, {
            name: 'end_call',
            type: 'end_call',
            description: 'End the call with user.'
          }]
        } : state
      )
    }));
  };

  const handleAddToolWithType = (stateIndex, toolType) => {
    // For Transfer Call, open the modal instead of directly adding the tool
    if (toolType === 'transfer_call') {
      console.log('[Transfer Modal] Opening Transfer Call modal for state:', stateIndex);
      setSelectedToolType(toolType);
      setSelectedStateIndex(stateIndex);
      
      // Set default values for transfer call
      setToolConfig({
        name: 'transfer_call',
        description: 'Transfer the call to a human agent.',
        transfer_to: 'static',
        destination_number: '',
        transfer_type: 'cold',
        displayed_phone_number: 'retell_agent',
        custom_sip_headers: []
      });
      
      // Set protection timer
      const currentTime = Date.now();
      setModalOpenTime(currentTime);
      setIsModalProtected(true);
      console.log('[Transfer Modal] Protection timer set at:', new Date(currentTime).toISOString());
      
      setToolConfigModalOpenProtected(true);
      console.log('[Transfer Modal] Modal opened successfully');
      return;
    }

    let newTool;
    
    switch (toolType) {
      case 'end_call':
        newTool = {
          name: 'end_call',
          type: 'end_call',
          description: 'End the call with user.'
        };
        break;
      case 'check_calendar':
        newTool = {
          name: 'check_calendar',
          type: 'check_calendar',
          description: 'Check calendar availability.',
          cal_api_key: 'your_cal_api_key'
        };
        break;
      case 'book_calendar':
        newTool = {
          name: 'book_calendar',
          type: 'book_calendar',
          description: 'Book an event on calendar.',
          cal_api_key: 'your_cal_api_key'
        };
        break;
      case 'press_digit':
        newTool = {
          name: 'press_digit',
          type: 'press_digit',
          description: 'Press digit for IVR navigation.',
          digit: '1'
        };
        break;
      case 'send_sms':
        newTool = {
          name: 'send_sms',
          type: 'send_sms',
          description: 'Send SMS message.',
          sms_content: 'Thank you for your call.'
        };
        break;
      case 'agent_transfer':
        newTool = {
          name: 'agent_transfer',
          type: 'agent_transfer',
          description: 'Transfer to another agent.',
          agent_id: 'target_agent_id'
        };
        break;
      case 'custom':
        newTool = {
          name: 'custom_function',
          type: 'custom',
          description: 'Custom function.',
          url: 'https://your-api-endpoint.com/function'
        };
        break;
      case 'extract_variable':
        newTool = {
          name: 'extract_variable',
          type: 'extract_variable',
          description: 'Extract dynamic variables.',
          variables: ['name', 'email', 'phone']
        };
        break;
      default:
        newTool = {
          name: 'end_call',
          type: 'end_call',
          description: 'End the call with user.'
        };
    }
    
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          tools: [...state.tools, newTool]
        } : state
      )
    }));
  };

  const handleRemoveTool = (stateIndex, toolIndex) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          tools: state.tools.filter((_, tIndex) => tIndex !== toolIndex)
        } : state
      )
    }));
  };

  const handleUpdateTool = (stateIndex, toolIndex, field, value) => {
    setMultiPromptData(prev => ({
      ...prev,
      states: prev.states.map((state, index) => 
        index === stateIndex ? {
          ...state,
          tools: state.tools.map((tool, tIndex) => 
            tIndex === toolIndex ? { ...tool, [field]: value } : tool
          )
        } : state
      )
    }));
  };

  const handleAddGeneralTool = () => {
    setMultiPromptData(prev => ({
      ...prev,
      general_tools: [...prev.general_tools, {
        name: 'end_call',
        type: 'end_call',
        description: 'End the call with user.'
      }]
    }));
  };

  const handleAddGeneralToolWithType = (toolType) => {
    // For Transfer Call, open the modal instead of directly adding the tool
    if (toolType === 'transfer_call') {
      console.log('[Transfer Modal] Opening Transfer Call modal for general tools');
      setSelectedToolType(toolType);
      setSelectedStateIndex(null); // null indicates general tools
      
      // Set default values for transfer call
      setToolConfig({
        name: 'transfer_call',
        description: 'Transfer the call to a human agent.',
        transfer_to: 'static',
        destination_number: '',
        transfer_type: 'cold',
        displayed_phone_number: 'retell_agent',
        custom_sip_headers: []
      });
      
      // Set protection timer
      const currentTime = Date.now();
      setModalOpenTime(currentTime);
      setIsModalProtected(true);
      console.log('[Transfer Modal] Protection timer set at:', new Date(currentTime).toISOString());
      
      setToolConfigModalOpenProtected(true);
      console.log('[Transfer Modal] Modal opened successfully');
      return;
    }

    let newTool;
    
    switch (toolType) {
      case 'end_call':
        newTool = {
          name: 'end_call',
          type: 'end_call',
          description: 'End the call with user.'
        };
        break;
      case 'check_calendar':
        newTool = {
          name: 'check_calendar',
          type: 'check_calendar',
          description: 'Check calendar availability.',
          cal_api_key: 'your_cal_api_key'
        };
        break;
      case 'book_calendar':
        newTool = {
          name: 'book_calendar',
          type: 'book_calendar',
          description: 'Book an event on calendar.',
          cal_api_key: 'your_cal_api_key'
        };
        break;
      case 'press_digit':
        newTool = {
          name: 'press_digit',
          type: 'press_digit',
          description: 'Press digit for IVR navigation.',
          digit: '1'
        };
        break;
      case 'send_sms':
        newTool = {
          name: 'send_sms',
          type: 'send_sms',
          description: 'Send SMS message.',
          sms_content: 'Thank you for your call.'
        };
        break;
      case 'agent_transfer':
        newTool = {
          name: 'agent_transfer',
          type: 'agent_transfer',
          description: 'Transfer to another agent.',
          agent_id: 'target_agent_id'
        };
        break;
      case 'custom':
        newTool = {
          name: 'custom_function',
          type: 'custom',
          description: 'Custom function.',
          url: 'https://your-api-endpoint.com/function'
        };
        break;
      case 'extract_variable':
        newTool = {
          name: 'extract_variable',
          type: 'extract_variable',
          description: 'Extract dynamic variables.',
          variables: ['name', 'email', 'phone']
        };
        break;
      default:
        newTool = {
          name: 'end_call',
          type: 'end_call',
          description: 'End the call with user.'
        };
    }
    
    setMultiPromptData(prev => ({
      ...prev,
      general_tools: [...prev.general_tools, newTool]
    }));
  };

  // Unified handler for both Add Function and Add Tool - opens the same modal
  const handleUnifiedItemClick = (itemType, mode, stateIndex = null) => {
    // Set the mode (function or tool)
    setUnifiedModalMode(mode);
    setUnifiedModalStateIndex(stateIndex);
    
    // Map the shared option type to the appropriate handler type
    const functionType = itemType === 'call_transfer' ? 'call_transfer' : itemType;
    const toolType = itemType === 'call_transfer' ? 'transfer_call' : itemType;
    
    if (mode === 'function') {
      // Use existing function handler logic
      handleFunctionClick(functionType);
    } else if (mode === 'tool') {
      // For tools, also open the same function modal for consistency
      // Map tool type to function type for the modal
      setSelectedFunctionType(functionType);
      
      // Set default values based on function type (same as function handler)
      let defaultName = '';
      let defaultDescription = '';
      
      switch (functionType) {
        case 'end_call':
          defaultName = 'end_call';
          defaultDescription = 'End the call with user. Use this when the user says goodbye, thank you, or when the conversation is complete.';
          break;
        case 'call_transfer':
          defaultName = 'transfer_call';
          defaultDescription = 'Transfer the call to a human agent.';
          break;
        case 'agent_transfer':
          defaultName = 'agent_transfer';
          defaultDescription = 'Transfer the interaction to another agent.';
          fetchAvailableAgents();
          break;
        case 'check_calendar':
          defaultName = 'check_calendar';
          defaultDescription = 'Check availability via Cal.com.';
          break;
        case 'book_calendar':
          defaultName = 'book_calendar';
          defaultDescription = 'Book an event on calendar via Cal.com.';
          break;
        case 'press_digit':
          defaultName = 'press_digit';
          defaultDescription = 'Navigate to the human agent of sales department';
          break;
        case 'send_sms':
          defaultName = 'send_sms';
          defaultDescription = 'Functions for sending SMS';
          break;
        case 'extract_variable':
          defaultName = 'extract_variable';
          defaultDescription = 'Extract the user\'s details like name, email, age, etc. from the conversation';
          break;
        case 'custom':
          defaultName = 'custom_function';
          defaultDescription = 'Custom function for specific actions.';
          break;
        default:
          defaultName = '';
          defaultDescription = '';
      }
      
      setFunctionConfig({
        name: defaultName,
        description: defaultDescription,
        transfer_to: 'static',
        destination_number: '',
        transfer_type: 'cold',
        displayed_phone_number: 'retell_agent',
        custom_sip_headers: [],
        tool_name: 'transfer_call',
        tool_description: 'Transfer the call to support when the user needs human assistance.',
        pause_detection_delay: '1000',
        sms_content: ''
      });
      
      // Open the same modal that functions use
      setFunctionConfigModalOpen(true);
      setOpenIndex(null); // Close tool dropdown
    }
  };

  const handleFunctionClick = (functionType) => {
    setSelectedFunctionType(functionType);
    
    // Set default values based on function type
    let defaultName = '';
    let defaultDescription = '';
    
    switch (functionType) {
      case 'end_call':
        defaultName = 'end_call';
        defaultDescription = 'End the call with user. Use this when the user says goodbye, thank you, or when the conversation is complete.';
        break;
      case 'call_transfer':
        defaultName = 'transfer_call';
        defaultDescription = 'Transfer the call to a human agent.';
        break;
      case 'agent_transfer':
        defaultName = 'agent_transfer';
        defaultDescription = 'Transfer the interaction to another agent.';
        // Fetch available agents for Agent Transfer
        fetchAvailableAgents();
        break;
      case 'check_calendar':
        defaultName = 'check_calendar';
        defaultDescription = 'Check availability via Cal.com.';
        break;
      case 'book_calendar':
        defaultName = 'book_calendar';
        defaultDescription = 'Book an event on calendar via Cal.com.';
        break;
      case 'press_digit':
        defaultName = 'press_digit';
        defaultDescription = 'Navigate to the human agent of sales department';
        break;
      case 'send_sms':
        defaultName = 'send_sms';
        defaultDescription = 'Functions for sending SMS';
        break;
      case 'extract_variable':
        defaultName = 'extract_variable';
        defaultDescription = 'Extract the user\'s details like name, email, age, etc. from the conversation';
        break;
      case 'custom':
        defaultName = 'custom_function';
        defaultDescription = 'Custom function for specific actions.';
        break;
      default:
        defaultName = '';
        defaultDescription = '';
    }
    
    setFunctionConfig({
      name: defaultName,
      description: defaultDescription,
      transfer_to: 'static',
      destination_number: '',
      transfer_type: 'cold',
      displayed_phone_number: 'retell_agent',
      custom_sip_headers: [],
      tool_name: 'transfer_call',
      tool_description: 'Transfer the call to support when the user needs human assistance.',
      pause_detection_delay: '1000',
      sms_content: ''
    });
    
    setFunctionConfigModalOpen(true);
    setFunctionDropdownOpen(false);
  };

  const handleSaveFunction = async () => {
    // If in tool mode, save as tool instead
    if (unifiedModalMode === 'tool' && unifiedModalStateIndex !== null) {
      // Map function type to tool type
      const toolType = selectedFunctionType === 'call_transfer' ? 'transfer_call' : selectedFunctionType;
      
      // Use the existing tool save logic
      if (toolType === 'transfer_call') {
        // For transfer_call, use the tool config modal logic
        setSelectedToolType('transfer_call');
        setSelectedStateIndex(unifiedModalStateIndex);
        setToolConfig({
          name: functionConfig.name || 'transfer_call',
          description: functionConfig.description || 'Transfer the call to a human agent.',
          transfer_to: functionConfig.transfer_to || 'static',
          destination_number: functionConfig.destination_number || '',
          transfer_type: functionConfig.transfer_type || 'cold',
          displayed_phone_number: functionConfig.displayed_phone_number || 'retell_agent',
          custom_sip_headers: functionConfig.custom_sip_headers || []
        });
        
        // Validate
        if (!functionConfig.destination_number.trim()) {
          alert('Destination number is required for transfer call function.');
          return;
        }
        
        // Close function modal and open tool modal (which will handle the save)
        setFunctionConfigModalOpen(false);
        setToolConfigModalOpenProtected(true);
        return;
      } else {
        // For other tool types, add directly to the state
        let newTool;
        switch (toolType) {
          case 'end_call':
            newTool = {
              name: functionConfig.name || 'end_call',
              type: 'end_call',
              description: functionConfig.description || 'End the call with user.'
            };
            break;
          case 'check_calendar':
            newTool = {
              name: 'check_calendar',
              type: 'check_calendar',
              description: functionConfig.description || 'Check calendar availability.',
              cal_api_key: 'your_cal_api_key'
            };
            break;
          case 'book_calendar':
            newTool = {
              name: 'book_calendar',
              type: 'book_calendar',
              description: functionConfig.description || 'Book an event on calendar.',
              cal_api_key: 'your_cal_api_key'
            };
            break;
          case 'press_digit':
            newTool = {
              name: 'press_digit',
              type: 'press_digit',
              description: functionConfig.description || 'Press digit for IVR navigation.',
              digit: functionConfig.pause_detection_delay || '1'
            };
            break;
          case 'send_sms':
            newTool = {
              name: 'send_sms',
              type: 'send_sms',
              description: functionConfig.description || 'Send SMS message.',
              sms_content: functionConfig.sms_content || 'Thank you for your call.'
            };
            break;
          case 'agent_transfer':
            newTool = {
              name: 'agent_transfer',
              type: 'agent_transfer',
              description: functionConfig.description || 'Transfer to another agent.',
              agent_id: agentTransferConfig.selected_agent || 'target_agent_id'
            };
            break;
          case 'custom':
            newTool = {
              name: functionConfig.name || 'custom_function',
              type: 'custom',
              description: functionConfig.description || 'Custom function.',
              url: functionConfig.tool_name || 'https://your-api-endpoint.com/function'
            };
            break;
          case 'extract_variable':
            newTool = {
              name: 'extract_variable',
              type: 'extract_variable',
              description: functionConfig.description || 'Extract dynamic variables.',
              variables: []
            };
            break;
          default:
            return;
        }
        
        // Add tool to the state
        setMultiPromptData(prev => ({
          ...prev,
          states: prev.states.map((state, index) => 
            index === unifiedModalStateIndex ? {
              ...state,
              tools: [...state.tools, newTool]
            } : state
          )
        }));
        
        // Close modal and reset
        setFunctionConfigModalOpen(false);
        setUnifiedModalMode(null);
        setUnifiedModalStateIndex(null);
        setSelectedFunctionType('');
        setFunctionConfig({ 
          name: '', 
          description: '', 
          transfer_to: 'static',
          destination_number: '', 
          transfer_type: 'cold',
          displayed_phone_number: 'retell_agent',
          custom_sip_headers: [],
          tool_name: 'transfer_call',
          tool_description: 'Transfer the call to support when the user needs human assistance.',
          pause_detection_delay: '1000',
          sms_content: ''
        });
        return;
      }
    }
    
    // Original function save logic (for function mode)
    // Validate required fields for transfer call
    if (selectedFunctionType === 'call_transfer' && !functionConfig.destination_number.trim()) {
      alert('Destination number is required for transfer call function.');
      return;
    }

    // Validate required fields for agent transfer
    if (selectedFunctionType === 'agent_transfer' && !agentTransferConfig.selected_agent.trim()) {
      alert('Please select an agent to transfer to.');
      return;
    }

    // Handle transfer_call function separately using the dedicated endpoint
    if (selectedFunctionType === 'call_transfer') {
      try {
        const transferToolData = {
          agent_id: agent?.agent_id,
          tool_name: functionConfig.tool_name || 'transfer_call',
          description: functionConfig.description || 'Transfer the call to support when the user needs human assistance.',
          transfer_number: functionConfig.destination_number,
          transfer_type: functionConfig.transfer_type || 'cold',
          user_email: userEmail
        };

        console.log('Adding transfer tool with data:', transferToolData);

        const response = await fetch(`${API_BASE_URL}/api/agents/add-transfer-tool`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(transferToolData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add transfer tool');
        }

        const result = await response.json();
        console.log('Transfer tool added successfully:', result);

        // Show success message (keep generic product wording)
        alert('Call transfer has been saved successfully.');

        // Refresh transfer phone number to display the newly added number
        fetchTransferPhoneNumber();

        // Close modal and reset state
        setFunctionConfigModalOpen(false);
        setSelectedFunctionType('');
        setFunctionConfig({ 
          name: '', 
          description: '', 
          transfer_to: 'static',
          destination_number: '', 
          transfer_type: 'cold',
          displayed_phone_number: 'retell_agent',
          custom_sip_headers: [],
          tool_name: 'transfer_call',
          tool_description: 'Transfer the call to support when the user needs human assistance.'
        });

        // Refresh the LLM data to show the new tool
        refreshLLMData();
        
        // Transfer popup logic removed - no longer needed

        return; // Exit early for transfer_call
      } catch (error) {
        console.error('Error adding transfer tool:', error);
        alert(`Failed to add transfer tool: ${error.message}`);
        return;
      }
    }

    let newFunction;
    
    switch (selectedFunctionType) {
      case 'end_call':
        newFunction = {
          type: 'function',
          name: functionConfig.name || 'end_call',
          description: functionConfig.description || 'End the call with user. Use this when the user says goodbye, thank you, or when the conversation is complete.',
          parameters: {
            type: 'object',
            properties: {
              reason: {
                type: 'string',
                description: 'Reason for ending the call'
              },
              agent_id: {
                type: 'string',
                description: 'ID of the agent performing the action'
              }
            },
            required: ['reason', 'agent_id']
          },
          http_method: 'POST',
          url: `${API_BASE_URL}/api/function-calls/end-call`
        };
        break;
      case 'agent_transfer':
        newFunction = {
          type: 'function',
          name: agentTransferConfig.function_name || 'agent_transfer',
          description: agentTransferConfig.description || 'Transfer the interaction to another agent.',
          parameters: {
            type: 'object',
            properties: {
              agent_id: {
                type: 'string',
                description: 'ID of the agent to transfer to'
              },
              reason: {
                type: 'string',
                description: 'Reason for transferring to another agent'
              }
            },
            required: ['agent_id']
          },
          // Store the configured values for reference
          config: {
            selected_agent: agentTransferConfig.selected_agent,
            speak_during_execution: agentTransferConfig.speak_during_execution,
            post_call_analysis: agentTransferConfig.post_call_analysis
          }
        };
        break;
      case 'check_calendar':
        newFunction = {
          type: 'function',
          name: 'check_calendar',
          description: 'Check availability via Cal.com.',
          parameters: {
            type: 'object',
            properties: {
              date: {
                type: 'string',
                description: 'Date to check availability for (YYYY-MM-DD)'
              },
              timezone: {
                type: 'string',
                description: 'Timezone for the date check'
              }
            },
            required: ['date']
          }
        };
        break;
      case 'book_calendar':
        newFunction = {
          type: 'function',
          name: 'book_calendar',
          description: 'Book an event on calendar via Cal.com.',
          parameters: {
            type: 'object',
            properties: {
              title: {
                type: 'string',
                description: 'Title of the calendar event'
              },
              date: {
                type: 'string',
                description: 'Date for the event (YYYY-MM-DD)'
              },
              time: {
                type: 'string',
                description: 'Time for the event (HH:MM)'
              },
              duration: {
                type: 'number',
                description: 'Duration in minutes'
              }
            },
            required: ['title', 'date', 'time']
          }
        };
        break;
      case 'press_digit':
        newFunction = {
          type: 'function',
          name: 'press_digit',
          description: 'Navigate IVR system by pressing digits.',
          parameters: {
            type: 'object',
            properties: {
              digit: {
                type: 'string',
                description: 'Digit to press (0-9, *, #)'
              },
              reason: {
                type: 'string',
                description: 'Reason for pressing this digit'
              }
            },
            required: ['digit']
          }
        };
        break;
      case 'send_sms':
        newFunction = {
          type: 'function',
          name: 'send_sms',
          description: 'Send a text message to a phone number.',
          parameters: {
            type: 'object',
            properties: {
              phone_number: {
                type: 'string',
                description: 'Phone number to send SMS to'
              },
              message: {
                type: 'string',
                description: 'Message content to send'
              }
            },
            required: ['phone_number', 'message']
          },
          http_method: 'POST',
          url: `${API_BASE_URL}/api/function-calls/send-sms`
        };
        break;
      case 'extract_variable':
        newFunction = {
          type: 'function',
          name: 'extract_variable',
          description: 'Extract dynamic variables from the conversation.',
          parameters: {
            type: 'object',
            properties: {
              variable_name: {
                type: 'string',
                description: 'Name of the variable to extract'
              },
              extraction_rule: {
                type: 'string',
                description: 'Rule or pattern for extraction'
              }
            },
            required: ['variable_name']
          }
        };
        break;
      case 'custom':
        newFunction = {
          type: 'function',
          name: 'custom_function',
          description: 'Custom function for specific actions.',
          parameters: {
            type: 'object',
            properties: {
              action: {
                type: 'string',
                description: 'Action to perform'
              },
              parameters: {
                type: 'object',
                description: 'Additional parameters for the action'
              }
            },
            required: ['action']
          }
        };
        break;
      default:
        return;
    }
    
    setMultiPromptData(prev => ({
      ...prev,
      general_tools: [...prev.general_tools, newFunction]
    }));
    
    // Close modal and reset state
    setFunctionConfigModalOpen(false);
    setUnifiedModalMode(null);
    setUnifiedModalStateIndex(null);
    setSelectedFunctionType('');
    setFunctionConfig({ 
      name: '', 
      description: '', 
      transfer_to: 'static',
      destination_number: '', 
      transfer_type: 'cold',
      displayed_phone_number: 'retell_agent',
      custom_sip_headers: [],
      tool_name: 'transfer_call',
      tool_description: 'Transfer the call to support when the user needs human assistance.',
      pause_detection_delay: '1000',
      sms_content: ''
    });
    
    // Reset Agent Transfer configuration
    setAgentTransferConfig({
      function_name: 'agent_transfer',
      description: '',
      selected_agent: '',
      speak_during_execution: false,
      post_call_analysis: 'transferred_agent_only'
    });
    
    // Trigger save to backend
    handleMultiPromptUpdate();
  };

  const handleCancelFunction = () => {
    setFunctionConfigModalOpen(false);
    setUnifiedModalMode(null);
    setUnifiedModalStateIndex(null);
    setSelectedFunctionType('');
    setFunctionConfig({ 
      name: '', 
      description: '', 
      transfer_to: 'static',
      destination_number: '', 
      transfer_type: 'cold',
      displayed_phone_number: 'retell_agent',
      custom_sip_headers: [],
      tool_name: 'transfer_call',
      tool_description: 'Transfer the call to support when the user needs human assistance.',
      pause_detection_delay: '1000',
      sms_content: ''
    });
    // Reset Agent Transfer configuration
    setAgentTransferConfig({
      function_name: 'agent_transfer',
      description: '',
      selected_agent: '',
      speak_during_execution: false,
      post_call_analysis: 'transferred_agent_only'
    });
  };

  const handleSaveTool = async () => {
    // Validate required fields for transfer call
    if (selectedToolType === 'transfer_call' && !toolConfig.destination_number.trim()) {
      alert('Destination number is required for transfer call function.');
      return;
    }

    let newTool;
    
    if (selectedToolType === 'transfer_call') {
      newTool = {
        name: toolConfig.name || 'transfer_call',
        type: 'transfer_call',
        description: toolConfig.description || 'Transfer the call to a human agent.',
        transfer_to: toolConfig.transfer_to,
        destination_number: toolConfig.destination_number,
        transfer_type: toolConfig.transfer_type,
        displayed_phone_number: toolConfig.displayed_phone_number,
        custom_sip_headers: toolConfig.custom_sip_headers
      };

      // Send transfer call details to backend to update LLM
      try {
        console.log('[Transfer Call] Sending transfer call details to backend:', {
          agent_id: agent?.agent_id,
          tool_config: toolConfig
        });

        const response = await fetch(`${API_BASE_URL}/api/agents/add-transfer-tool`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            agent_id: agent?.agent_id,
            tool_name: toolConfig.name || 'transfer_call',
            description: toolConfig.description || 'Transfer the call to a human agent.',
            transfer_number: toolConfig.destination_number, // Map destination_number to transfer_number
            transfer_type: toolConfig.transfer_type,
            transfer_to: toolConfig.transfer_to,
            displayed_phone_number: toolConfig.displayed_phone_number,
            custom_sip_headers: toolConfig.custom_sip_headers
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add transfer tool to LLM');
        }

        const result = await response.json();
        console.log('[Transfer Call] Successfully added transfer tool to LLM:', result);

        // Set the result to display
        setTransferCallResult({
          success: true,
          tool_name: toolConfig.name || 'transfer_call',
          destination_number: toolConfig.destination_number,
          transfer_type: toolConfig.transfer_type,
          transfer_to: toolConfig.transfer_to,
          displayed_phone_number: toolConfig.displayed_phone_number,
          backend_result: result,
          timestamp: new Date().toISOString()
        });
        setShowTransferResult(true);

        // Refresh transfer phone number to display the newly added number
        fetchTransferPhoneNumber();
        
        // Refresh LLM data to show updated configuration
        await refreshLLMData();
        
        // Transfer popup logic removed - no longer needed

        // Show success message
        alert(`Transfer Call tool successfully added to LLM!\n\nTool Name: ${toolConfig.name || 'transfer_call'}\nDestination: ${toolConfig.destination_number}\nTransfer Type: ${toolConfig.transfer_type}`);

      } catch (error) {
        console.error('[Transfer Call] Error adding transfer tool to LLM:', error);
        alert(`Failed to add transfer tool to LLM: ${error.message}`);
        return; // Don't proceed if backend update fails
      }
    }

    // Add the tool to the appropriate location
    if (selectedStateIndex !== null) {
      // Add to specific state
      setMultiPromptData(prev => ({
        ...prev,
        states: prev.states.map((state, index) => 
          index === selectedStateIndex ? {
            ...state,
            tools: [...state.tools, newTool]
          } : state
        )
      }));
    } else {
      // Add to general tools
      setMultiPromptData(prev => ({
        ...prev,
        general_tools: [...prev.general_tools, newTool]
      }));
    }
    
    // Close modal and reset state
    setToolConfigModalOpenProtected(false);
    setSelectedToolType('');
    setSelectedStateIndex(null);
    setToolConfig({ 
      name: '', 
      description: '', 
      transfer_to: 'static',
      destination_number: '', 
      transfer_type: 'cold',
      displayed_phone_number: 'retell_agent',
      custom_sip_headers: []
    });
    setModalOpenTime(null);
    setIsModalProtected(false);
    
    // Trigger save to backend
    handleMultiPromptUpdate();
    
    // If a transfer tool was added, refresh transfer details
    // Transfer popup logic removed - no longer needed
  };

  const handleCancelTool = () => {
    console.log('[Transfer Modal] Cancel button clicked');
    
    // Check if modal was just opened (less than 3 seconds ago)
    if (modalOpenTime && Date.now() - modalOpenTime < 3000) {
      console.log('[Transfer Modal] Modal protection active - preventing premature close');
      if (!window.confirm('Are you sure you want to close the Transfer Call configuration? The modal was just opened.')) {
        return; // Don't close if user cancels
      }
    }
    
    console.log('[Transfer Modal] Closing modal');
    setToolConfigModalOpenProtected(false);
    setSelectedToolType('');
    setSelectedStateIndex(null);
    setToolConfig({ 
      name: '', 
      description: '', 
      transfer_to: 'static',
      destination_number: '', 
      transfer_type: 'cold',
      displayed_phone_number: 'retell_agent',
      custom_sip_headers: []
    });
    setModalOpenTime(null);
    setIsModalProtected(false);
  };

  const handleRemoveGeneralTool = (toolIndex) => {
    setMultiPromptData(prev => ({
      ...prev,
      general_tools: prev.general_tools.filter((_, index) => index !== toolIndex)
    }));
  };

  const handleUpdateGeneralTool = (toolIndex, field, value) => {
    setMultiPromptData(prev => ({
      ...prev,
      general_tools: prev.general_tools.map((tool, index) => 
        index === toolIndex ? { ...tool, [field]: value } : tool
      )
    }));
  };

  // Handle editing existing function
  const handleEditFunction = (toolIndex) => {
    const tool = multiPromptData.general_tools[toolIndex];
    setEditingFunctionIndex(toolIndex);
    
    // Set the edit config with existing tool data
    setEditFunctionConfig({
      name: tool.name || '',
      description: tool.description || '',
      transfer_to: tool.transfer_to || 'static',
      destination_number: tool.destination_number || '',
      transfer_type: tool.transfer_type || 'cold',
      displayed_phone_number: tool.displayed_phone_number || 'retell_agent',
      custom_sip_headers: tool.custom_sip_headers || [],
      tool_name: tool.tool_name || tool.name || '',
      tool_description: tool.tool_description || tool.description || ''
    });
    
    setEditFunctionModalOpen(true);
  };

  // Handle saving edited function
  const handleSaveEditedFunction = async () => {
    if (!editingFunctionIndex !== null) {
      // Update the function in the general_tools array
      setMultiPromptData(prev => ({
        ...prev,
        general_tools: prev.general_tools.map((tool, index) => 
          index === editingFunctionIndex ? {
            ...tool,
            name: editFunctionConfig.name,
            description: editFunctionConfig.description,
            transfer_to: editFunctionConfig.transfer_to,
            destination_number: editFunctionConfig.destination_number,
            transfer_type: editFunctionConfig.transfer_type,
            displayed_phone_number: editFunctionConfig.displayed_phone_number,
            custom_sip_headers: editFunctionConfig.custom_sip_headers,
            tool_name: editFunctionConfig.tool_name,
            tool_description: editFunctionConfig.tool_description
          } : tool
        )
      }));

      // Refresh transfer phone number if this was a transfer_call function
      if (editFunctionConfig.tool_name === 'transfer_call' || editFunctionConfig.name === 'transfer_call') {
        fetchTransferPhoneNumber();
      }

      // Close modal and reset state
      setEditFunctionModalOpen(false);
      setEditingFunctionIndex(null);
      setEditFunctionConfig({
        name: '',
        description: '',
        transfer_to: 'static',
        destination_number: '',
        transfer_type: 'cold',
        displayed_phone_number: 'retell_agent',
        custom_sip_headers: [],
        tool_name: '',
        tool_description: ''
      });
    }
  };

  // Handle canceling edit function
  const handleCancelEditFunction = () => {
    setEditFunctionModalOpen(false);
    setEditingFunctionIndex(null);
    setEditFunctionConfig({
      name: '',
      description: '',
      transfer_to: 'static',
      destination_number: '',
      transfer_type: 'cold',
      displayed_phone_number: 'retell_agent',
      custom_sip_headers: [],
      tool_name: '',
      tool_description: ''
    });
  };

  // Verified Numbers Connection Handler
  const handleVerifiedNumbersConnection = async (connectionType) => {
    if (!agent?.agent_id || !userEmail) return;
    
    setVerifiedNumbersLoading(true);
    try {
      if (connectionType === 'connect') {
        // Use the verified phone numbers we already fetched for this agent
        if (!verifiedPhoneNumbersList || verifiedPhoneNumbersList.length === 0) {
          throw new Error('No verified phone numbers found for this agent. Please add some verified numbers first.');
        }
        
        // Convert the phone numbers to the format expected by the existing system
        const verifiedNumbers = verifiedPhoneNumbersList.map(phone => ({
          phone_no: phone.phone_no,
          created_at: phone.created_at
        }));
        
        console.log('ðŸ“ž Using verified phone numbers for this agent:', verifiedNumbers);
        
        // Send verified numbers to the agent/chatbot
        const agentResponse = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/verified-numbers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            verified_numbers: verifiedNumbers,
            user_id: userId
          })
        });
        
        if (!agentResponse.ok) {
          const errorData = await agentResponse.json();
          throw new Error(errorData.error || 'Failed to connect verified numbers to agent');
        }
        
        setVerifiedNumbersConnection('connected');
        alert('Verified numbers successfully connected to this agent!');
      } else {
        // First, get the current connections for this agent to delete them specifically
        const connectionsResponse = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/verified-numbers/connections?user_id=${encodeURIComponent(userId)}`);
        
        if (!connectionsResponse.ok) {
          throw new Error('Failed to fetch current connections');
        }
        
        const connectionsData = await connectionsResponse.json();
        
        if (!connectionsData.success) {
          throw new Error(connectionsData.error || 'Failed to fetch current connections');
        }
        
        // If there are connections, delete them specifically
        if (connectionsData.connections && connectionsData.connections.length > 0) {
          const agentResponse = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/verified-numbers`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              connections_to_delete: connectionsData.connections
            })
          });
          
          if (!agentResponse.ok) {
            const errorData = await agentResponse.json();
            throw new Error(errorData.error || 'Failed to disconnect verified numbers from agent');
          }
          
          setVerifiedNumbersConnection('disconnected');
          alert('Verified numbers successfully disconnected from this agent!');
        } else {
          // No connections to delete
          setVerifiedNumbersConnection('disconnected');
          alert('No verified numbers were connected to this agent.');
        }
      }
    } catch (err) {
      console.error('Error handling verified numbers connection:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setVerifiedNumbersLoading(false);
      // Refresh connection status after the operation
      fetchVerifiedNumbersConnectionStatus();
    }
  };

  // Function to fetch inbound agent phone number
  const fetchInboundAgentPhoneNumber = async (agentId) => {
    if (!agentId || !userId) return;
    
    setInboundAgentPhoneLoading(true);
    try {
      console.log('ðŸ“ž Fetching inbound agent phone number for agent:', agentId, 'user:', userId);
      
      // Fetch phone numbers from phone_numbers table where inbound_agent_id matches agent_id and user_id matches
      const response = await fetch(`${API_BASE_URL}/api/phone-numbers?inbound_agent_id=${encodeURIComponent(agentId)}&user_id=${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const phoneData = await response.json();
        console.log('ðŸ“ž Phone numbers data from phone_numbers table:', phoneData);
        
        // Process the phone numbers data
        if (phoneData && phoneData.length > 0) {
          // Find the phone number where inbound_agent_id matches the current agent_id
          const inboundPhone = phoneData.find(phone => phone.inbound_agent_id === agentId);
          if (inboundPhone) {
            setInboundAgentPhoneNumber(inboundPhone.phone_numbers);
            console.log('ðŸ“ž Found inbound phone number:', inboundPhone.phone_numbers);
            console.log('ðŸ“ž Phone number details:', {
              phone_numbers: inboundPhone.phone_numbers,
              inbound_agent_id: inboundPhone.inbound_agent_id,
              user_id: inboundPhone.user_id,
              nickname: inboundPhone.nickname
            });
          } else {
            console.log('âš ï¸ No inbound phone number found for this agent in phone_numbers table');
            setInboundAgentPhoneNumber(null);
          }
        } else {
          console.log('âš ï¸ No phone numbers found in phone_numbers table for this agent and user');
          setInboundAgentPhoneNumber(null);
        }
      } else {
        console.error('âŒ Failed to fetch phone numbers from phone_numbers table');
        setInboundAgentPhoneNumber(null);
      }
    } catch (error) {
      console.error('âŒ Error fetching inbound agent phone number:', error);
      setInboundAgentPhoneNumber(null);
    } finally {
      setInboundAgentPhoneLoading(false);
    }
  };

  console.log('speechSettings:', speechSettings);

  // Add local state for transcription settings radio groups
  const [localDenoisingMode, setLocalDenoisingMode] = useState(transcriptionSettings.denoising_mode);
  const [localSttMode, setLocalSttMode] = useState(transcriptionSettings.stt_mode);
  const [localVocabSpecialization, setLocalVocabSpecialization] = useState(transcriptionSettings.vocab_specialization);

  // Sync local state with backend when backend value changes
  useEffect(() => { setLocalDenoisingMode(transcriptionSettings.denoising_mode); }, [transcriptionSettings.denoising_mode]);
  useEffect(() => { setLocalSttMode(transcriptionSettings.stt_mode); }, [transcriptionSettings.stt_mode]);
  useEffect(() => { setLocalVocabSpecialization(transcriptionSettings.vocab_specialization); }, [transcriptionSettings.vocab_specialization]);

  // Debounce sync to backend
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localDenoisingMode !== transcriptionSettings.denoising_mode) {
        handleTranscriptionSettingsUpdate({ denoising_mode: localDenoisingMode });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localDenoisingMode]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSttMode !== transcriptionSettings.stt_mode) {
        handleTranscriptionSettingsUpdate({ stt_mode: localSttMode });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localSttMode]);
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localVocabSpecialization !== transcriptionSettings.vocab_specialization) {
        handleTranscriptionSettingsUpdate({ vocab_specialization: localVocabSpecialization });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localVocabSpecialization]);

  // Add state for publish modal
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [inboundPhoneNumber, setInboundPhoneNumber] = useState(false);
  const [outboundPhoneNumber, setOutboundPhoneNumber] = useState(false);
  const [isAgentPublished, setIsAgentPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishStatusLoading, setPublishStatusLoading] = useState(false);

  // Fetch publish status when component mounts
  useEffect(() => {
    const fetchPublishStatus = async () => {
      if (agentId && userEmail) {
        setPublishStatusLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/api/agents/${agentId}/publish-status?user_email=${encodeURIComponent(userEmail)}`);
          const data = await response.json();
          if (data.success) {
            setIsAgentPublished(data.is_published);
          }
        } catch (error) {
          console.error('Error fetching publish status:', error);
        } finally {
          setPublishStatusLoading(false);
        }
      }
    };

    fetchPublishStatus();
  }, [agentId, userEmail]);

  // Fetch available agents when component mounts
  useEffect(() => {
    if (userEmail) {
      fetchAvailableAgents();
    }
  }, [userEmail]);

  // Transfer call popup logic removed - no longer needed

  // Reset popup state logic removed - no longer needed

  // Function to fetch call transfer details from backend
  const fetchCallTransferDetails = async (agentData) => {
    try {
      console.log(`[Fetch Transfer Details] Fetching transfer details for agent ${agentData.agent_id}`);
      
      // Check if agent has agent_id
      if (!agentData.agent_id) {
        console.log('[Fetch Transfer Details] Missing agent_id, cannot fetch transfer details');
        return { hasTransferTool: false, transferTools: [] };
      }

      // Call the new backend endpoint
      const response = await fetch(`${API_BASE_URL}/api/agents/${agentData.agent_id}/transfer-details`);
      if (!response.ok) {
        console.error(`[Fetch Transfer Details] Failed to fetch transfer details: ${response.status} ${response.statusText}`);
        return { hasTransferTool: false, transferTools: [] };
      }

      const transferData = await response.json();
      console.log(`[Fetch Transfer Details] Transfer details retrieved for agent ${agentData.agent_id}:`, transferData);

      return {
        hasTransferTool: transferData.hasTransferTool,
        transferTools: transferData.transferTools || [],
        agent_id: transferData.agent_id,
        llm_id: transferData.llm_id,
        totalTools: transferData.totalTools || 0,
        message: transferData.message
      };
    } catch (error) {
      console.error('[Fetch Transfer Details] Error fetching transfer details:', error);
      return { hasTransferTool: false, transferTools: [] };
    }
  };

  // Function to fetch transfer phone number for the agent
  const fetchTransferPhoneNumber = async () => {
    if (!agent?.agent_id || !userId) {
      setTransferPhoneNumber(null);
      return;
    }

    setTransferPhoneNumberLoading(true);
    try {
      console.log(`[Transfer Phone] Fetching transfer phone number for agent ${agent.agent_id}`);
      
      // Fetch from agent_transfer_tools table
      const response = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/transfer-details?user_id=${encodeURIComponent(userId)}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Transfer Phone] Full transfer data received:', data);
        // Get the transfer number from transferTools
        if (data.transferTools && data.transferTools.length > 0) {
          const transferTool = data.transferTools.find(tool => tool.tool_name === 'transfer_call');
          console.log('[Transfer Phone] Found transfer tool:', transferTool);
          // Check for transfer_destination.number (new structure) or transfer_number (old structure)
          const phoneNumber = transferTool?.transfer_destination?.number || transferTool?.transfer_number;
          if (transferTool && phoneNumber) {
            setTransferPhoneNumber(phoneNumber);
            console.log(`[Transfer Phone] Found transfer phone number: ${phoneNumber}`);
          } else {
            console.log('[Transfer Phone] No phone number found in transfer tool');
            setTransferPhoneNumber(null);
          }
        } else {
          // Fallback: try to get from agent_phone_connection table
          const connectionResponse = await fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/phone-connection?user_id=${encodeURIComponent(userId)}`);
          if (connectionResponse.ok) {
            const connectionData = await connectionResponse.json();
            if (connectionData.notification_transfer_call) {
              setTransferPhoneNumber(connectionData.notification_transfer_call);
              console.log(`[Transfer Phone] Found transfer phone number from connection: ${connectionData.notification_transfer_call}`);
            } else {
              setTransferPhoneNumber(null);
            }
          } else {
            setTransferPhoneNumber(null);
          }
        }
      } else {
        setTransferPhoneNumber(null);
      }
    } catch (error) {
      console.error('[Transfer Phone] Error fetching transfer phone number:', error);
      setTransferPhoneNumber(null);
    } finally {
      setTransferPhoneNumberLoading(false);
    }
  };

  // Function to check if agent already has transfer_call function (legacy function for backward compatibility)
  const checkForTransferCallFunction = async (agentData) => {
    try {
      const { hasTransferTool } = await fetchCallTransferDetails(agentData);
      return hasTransferTool;
    } catch (error) {
      console.error('Error checking for transfer_call function:', error);
      return false;
    }
  };

  // refreshTransferDetails function removed - no longer needed

  // checkTransferDetails function removed - no longer needed

  return (
    <>
      {/* Function Configuration Modal */}
      {functionConfigModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            width: '90vw',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-link" style={{ 
                  color: '#6b7280', 
                  fontSize: '18px' 
                }}></i>
                <span style={{
                  color: '#1f2937',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  {selectedFunctionType === 'end_call' ? 'End Call' :
                   selectedFunctionType === 'call_transfer' ? 'Call Transfer' :
                   selectedFunctionType === 'agent_transfer' ? 'Agent Transfer' :
                   selectedFunctionType === 'check_calendar' ? 'Check Calendar Availability' :
                   selectedFunctionType === 'book_calendar' ? 'Book on the Calendar' :
                   selectedFunctionType === 'press_digit' ? 'Press Digit (IVR Navigation)' :
                   selectedFunctionType === 'send_sms' ? 'Send SMS' :
                   selectedFunctionType === 'extract_variable' ? '{ } Extract Dynamic Variable' :
                   selectedFunctionType === 'custom' ? 'Custom Function' : 'Function'}
                </span>
              </div>
              <button
                onClick={handleCancelFunction}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#1f2937'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Content */}
            <div style={{ marginBottom: '20px' }}>
              {/* End Call Specific Content */}
              {selectedFunctionType === 'end_call' ? (
                <>
                  {/* Name Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Name
                    </label>
                    <input
                      type="text"
                      value={functionConfig.name}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="end_call"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* Description Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Description (Optional)
                    </label>
                    <textarea
                      value={functionConfig.description}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter function description"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>
                </>
              ) : selectedFunctionType === 'call_transfer' ? (
                <>
                  {/* Name Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Name
                    </label>
                    <input
                      type="text"
                      value={functionConfig.name}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="transfer_call"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* Description Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Description
                    </label>
                    <textarea
                      value={functionConfig.description}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Transfer the call to a human agent when the user needs assistance."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>
                </>
              ) : selectedFunctionType === 'agent_transfer' ? null : selectedFunctionType === 'extract_variable' ? null : selectedFunctionType === 'send_sms' ? null : (
                <>
                  {/* Name Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Name
                    </label>
                    <input
                      type="text"
                      value={functionConfig.name}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter the name of the custom function"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* Description Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Description
                    </label>
                    <textarea
                      value={functionConfig.description}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter the description of the custom function"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>
                </>
              )}

              {/* Press Digit Specific Content */}
              {selectedFunctionType === 'press_digit' && (
                <>
                  {/* Pause Detection Delay Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Pause Detection Delay (Optional)
                      <i className="fas fa-info-circle" style={{ 
                        marginLeft: '6px', 
                        color: '#6b7280', 
                        fontSize: '12px' 
                      }}></i>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        value={functionConfig.pause_detection_delay || '1000'}
                        onChange={(e) => setFunctionConfig(prev => ({ ...prev, pause_detection_delay: e.target.value }))}
                        placeholder="1000"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          background: '#ffffff',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#1f2937',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                      <span style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        miliseconds
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Extract Dynamic Variable Specific Content */}
              {selectedFunctionType === 'extract_variable' && (
                <>
                  {/* Subtitle */}
                  <div style={{
                    color: '#6b7280',
                    fontSize: '14px',
                    marginBottom: '20px',
                    lineHeight: '1.5'
                  }}>
                    Extract variables so they can be used in subsequent dialogue steps.
                  </div>

                  {/* Function Name Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Function Name
                    </label>
                    <input
                      type="text"
                      value={functionConfig.name}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. extract_user_details"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* Description Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Description
                    </label>
                    <textarea
                      value={functionConfig.description}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g. Extract the user's details like name, email, age, etc. from the conversation"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* Variables Section */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Variables
                    </label>
                    <button
                      type="button"
                      style={{
                        padding: '10px 16px',
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e5e7eb';
                        e.target.style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '12px' }}></i>
                      Add
                    </button>
                  </div>
                </>
              )}

              {/* Send SMS Specific Content */}
              {selectedFunctionType === 'send_sms' && (
                <>
                  {/* Name Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Name
                    </label>
                    <input
                      type="text"
                      value={functionConfig.name}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="send_sms"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* Description Field */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Description (Optional)
                    </label>
                    <textarea
                      value={functionConfig.description}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g. Functions for sending SMS"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                  {/* SMS Content Section */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      SMS content
                    </label>
                    
                    {/* Content Type Tabs */}
                    <div style={{ 
                      display: 'flex', 
                      marginBottom: '12px',
                      background: '#f3f4f6',
                      borderRadius: '6px',
                      padding: '2px'
                    }}>
                      <button
                        type="button"
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          background: '#3b82f6',
                          border: 'none',
                          borderRadius: '4px',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                      >
                        Prompt
                      </button>
                      <button
                        type="button"
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '4px',
                          color: '#6b7280',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e5e7eb';
                          e.target.style.color = '#374151';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = '#6b7280';
                        }}
                      >
                        Static Sentence
                      </button>
                    </div>
                    
                    {/* Content Input Area */}
                    <textarea
                      value={functionConfig.sms_content || ''}
                      onChange={(e) => setFunctionConfig(prev => ({ ...prev, sms_content: e.target.value }))}
                      placeholder="e.g. Inform the user that their appointment has been booked"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '100px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>
                </>
              )}

              {/* Agent Transfer Specific Content */}
              {selectedFunctionType === 'agent_transfer' && (
                <>
                  {/* Function Name Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '8px'
                    }}>
                      Function Name
                    </label>
                    <input
                      type="text"
                      value={agentTransferConfig.function_name}
                      onChange={(e) => setAgentTransferConfig(prev => ({ ...prev, function_name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        color: '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  {/* Information Banner */}
                  <div style={{
                    background: '#dbeafe',
                    border: '1px solid #93c5fd',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <i className="fas fa-info-circle" style={{ color: '#3b82f6', fontSize: '16px', marginTop: '2px' }}></i>
                    <div style={{ color: '#1e40af', fontSize: '14px', lineHeight: '1.5' }}>
                      It will be a seamless transition, with all the <strong>call context preserved</strong>. It will appear as a single call in history.
                    </div>
                  </div>

                  {/* Select Agent Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '8px'
                    }}>
                      Select Agent
                    </label>
                    <select
                      value={agentTransferConfig.selected_agent}
                      onChange={(e) => setAgentTransferConfig(prev => ({ ...prev, selected_agent: e.target.value }))}
                      disabled={agentsLoading}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        color: agentsLoading ? '#9ca3af' : '#1f2937',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        cursor: agentsLoading ? 'not-allowed' : 'pointer'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <option value="">
                        {agentsLoading ? 'Loading agents...' : 'Select an agent...'}
                      </option>
                      {availableAgents.length === 0 && !agentsLoading ? (
                        <option value="" disabled>No agents available</option>
                      ) : (
                        availableAgents.map(agent => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name} {agent.description}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Voice Section */}
                  {agentTransferConfig.selected_agent && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        display: 'block',
                        color: '#1f2937',
                        fontSize: '14px',
                        fontWeight: '500',
                        marginBottom: '8px'
                      }}>
                        Voice
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {(() => {
                            const selectedAgent = availableAgents.find(a => a.id === agentTransferConfig.selected_agent);
                            return selectedAgent?.voice?.charAt(0)?.toUpperCase() || 'V';
                          })()}
                        </div>
                        <span style={{ color: '#1f2937', fontSize: '14px' }}>
                          {(() => {
                            const selectedAgent = availableAgents.find(a => a.id === agentTransferConfig.selected_agent);
                            return selectedAgent?.voice || 'Default Voice';
                          })()}
                        </span>
                        <i className="fas fa-external-link-alt" style={{ color: '#9ca3af', fontSize: '12px' }}></i>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                        It will use the voice that you set for the selected agent.
                      </div>
                    </div>
                  )}

                  {/* Execution Message Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <input
                        type="checkbox"
                        checked={agentTransferConfig.speak_during_execution}
                        onChange={(e) => setAgentTransferConfig(prev => ({ ...prev, speak_during_execution: e.target.checked }))}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <label style={{
                        color: '#1f2937',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}>
                        Speak During Execution
                      </label>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '12px', marginLeft: '24px' }}>
                      If the function takes over 2 seconds, the agent can say something like: 'Let me check that for you.'
                    </div>
                  </div>

                  {/* Post Call Analysis Setting Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      color: '#1f2937',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '8px'
                    }}>
                      Post Call Analysis Setting
                    </label>
                    <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '12px' }}>
                      Select which agent's analysis to include in the call history, noting that including both increases usage cost.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '100%' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <input
                          type="radio"
                          name="post_call_analysis"
                          value="transferred_agent_only"
                          checked={agentTransferConfig.post_call_analysis === 'transferred_agent_only'}
                          onChange={(e) => setAgentTransferConfig(prev => ({ ...prev, post_call_analysis: e.target.value }))}
                          style={{
                            accentColor: '#3b82f6',
                            width: '16px',
                            height: '16px'
                          }}
                        />
                        <label style={{
                          color: '#1f2937',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}>
                          Only transferred agent
                        </label>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <input
                          type="radio"
                          name="post_call_analysis"
                          value="both_agents"
                          checked={agentTransferConfig.post_call_analysis === 'both_agents'}
                          onChange={(e) => setAgentTransferConfig(prev => ({ ...prev, post_call_analysis: e.target.value }))}
                          style={{
                            accentColor: '#3b82f6',
                            width: '16px',
                            height: '16px'
                          }}
                        />
                        <label style={{
                          color: '#1f2937',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}>
                          Both this agent and transferred agent
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Transfer Call Specific Fields - Simplified to show only Destination Number */}
              {selectedFunctionType === 'call_transfer' && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    color: '#1f2937',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '6px'
                  }}>
                    Destination Phone Number
                  </label>
                  <input
                    type="text"
                    value={functionConfig.destination_number}
                    onChange={(e) => setFunctionConfig(prev => ({ ...prev, destination_number: e.target.value }))}
                    placeholder="Enter phone number (e.g., +1234567890)"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      color: '#1f2937',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              )}
            </div>

            {/* API Endpoint - Hidden for End Call, Call Transfer, Press Digit, Extract Variable, and Send SMS */}
            {selectedFunctionType !== 'end_call' && selectedFunctionType !== 'call_transfer' && selectedFunctionType !== 'press_digit' && selectedFunctionType !== 'extract_variable' && selectedFunctionType !== 'send_sms' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                API Endpoint
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  value="POST"
                  style={{
                    padding: '10px 12px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#1f2937',
                    fontSize: '14px',
                    outline: 'none',
                    minWidth: '70px',
                    width: '70px'
                  }}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
                <input
                  type="text"
                  placeholder="Enter the URL of the custom function"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#1f2937',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            </div>
            )}

            {/* Additional fields - Hidden for End Call, Call Transfer, Press Digit, Extract Variable, and Send SMS */}
            {selectedFunctionType !== 'end_call' && selectedFunctionType !== 'call_transfer' && selectedFunctionType !== 'press_digit' && selectedFunctionType !== 'extract_variable' && selectedFunctionType !== 'send_sms' && (
            <>
            {/* Timeout */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Timeout (ms)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="number"
                  value="120000"
                  style={{
                    width: '120px',
                    padding: '10px 12px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#1f2937',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                <span style={{ color: '#6b7280', fontSize: '12px' }}>milliseconds</span>
              </div>
            </div>

            {/* Headers */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Headers
              </label>
              <div style={{
                color: '#6b7280',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                Specify the HTTP headers required for your API request.
              </div>
              <button
                style={{
                  background: '#f3f4f6',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                New key value pair
              </button>
            </div>

            {/* Query Parameters */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Query Parameters
              </label>
              <div style={{
                color: '#6b7280',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                Query string parameters to append to the URL.
              </div>
              <button
                style={{
                  background: '#f3f4f6',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                New key value pair
              </button>
            </div>

            {/* Parameters (Optional) */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Parameters (Optional)
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>Payload: args only</span>
                  <div style={{
                    width: '40px',
                    height: '20px',
                    background: '#e5e7eb',
                    borderRadius: '10px',
                    position: 'relative',
                    cursor: 'pointer'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      background: '#9ca3af',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: '2px',
                      transition: 'transform 0.2s ease'
                    }}></div>
                  </div>
                </div>
              </div>
              <textarea
                placeholder="Enter JSON Schema here..."
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#1f2937',
                  fontSize: '14px',
                  outline: 'none',
                  minHeight: '120px',
                  resize: 'vertical',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            {/* Response Variables */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Response Variables
              </label>
              <div style={{
                color: '#6b7280',
                fontSize: '12px',
                marginBottom: '8px'
              }}>
                Extracted values from API response saved as dynamic variables.
              </div>
              <button
                style={{
                  background: '#f3f4f6',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                New key value pair
              </button>
            </div>

            {/* Speak During Execution - Hidden for End Call, Call Transfer, Press Digit, Extract Variable, and Send SMS */}
            {selectedFunctionType !== 'end_call' && selectedFunctionType !== 'call_transfer' && selectedFunctionType !== 'press_digit' && selectedFunctionType !== 'extract_variable' && selectedFunctionType !== 'send_sms' && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  style={{
                    width: '16px',
                    height: '16px',
                    marginTop: '2px'
                  }}
                />
                <div>
                  <label style={{
                    color: '#1f2937',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Speak During Execution
                  </label>
                  <div style={{
                    color: '#6b7280',
                    fontSize: '12px'
                  }}>
                    If the function takes over 2 seconds, the agent can say something like: "Let me check that for you."
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Speak After Execution - Hidden for End Call, Call Transfer, Press Digit, Extract Variable, and Send SMS */}
            {selectedFunctionType !== 'end_call' && selectedFunctionType !== 'call_transfer' && selectedFunctionType !== 'press_digit' && selectedFunctionType !== 'extract_variable' && selectedFunctionType !== 'send_sms' && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked
                  style={{
                    width: '16px',
                    height: '16px',
                    marginTop: '2px'
                  }}
                />
                <div>
                  <label style={{
                    color: '#1f2937',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Speak After Execution
                  </label>
                  <div style={{
                    color: '#6b7280',
                    fontSize: '12px'
                  }}>
                    Unselect if you want to run the function silently, such as uploading the call result to the server silently.
                  </div>
                </div>
              </div>
            </div>
            )}
            </>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              {selectedFunctionType === 'end_call' ? (
                <>
                  <button
                    onClick={handleCancelFunction}
                    style={{
                      background: '#ffffff',
                      color: '#1f2937',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.target.style.background = '#ffffff'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveFunction}
                    style={{
                      background: '#3b82f6',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                    onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleCancelFunction}
                    style={{
                      background: '#f3f4f6',
                      color: '#1f2937',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                    onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveFunction}
                    style={{
                      background: '#059669',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#047857'}
                    onMouseLeave={(e) => e.target.style.background = '#059669'}
                  >
                    Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tool Configuration Modal */}
      {toolConfigModalOpen && selectedToolType === 'transfer_call' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            width: selectedToolType === 'transfer_call' ? '600px' : '400px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-phone" style={{ color: '#6b7280', fontSize: '18px' }}></i>
                <span style={{
                  color: '#f9fafb',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  {selectedToolType === 'transfer_call' ? 'Transfer Call' : 'Tool Configuration'}
                </span>
                {isModalProtected && (
                  <span style={{
                    background: '#059669',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500',
                    marginLeft: '8px'
                  }}>
                    PROTECTED
                  </span>
                )}
              </div>
              <button
                onClick={handleCancelTool}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Content */}
            <div style={{ marginBottom: '20px' }}>
              {/* Name Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  color: '#1f2937',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px'
                }}>
                  Name
                </label>
                <input
                  type="text"
                  value={toolConfig.name}
                  onChange={(e) => setToolConfig(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                />
              </div>

              {/* Description Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  color: '#f9fafb',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px'
                }}>
                  Description (Optional)
                </label>
                <textarea
                  value={toolConfig.description}
                  onChange={(e) => setToolConfig(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter function description"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                />
              </div>

              {/* Transfer Call Specific Fields - Simplified to show only Name, Description, and Destination Number */}
              {selectedToolType === 'transfer_call' && (
                <>
                  {/* Destination Phone Number Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      display: 'block',
                      color: '#f9fafb',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '6px'
                    }}>
                      Destination Phone Number
                    </label>
                    <input
                      type="text"
                      value={toolConfig.destination_number}
                      onChange={(e) => setToolConfig(prev => ({ ...prev, destination_number: e.target.value }))}
                      placeholder="Enter phone number (e.g., +1234567890)"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#374151',
                        border: '1px solid #4b5563',
                        borderRadius: '6px',
                        color: '#f9fafb',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#4b5563'}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCancelTool}
                style={{
                  padding: '10px 16px',
                  background: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#4b5563'}
                onMouseLeave={(e) => e.target.style.background = '#374151'}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTool}
                style={{
                  padding: '10px 16px',
                  background: '#f9fafb',
                  border: '1px solid #f9fafb',
                  borderRadius: '6px',
                  color: '#1f2937',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f9fafb'}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Call Result Display Modal */}
      {showTransferResult && transferCallResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '18px' }}></i>
                <span style={{
                  color: '#f9fafb',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  Transfer Call Tool Added Successfully
                </span>
              </div>
              <button
                onClick={() => setShowTransferResult(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Content */}
            <div style={{ marginBottom: '20px' }}>
              {/* Success Message */}
              <div style={{
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#065f46',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-check-circle"></i>
                  Transfer Call tool has been successfully added to the LLM configuration
                </div>
              </div>

              {/* Tool Details */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  color: '#f9fafb',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Tool Configuration Details
                </h3>
                <div style={{
                  background: '#374151',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                    <div>
                      <span style={{ color: '#9ca3af', fontWeight: '500' }}>Tool Name:</span>
                      <span style={{ color: '#f9fafb', marginLeft: '8px' }}>{transferCallResult.tool_name}</span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontWeight: '500' }}>Destination Number:</span>
                      <span style={{ color: '#f9fafb', marginLeft: '8px' }}>{transferCallResult.destination_number}</span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontWeight: '500' }}>Transfer Type:</span>
                      <span style={{ color: '#f9fafb', marginLeft: '8px' }}>{transferCallResult.transfer_type}</span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontWeight: '500' }}>Transfer To:</span>
                      <span style={{ color: '#f9fafb', marginLeft: '8px' }}>{transferCallResult.transfer_to}</span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontWeight: '500' }}>Displayed Phone:</span>
                      <span style={{ color: '#f9fafb', marginLeft: '8px' }}>{transferCallResult.displayed_phone_number}</span>
                    </div>
                    <div>
                      <span style={{ color: '#9ca3af', fontWeight: '500' }}>Added At:</span>
                      <span style={{ color: '#f9fafb', marginLeft: '8px' }}>{new Date(transferCallResult.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current LLM Tools */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                  color: '#f9fafb',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  Current LLM Tools ({multiPromptData.general_tools.length} tools)
                </h3>
                <div style={{
                  background: '#374151',
                  borderRadius: '8px',
                  padding: '16px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {multiPromptData.general_tools.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {multiPromptData.general_tools.map((tool, index) => (
                        <div key={index} style={{
                          background: tool.type === 'transfer_call' ? '#10b981' : '#4b5563',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <i className={`fas ${tool.type === 'transfer_call' ? 'fa-phone' : 'fa-tools'}`} 
                             style={{ color: '#f9fafb', fontSize: '12px' }}></i>
                          <span style={{ color: '#f9fafb', fontSize: '12px', fontWeight: '500' }}>
                            {tool.name || tool.type}
                          </span>
                          {tool.type === 'transfer_call' && (
                            <>
                              <span style={{
                                background: '#ffffff',
                                color: '#10b981',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '600',
                                marginRight: 4
                              }}>
                                NEW
                              </span>
                              {(tool.destination_number || tool.transfer_option?.destination_number) && (
                                <span style={{
                                  fontSize: '10px',
                                  color: '#f9fafb',
                                  opacity: 0.9
                                }}>
                                  • {tool.destination_number || tool.transfer_option?.destination_number}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center' }}>
                      No tools configured yet
                    </div>
                  )}
                </div>
              </div>

              {/* Backend Response */}
              {transferCallResult.backend_result && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{
                    color: '#f9fafb',
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    Backend Response
                  </h3>
                  <div style={{
                    background: '#374151',
                    borderRadius: '8px',
                    padding: '16px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: '#f9fafb',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(transferCallResult.backend_result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '20px',
              borderTop: '1px solid #374151'
            }}>
              <button
                onClick={async () => {
                  await refreshLLMData();
                  alert('LLM configuration refreshed! Check the tools section to see the updated configuration.');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  border: '1px solid #10b981',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                Refresh LLM Config
              </button>
              <button
                onClick={() => setShowTransferResult(false)}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @media (max-width: 768px) {
            body {
              overflow-y: hidden !important;
              overflow-x: auto !important;
              position: fixed;
              width: 100%;
              height: 100%;
            }
            html {
              overflow-y: hidden !important;
              overflow-x: auto !important;
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ 
        minWidth: '1200px', 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        background: '#f8fafc', 
        margin: 0, 
        padding: 0, 
        overflow: 'hidden',
        overflowY: 'hidden',
        overflowX: 'auto'
      }}>
      {/* Fixed header, flush to top */}
      <div style={{ position: 'sticky', top: 0, left: 0, right: 0, background: '#fff', zIndex: 10, padding: '20px 32px 12px 32px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: '1.3rem', color: '#222', display: 'flex', flexDirection: 'column', gap: 0, margin: 0, minWidth: '1200px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              style={{ background: 'none', border: 'none', fontSize: 22, color: '#64748b', marginRight: 12, cursor: 'pointer' }}
              title="Back"
              onClick={() => {
                window.history.pushState({}, '', '/dashboard');
                window.dispatchEvent(new Event('popstate'));
              }}
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <span style={{ background: '#e0e7ff', color: '#6366f1', borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 22 }}>
              <i className="fas fa-robot"></i>
            </span>
            {/* Agent name with edit functionality */}
            {isEditingAgentName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  value={editedAgentName}
                  onChange={(e) => setEditedAgentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveAgentName();
                    } else if (e.key === 'Escape') {
                      handleCancelEditAgentName();
                    }
                  }}
                  autoFocus
                  style={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    color: '#222',
                    border: '2px solid #6366f1',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    outline: 'none',
                    minWidth: 200,
                    maxWidth: 300
                  }}
                  disabled={updatingAgentName}
                />
                <button
                  onClick={handleSaveAgentName}
                  disabled={updatingAgentName}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: updatingAgentName ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: updatingAgentName ? 0.6 : 1
                  }}
                  title="Save"
                >
                  <i className="fas fa-check"></i>
                </button>
                <button
                  onClick={handleCancelEditAgentName}
                  disabled={updatingAgentName}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: updatingAgentName ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: updatingAgentName ? 0.6 : 1
                  }}
                  title="Cancel"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: '1.3rem', color: '#222', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {agent?.agent_name || 'Agent'}
                </span>
                <button
                  onClick={handleStartEditAgentName}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#6366f1';
                    e.target.style.background = '#e0e7ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#64748b';
                    e.target.style.background = 'none';
                  }}
                  title="Edit agent name"
                >
                  <i className="fas fa-edit"></i>
                </button>
              </div>
            )}
          </div>
          
          {/* Top Right Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Publish Button */}
            <button
              onClick={() => setPublishModalOpen(true)}
              style={{
                background: '#000',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              title="Publish Agent"
            >
              <i className="fas fa-upload" style={{ fontSize: '12px' }}></i>
              Publish
            </button>
            
            {/* Assistant Button - COMMENTED OUT */}
            {false && (
              <button
                onClick={() => setAssistantPanelOpen(!assistantPanelOpen)}
                style={{
                  background: assistantPanelOpen ? 'transparent' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: assistantPanelOpen ? '50%' : '25px',
                  width: assistantPanelOpen ? '40px' : 'auto',
                  height: '40px',
                  fontSize: assistantPanelOpen ? '18px' : '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: assistantPanelOpen ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: assistantPanelOpen ? '0' : '8px 16px',
                  minWidth: assistantPanelOpen ? '40px' : '90px',
                  transition: 'all 0.3s ease'
                }}
                title={assistantPanelOpen ? "Close Assistant" : "Assistant"}
              >
                {assistantPanelOpen ? '' : 'Assistant'}
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 66, marginTop: 6 }}>
          <span
            ref={costRef}
            style={{ cursor: 'pointer', fontWeight: 400, fontSize: '0.97rem' }}
            onMouseEnter={() => setShowCostTooltip(true)}
            onMouseLeave={() => setShowCostTooltip(false)}
          >
            {estimatesLoading ? (
              <span>Loading estimates...</span>
            ) : estimatesError ? (
              <span style={{ color: '#dc2626' }}>Failed to load estimates</span>
            ) : agentEstimates ? (
              <>
                <span style={{ cursor: 'pointer', fontWeight: 400, fontSize: '0.97rem' }}
                  onMouseEnter={() => setShowCostTooltip(true)}
                  onMouseLeave={() => setShowCostTooltip(false)}
                >
                  Estimated ${agentEstimates.estimates.cost_per_minute.toFixed(3)}/min
                </span>
                {showCostTooltip && ReactDOM.createPortal(
                  <div style={{
                    position: 'absolute',
                    top: popupPos.top,
                    left: popupPos.left,
                    background: '#fff',
                    color: '#222',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                    padding: '12px 18px',
                    minWidth: 210,
                    zIndex: 2147483647,
                    fontSize: 14,
                    fontWeight: 400
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Cost per minute <span style={{ float: 'right' }}>${agentEstimates.breakdown.total.toFixed(3)}/min</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span>- Voice Engine: {agentEstimates.voice_provider === 'openai' ? 'OpenAI' : '11labs'}</span>
                      <span>${agentEstimates.breakdown.voice.toFixed(3)}/min</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span>- LLM: {agentEstimates.model}</span>
                      <span>${agentEstimates.breakdown.llm.toFixed(3)}/min</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span>- Knowledge Base</span>
                      <span>${agentEstimates.breakdown.kb.toFixed(3)}/min</span>
                    </div>
                  </div>, document.body
                )}
                {' • '}
                {agentEstimates.estimates.latency_ms.min}-{agentEstimates.estimates.latency_ms.max}ms latency • 
                {agentEstimates.estimates.tokens_per_minute.min}-{agentEstimates.estimates.tokens_per_minute.max} tokens
              </>
            ) : (
              <span>No estimates available</span>
            )}
          </span>
          {/* Refresh estimates button */}
          <button 
            onClick={() => {
              if (agent?.agent_id) {
                setEstimatesLoading(true);
                setEstimatesError(null);
                fetch(`${API_BASE_URL}/api/agents/${encodeURIComponent(agent.agent_id)}/estimates`)
                  .then(res => res.json())
                  .then(data => {
                    if (data.estimates) {
                      setAgentEstimates(data);
                    } else {
                      setEstimatesError('Invalid response format');
                    }
                  })
                  .catch(err => {
                    console.error('Error fetching fresh estimates:', err);
                    setEstimatesError('Failed to fetch estimates');
                  })
                  .finally(() => {
                    setEstimatesLoading(false);
                  });
              }
            }}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              color: '#64748b', 
              fontSize: 14,
              padding: '2px 4px',
              borderRadius: '4px',
              opacity: estimatesLoading ? 0.5 : 1
            }}
            disabled={estimatesLoading}
            title="Refresh estimates"
          >
            <i className={`fas fa-sync-alt ${estimatesLoading ? 'fa-spin' : ''}`}></i>
          </button>
          {/* Phone numbers display */}
          {agentPhoneNumbersLoading ? (
            <span style={{ fontSize: 14, color: '#64748b', marginLeft: 8 }}>Loading phone numbers...</span>
          ) : agentPhoneNumbersError ? (
            <span style={{ fontSize: 14, color: '#dc2626', marginLeft: 8 }}>Failed to load phone numbers</span>
          ) : agentPhoneNumbers.length > 0 ? (
            <span style={{ fontSize: 14, color: '#64748b', marginLeft: 8 }}>
              {agentPhoneNumbers.map((phone, index) => (
                <span key={`${phone.phone_number}-${phone.type}-${index}`}>
                  {index > 0 && ' • '}
                  {phone.display_text}: {phone.phone_number}
                </span>
              ))}
            </span>
          ) : null}
        </div>
        {/* Publish button removed as requested */}
      </div>
      {/* 3-column body with assistant panel */}
      <div style={{ 
        display: 'flex', 
        flex: 1, 
        minWidth: '1200px', 
        minHeight: 0, 
        background: '#f8fafc', 
        margin: 0, 
        padding: 0, 
        overflow: 'hidden',
        overflowY: 'hidden',
        overflowX: 'auto'
      }}>
        {/* Left column: agent info/controls and main content */}
        <div style={{ 
          flex: '0 0 50%', 
          minWidth: assistantPanelOpen ? '500px' : '600px', 
          background: '#fff', 
          borderRight: '1px solid #e5e7eb', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          padding: '24px 0 0 0', 
          height: 'calc(100vh - 120px)', 
          overflowY: 'auto', 
          transition: 'all 0.3s ease' 
        }}>
          {/* Example controls (avatar, model, language, etc.) */}
          <div style={{ padding: '0 24px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              {/* Removed agent icon here */}
              <span style={{ fontWeight: 600, fontSize: 18 }}>{agent?.agent_name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              {/* Model selector dropdown */}
              <div style={{ position: 'relative', zIndex: 20 }} ref={modelDropdownRef}>
                <button
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontWeight: 500, color: '#222', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 90 }}
                  onClick={() => setOpenIndex(openIndex === 'model' ? null : 'model')}
                  disabled={updatingModel}
                >
                  {currentModel ? (
                    <>
                      <i className={`${currentModel.provider === 'OpenAI' ? 'fab fa-openai' : currentModel.provider === 'Anthropic' ? 'ai ai-anthropic' : 'fas fa-robot'}`} 
                         style={{ color: currentModel.provider === 'OpenAI' ? '#6366f1' : currentModel.provider === 'Anthropic' ? '#f59e42' : '#64748b', fontSize: 18 }}></i>
                      {currentModel.name}
                    </>
                  ) : (
                    <>
                      <i className="fab fa-openai" style={{ color: '#6366f1', fontSize: 18 }}></i>
                      {modelsLoading ? 'Loading...' : 'Select Model'}
                    </>
                  )}
                  <i className={`fas fa-chevron-down ${updatingModel ? 'fa-spin' : ''}`} style={{ fontSize: 13, marginLeft: 6 }}></i>
                </button>
                {openIndex === 'model' && (
                  <div style={{ position: 'absolute', top: 38, left: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', minWidth: 280, maxHeight: 400, overflowY: 'auto', padding: '8px 0', zIndex: 100 }}>
                    {modelsLoading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                        Loading models...
                      </div>
                    ) : modelsError ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                        {modelsError}
                      </div>
                    ) : availableModels.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                        No models available
                      </div>
                    ) : (
                      availableModels.map((model) => (
                        <div 
                          key={model.id}
                          style={{ 
                            padding: '12px 18px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 10, 
                            cursor: 'pointer', 
                            fontWeight: 500, 
                            color: '#222',
                            background: currentModel?.id === model.id ? '#f0f9ff' : 'transparent',
                            borderLeft: currentModel?.id === model.id ? '3px solid #3b82f6' : '3px solid transparent'
                          }}
                          onClick={() => handleModelSelect(model)}
                        >
                          <i className={`${model.provider === 'OpenAI' ? 'fab fa-openai' : model.provider === 'Anthropic' ? 'ai ai-anthropic' : 'fas fa-robot'}`} 
                             style={{ color: model.provider === 'OpenAI' ? '#6366f1' : model.provider === 'Anthropic' ? '#f59e42' : '#64748b', fontSize: 18 }}></i>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{model.name}</div>
                            {model.features.length > 0 && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                                {model.features.map((feature, idx) => (
                                  <span key={idx} style={{ 
                                    background: '#f1f5f9', 
                                    color: '#64748b', 
                                    borderRadius: 4, 
                                    padding: '1px 6px', 
                                    fontSize: 11, 
                                    fontWeight: 500 
                                  }}>
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {currentModel?.id === model.id && (
                            <i className="fas fa-check" style={{ color: '#3b82f6', fontSize: 14, marginLeft: 8 }}></i>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {/* Voice selector dropdown */}
              <div style={{ position: 'relative', zIndex: 20 }} ref={voiceDropdownRef}>
                <button
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontWeight: 500, color: '#222', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 110 }}
                  onClick={() => setOpenIndex(openIndex === 'voice' ? null : 'voice')}
                  disabled={updatingVoice}
                >
                  {currentVoice ? (
                    <>
                      <i className="fas fa-microphone" style={{ color: '#6366f1', fontSize: 18 }}></i>
                      {currentVoice.name}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-microphone" style={{ color: '#6366f1', fontSize: 18 }}></i>
                      {voicesLoading ? 'Loading...' : 'Select Voice'}
                    </>
                  )}
                  <i className={`fas fa-chevron-down ${updatingVoice ? 'fa-spin' : ''}`} style={{ fontSize: 13, marginLeft: 6 }}></i>
                </button>
                {openIndex === 'voice' && (
                  <div style={{ position: 'absolute', top: 38, left: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', minWidth: 280, maxHeight: 400, overflowY: 'auto', padding: '8px 0', zIndex: 100 }}>
                    {voicesLoading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                        Loading voices...
                      </div>
                    ) : voicesError ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                        {voicesError}
                      </div>
                    ) : availableVoices.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                        No voices available
                      </div>
                    ) : (
                      availableVoices.map((voice) => (
                        <div 
                          key={voice.id}
                          style={{ 
                            padding: '12px 18px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 10, 
                            cursor: 'pointer', 
                            fontWeight: 500, 
                            color: '#222',
                            background: currentVoice?.id === voice.id ? '#f0f9ff' : 'transparent',
                            borderLeft: currentVoice?.id === voice.id ? '3px solid #3b82f6' : '3px solid transparent'
                          }}
                          onClick={() => handleVoiceSelect(voice)}
                        >
                          <i className="fas fa-microphone" style={{ color: '#6366f1', fontSize: 18 }}></i>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{voice.name}</div>
                            <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                              {voice.accent && (
                                <span style={{ 
                                  background: '#f1f5f9', 
                                  color: '#64748b', 
                                  borderRadius: 4, 
                                  padding: '1px 6px', 
                                  fontSize: 11, 
                                  fontWeight: 500 
                                }}>
                                  {voice.accent}
                                </span>
                              )}
                              {voice.gender && (
                                <span style={{ 
                                  background: '#f1f5f9', 
                                  color: '#64748b', 
                                  borderRadius: 4, 
                                  padding: '1px 6px', 
                                  fontSize: 11, 
                                  fontWeight: 500 
                                }}>
                                  {voice.gender}
                                </span>
                              )}
                              {voice.age && (
                                <span style={{ 
                                  background: '#f1f5f9', 
                                  color: '#64748b', 
                                  borderRadius: 4, 
                                  padding: '1px 6px', 
                                  fontSize: 11, 
                                  fontWeight: 500 
                                }}>
                                  {voice.age}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ color: '#64748b', fontSize: 12, fontWeight: 400 }}>
                            {voice.provider}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {/* Language selector dropdown */}
              <div style={{ position: 'relative', zIndex: 20 }} ref={languageDropdownRef}>
                <button
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontWeight: 500, color: '#222', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minWidth: 90 }}
                  onClick={() => setOpenIndex(openIndex === 'language' ? null : 'language')}
                  disabled={updatingLanguage}
                >
                  {currentLanguage ? (
                    <>
                      <i className="fas fa-globe" style={{ color: '#6366f1', fontSize: 18 }}></i>
                      {currentLanguage.name}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-globe" style={{ color: '#6366f1', fontSize: 18 }}></i>
                      {languagesLoading ? 'Loading...' : 'Select Language'}
                    </>
                  )}
                  <i className={`fas fa-chevron-down ${updatingLanguage ? 'fa-spin' : ''}`} style={{ fontSize: 13, marginLeft: 6 }}></i>
                </button>
                {openIndex === 'language' && (
                  <div style={{ position: 'absolute', top: 38, left: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', minWidth: 280, maxHeight: 400, overflowY: 'auto', padding: '8px 0', zIndex: 100 }}>
                    {languagesLoading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                        Loading languages...
                      </div>
                    ) : languagesError ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                        {languagesError}
                      </div>
                    ) : availableLanguages.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                        No languages available
                      </div>
                    ) : (
                      availableLanguages.map((language) => (
                        <div 
                          key={language.code}
                          style={{ 
                            padding: '12px 18px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 10, 
                            cursor: 'pointer', 
                            fontWeight: 500, 
                            color: '#222',
                            background: currentLanguage?.code === language.code ? '#f0f9ff' : 'transparent',
                            borderLeft: currentLanguage?.code === language.code ? '3px solid #3b82f6' : '3px solid transparent'
                          }}
                          onClick={() => handleLanguageSelect(language)}
                        >
                          <i className="fas fa-globe" style={{ color: '#6366f1', fontSize: 18 }}></i>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{language.name}</div>
                            {language.region && (
                              <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                                {language.region}
                              </div>
                            )}
                          </div>
                          {language.description && (
                            <div style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic', maxWidth: 120, textAlign: 'right' }}>
                              {language.description}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Remove prompt textarea, add Form and Transcription accordions */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: '#fff', border: '1px solid #e5e7eb' }}>
                <div
                  style={{
                    padding: '14px 18px',
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#222',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => setOpenIndex(openIndex === 'transcription' ? null : 'transcription')}
                >
                  Transcription
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Refresh button */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (agent?.agent_id && userEmail) {
                          setTranscriptsLoading(true);
                          setTranscriptsError(null);
                          
                          // Fetch fresh data from user-calls endpoint
                          fetch(`${API_BASE_URL}/api/user-calls?user_email=${encodeURIComponent(userEmail)}&agent_id=${encodeURIComponent(agent.agent_id)}`)
                            .then(res => res.json())
                            .then(data => {
                              if (Array.isArray(data)) {
                                setTranscripts(data);
                              } else {
                                setTranscriptsError('Invalid response format');
                              }
                            })
                            .catch(err => {
                              console.error('Error fetching fresh transcripts:', err);
                              setTranscriptsError('Failed to fetch transcripts');
                            })
                            .finally(() => {
                              setTranscriptsLoading(false);
                            });
                        }
                      }}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer', 
                        color: '#64748b', 
                        fontSize: 14,
                        padding: '2px 4px',
                        borderRadius: '4px'
                      }}
                      title="Refresh transcripts from Retell"
                    >
                      <i className="fas fa-sync-alt"></i>
                    </button>
                    <span style={{ fontSize: 18, color: '#64748b', marginLeft: 8 }}>
                      <i className={`fas fa-chevron-${openIndex === 'transcription' ? 'up' : 'down'}`}></i>
                    </span>
                  </div>
                </div>
                {openIndex === 'transcription' && (
                  <div style={{ padding: '14px 18px', color: '#64748b', fontSize: 15, borderTop: '1px solid #e5e7eb', background: '#f8fafc', maxHeight: 320, overflowY: 'auto' }}>
                    {/* List of fake call transcriptions as inner accordions */}
                    {transcriptsLoading ? (
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>Loading transcripts...</div>
                    ) : transcriptsError ? (
                      <div style={{ textAlign: 'center', padding: '20px 0', color: 'red' }}>{transcriptsError}</div>
                    ) : transcripts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>No transcripts available for this agent.</div>
                    ) : (
                      transcripts.map((call, idx) => (
                        <div key={call.call_id} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 8, padding: '10px 16px', color: '#222', fontSize: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.03)', cursor: 'pointer' }} onClick={() => setOpenTranscription(openTranscription === call.call_id ? null : call.call_id)}>
                                                         <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                               {/* Arrow icon for expand/collapse */}
                               <span style={{ display: 'flex', alignItems: 'center', transition: 'transform 0.2s', transform: openTranscription === call.call_id ? 'rotate(90deg)' : 'rotate(0deg)', color: '#64748b', fontSize: 18, marginRight: 8 }}>
                                 <i className="fas fa-chevron-right"></i>
                               </span>
                               <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                 {/* Removed call_id display here */}
                                 <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#64748b' }}>
                                   <span>{formatDate(call.start_timestamp)}</span>
                                   <span>•</span>
                                   <span>{formatDuration(call.duration_ms)}</span>
                                   <span>•</span>
                                   <span style={{ 
                                     color: call.call_status === 'completed' ? '#059669' : 
                                            call.call_status === 'failed' ? '#dc2626' : '#64748b',
                                     fontWeight: 500
                                   }}>
                                     {call.call_status || 'unknown'}
                                   </span>
                                 </div>
                               </div>
                             </div>
                            <button onClick={e => { e.stopPropagation(); handleDownloadPDF(call); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 20, marginLeft: 12 }} title="Download PDF">
                              <i className="fas fa-download"></i>
                            </button>
                          </div>
                                                     {openTranscription === call.call_id && (
                             <div style={{ background: '#f8fafc', borderRadius: 8, marginTop: 0, padding: '14px 18px', color: '#222', fontSize: 15, border: '1px solid #e5e7eb', borderTop: 'none' }}>
                               {/* Call details */}
                               <div style={{ marginBottom: 12, padding: '8px 0', borderBottom: '1px solid #e5e7eb' }}>
                                 <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#64748b' }}>
                                   <span><b>Duration:</b> {formatDuration(call.duration_ms)}</span>
                                   <span><b>Status:</b> {call.call_status}</span>
                                   {call.disconnection_reason && <span><b>End Reason:</b> {call.disconnection_reason}</span>}
                                 </div>
                               </div>
                               {/* Transcript content */}
                               <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                 {call.transcript ? (
                                   call.transcript
                                 ) : (
                                   <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No transcript available for this call</div>
                                 )}
                               </div>
                             </div>
                           )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: '#fff', border: '1px solid #e5e7eb', marginTop: 12 }}>
                <div
                  style={{
                    padding: '14px 18px',
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#222',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => setOpenIndex(openIndex === 'callHistory' ? null : 'callHistory')}
                >
                  Call History
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Refresh button */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (userEmail && agent?.agent_id) {
                          setCallHistoryLoading(true);
                          setCallHistoryError(null);
                          
                          // Fetch fresh data from cached-calls endpoint with both user_email and agent_id
                          fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(userEmail)}&agent_id=${encodeURIComponent(agent.agent_id)}`)
                            .then(res => res.json())
                            .then(data => {
                              if (Array.isArray(data)) {
                                setCallHistory(data);
                              } else {
                                setCallHistoryError('Invalid response format');
                              }
                            })
                            .catch(err => {
                              console.error('Error fetching fresh call history:', err);
                              setCallHistoryError('Failed to fetch call history');
                            })
                            .finally(() => {
                              setCallHistoryLoading(false);
                            });
                        }
                      }}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer', 
                        color: '#64748b', 
                        fontSize: 14,
                        padding: '2px 4px',
                        borderRadius: '4px'
                      }}
                      title="Refresh call history"
                    >
                      <i className="fas fa-sync-alt"></i>
                    </button>
                    <span style={{ fontSize: 18, color: '#64748b', marginLeft: 8 }}>
                      <i className={`fas fa-chevron-${openIndex === 'callHistory' ? 'up' : 'down'}`}></i>
                    </span>
                  </div>
                </div>
                {openIndex === 'callHistory' && (
                  <div style={{ padding: '0', color: '#64748b', fontSize: 15, borderTop: '1px solid #e5e7eb', background: '#f8fafc', maxHeight: 400, overflowY: 'auto' }}>
                    <div style={{ padding: 18 }}>
                      {callHistoryLoading ? (
                        <span style={{ color: '#aaa' }}>Loading call history...</span>
                      ) : callHistoryError ? (
                        <span style={{ color: 'red' }}>{callHistoryError}</span>
                      ) : callHistory.length === 0 ? (
                        <span>No call history found.</span>
                      ) : (
                        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f3f4f6', color: '#374151', fontWeight: 600 }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Date/Time</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Agent ID</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Duration</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Channel</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>End Reason</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Call ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {callHistory.map(call => (
                              <tr key={call.call_id} style={{ borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
                                <td style={{ padding: '6px 8px' }}>{formatDate(call.start_timestamp)}</td>
                                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{call.agent_id}</td>
                                <td style={{ padding: '6px 8px' }}>{formatDuration(call.duration_ms)}</td>
                                <td style={{ padding: '6px 8px' }}>{call.call_status}</td>
                                <td style={{ padding: '6px 8px' }}>{call.channel_type || '-'}</td>
                                <td style={{ padding: '6px 8px' }}>{call.disconnection_reason || '-'}</td>
                                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{call.call_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Multi-Prompt Backend Main Editor */}
            <div style={{ marginTop: 12, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: '#fff', fontSize: 15, color: '#222', border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '18px 24px', cursor: 'pointer', borderRadius: 10, fontWeight: 700, fontSize: 18, color: '#222', justifyContent: 'space-between', userSelect: 'none',
                }}
                onClick={() => setOpenMainSection(openMainSection === 'multiPrompt' ? null : 'multiPrompt')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <i className="fas fa-sitemap" style={{ color: '#4f46e5', fontSize: 24 }}></i>
                  <span>Multi-Prompt Backend</span>
                  {/* Assistant Button - COMMENTED OUT */}
                  {false && (
                    <div 
                      style={{ 
                        position: 'relative',
                        marginLeft: 8
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent toggling the section
                        setAssistantPanelOpen(!assistantPanelOpen);
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('#assistant-tooltip');
                        if (tooltip) tooltip.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('#assistant-tooltip');
                        if (tooltip) tooltip.style.opacity = '0';
                      }}
                    >
                      <button
                        style={{
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#5568d3';
                          e.target.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#667eea';
                          e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                        }}
                      >
                        <i className="fas fa-robot" style={{ fontSize: '12px' }}></i>
                        <span>Assistant</span>
                      </button>
                      {/* Tooltip */}
                      <div
                        id="assistant-tooltip"
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginBottom: '8px',
                          background: '#1f2937',
                          color: 'white',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 0.2s ease',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        For any issues let assistant help you
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #1f2937'
                      }}></div>
                    </div>
                  </div>
                  )}
                </div>
                <span style={{ fontSize: 18, color: '#64748b', marginLeft: 8, transition: 'transform 0.2s', transform: openMainSection === 'multiPrompt' ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <i className={`fas fa-chevron-down`}></i>
                </span>
              </div>
              {openMainSection === 'multiPrompt' && (
                <div style={{ padding: 20, borderTop: '1px solid #e5e7eb' }}>
                  {/* Multi-Prompt Backend Editor Content (move all previous content here) */}
                  {multiPromptLoading ? (
                    <div style={{ color: '#64748b', textAlign: 'center', padding: '40px 0' }}>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>
                      Loading multi-prompt data...
                    </div>
                  ) : multiPromptError ? (
                    <div style={{ color: '#ef4444', textAlign: 'center', padding: '40px 0' }}>
                      <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                      {multiPromptError}
                    </div>
                  ) : (
                    <>
                      {/* General Prompt */}
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontWeight: 600, color: '#222', marginBottom: 8, fontSize: 16 }}>General Prompt</div>
                        <textarea
                          value={multiPromptData.general_prompt}
                          onChange={(e) => setMultiPromptData(prev => ({ ...prev, general_prompt: e.target.value }))}
                          placeholder="Enter the general prompt that will be used across all states..."
                          style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 15, minHeight: 500, resize: 'vertical' }}
                        />
                        {/* Save Button */}
                        <button
                          onClick={handleSaveGeneralPrompt}
                          disabled={updatingMultiPrompt}
                          style={{
                            marginTop: 12,
                            background: updatingMultiPrompt ? '#9ca3af' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            padding: '10px 20px',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: updatingMultiPrompt ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!updatingMultiPrompt) {
                              e.target.style.background = '#059669';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!updatingMultiPrompt) {
                              e.target.style.background = '#10b981';
                            }
                          }}
                        >
                          {updatingMultiPrompt ? (
                            <>
                              <i className="fas fa-spinner fa-spin" style={{ fontSize: '12px' }}></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save" style={{ fontSize: '12px' }}></i>
                              Save
                            </>
                          )}
                        </button>
                      </div>
                      {/* Starting State */}
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontWeight: 600, color: '#222', marginBottom: 8, fontSize: 16 }}>Starting State</div>
                        <input
                          type="text"
                          value={multiPromptData.starting_state}
                          onChange={(e) => setMultiPromptData(prev => ({ ...prev, starting_state: e.target.value }))}
                          placeholder="e.g., intro, welcome, main_menu"
                          style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 15 }}
                        />
                      </div>
                      {/* General Tools */}
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontWeight: 600, color: '#222', marginBottom: 12, fontSize: 16 }}>General Tools (Available in All States)</div>
                        {multiPromptData.general_tools.map((tool, idx) => (
                          <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 12, background: '#f9fafb' }}>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                              {tool.type === 'end_call' ? (
                                <div style={{
                                  minWidth: 140,
                                  padding: '8px 12px',
                                  borderRadius: 6,
                                  border: '1px solid #e5e7eb',
                                  background: '#f9fafb',
                                  color: '#222',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  End Call
                                </div>
                              ) : (
                                <select 
                                  value={tool.type} 
                                  onChange={(e) => handleUpdateGeneralTool(idx, 'type', e.target.value)}
                                  style={{ minWidth: 140, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                                >
                                  <option value="transfer_call">Transfer Call</option>
                                  <option value="agent_transfer">Agent Transfer</option>
                                  <option value="check_calendar">Check Calendar</option>
                                  <option value="book_calendar">Book Calendar</option>
                                  <option value="press_digit">Press Digit</option>
                                  <option value="send_sms">Send SMS</option>
                                  <option value="extract_variable">Extract Variable</option>
                                  <option value="custom">Custom Tool</option>
                                </select>
                              )}
                              <input 
                                value={tool.name} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'name', e.target.value)}
                                placeholder="Tool name"
                                style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              />
                              <button 
                                onClick={() => handleRemoveGeneralTool(idx)}
                                style={{ color: '#ef4444', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', padding: '8px 12px' }}
                              >
                                Remove
                              </button>
                            </div>
                            <textarea 
                              value={tool.description} 
                              onChange={(e) => handleUpdateGeneralTool(idx, 'description', e.target.value)}
                              placeholder="Tool description"
                              style={{ width: '100%', marginBottom: 8, minHeight: 60, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb', resize: 'vertical' }}
                            />
                            
                            {/* Tool-specific fields */}
                            {tool.type === 'transfer_call' && (
                              <>
                                <input 
                                  value={tool.destination_number || tool.transfer_option?.destination_number || ''} 
                                  onChange={(e) => handleUpdateGeneralTool(idx, 'destination_number', e.target.value)}
                                  placeholder="Transfer destination number"
                                  style={{ width: '100%', marginBottom: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                                />
                                {/* Display the transfer phone number if configured */}
                                {(tool.destination_number || tool.transfer_option?.destination_number) && (
                                  <div style={{
                                    fontSize: 12,
                                    color: '#6b7280',
                                    marginTop: -4,
                                    marginBottom: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6
                                  }}>
                                    <i className="fas fa-phone" style={{ fontSize: 10 }}></i>
                                    <span>Transfer to: {tool.destination_number || tool.transfer_option?.destination_number}</span>
                                  </div>
                                )}
                              </>
                            )}
                            {(tool.type === 'check_calendar' || tool.type === 'book_calendar') && (
                              <input 
                                value={tool.cal_api_key || ''} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'cal_api_key', e.target.value)}
                                placeholder="Cal.com API Key"
                                style={{ width: '100%', marginBottom: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              />
                            )}
                            {tool.type === 'press_digit' && (
                              <input 
                                value={tool.digit || ''} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'digit', e.target.value)}
                                placeholder="Digit to press (0-9, *, #)"
                                style={{ width: '100%', marginBottom: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              />
                            )}
                            {tool.type === 'send_sms' && (
                              <textarea 
                                value={tool.sms_content || ''} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'sms_content', e.target.value)}
                                placeholder="SMS content"
                                style={{ width: '100%', marginBottom: 8, minHeight: 40, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb', resize: 'vertical' }}
                              />
                            )}
                            {tool.type === 'agent_transfer' && (
                              <input 
                                value={tool.agent_id || ''} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'agent_id', e.target.value)}
                                placeholder="Target Agent ID"
                                style={{ width: '100%', marginBottom: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              />
                            )}
                            {tool.type === 'extract_variable' && (
                              <input 
                                value={Array.isArray(tool.variables) ? tool.variables.join(', ') : ''} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'variables', e.target.value.split(',').map(v => v.trim()))}
                                placeholder="Variables to extract (comma separated)"
                                style={{ width: '100%', marginBottom: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              />
                            )}
                            {tool.type === 'custom' && (
                              <input 
                                value={tool.url || ''} 
                                onChange={(e) => handleUpdateGeneralTool(idx, 'url', e.target.value)}
                                placeholder="Tool URL"
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb' }}
                              />
                            )}
                          </div>
                        ))}
                        
                        {/* Add General Tool Dropdown */}
                        <div style={{ position: 'relative' }}>
                          <button 
                            onClick={() => setOpenIndex(openIndex === 'general-tool-dropdown' ? null : 'general-tool-dropdown')}
                            style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 16px', color: '#222', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            + Add General Tool
                            <i className={`fas fa-chevron-${openIndex === 'general-tool-dropdown' ? 'up' : 'down'}`} style={{ fontSize: 12 }}></i>
                          </button>
                          
                          {openIndex === 'general-tool-dropdown' && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              zIndex: 1000,
                              marginTop: 4
                            }}>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('end_call'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>End Call</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('transfer_call'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Transfer Call</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('agent_transfer'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Agent Transfer</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('check_calendar'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Check Calendar</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('book_calendar'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Book Calendar</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('press_digit'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Press Digit</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('send_sms'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Send SMS</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('extract_variable'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Extract Variable</span>
                              </div>
                              <div 
                                onClick={() => { handleAddGeneralToolWithType('custom'); setOpenIndex(null); }}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = '#fff'}
                              >
                                <span style={{ fontSize: 14, color: '#222' }}>Custom Tool</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* States */}
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontWeight: 600, color: '#222', marginBottom: 12, fontSize: 16 }}>States</div>
                        {multiPromptData.states.map((state, stateIdx) => (
                          <div key={stateIdx} style={{ border: '2px solid #4f46e5', borderRadius: 12, padding: 20, marginBottom: 20, background: '#f9fafb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e5e7eb' }}>
                              <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: 16 }}>State: {state.name}</div>
                              <button 
                                onClick={() => handleRemoveState(stateIdx)}
                                style={{ color: '#ef4444', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', padding: '6px 12px' }}
                              >
                                Remove State
                              </button>
                            </div>
                            {/* State Name */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontWeight: 600, color: '#222', marginBottom: 6 }}>State Name</div>
                              <input
                                type="text"
                                value={state.name}
                                onChange={(e) => handleUpdateState(stateIdx, 'name', e.target.value)}
                                placeholder="e.g., intro, menu, checkout"
                                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                              />
                            </div>
                            {/* State Prompt */}
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ fontWeight: 600, color: '#222', marginBottom: 6 }}>State Prompt</div>
                              <textarea
                                value={state.state_prompt}
                                onChange={(e) => handleUpdateState(stateIdx, 'state_prompt', e.target.value)}
                                placeholder="Enter the prompt for this state..."
                                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', minHeight: 100, resize: 'vertical' }}
                              />
                            </div>
                            {/* Edges */}
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ fontWeight: 600, color: '#10b981', marginBottom: 10, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className="fas fa-arrow-right"></i>
                                <span>Edges (Transitions)</span>
                              </div>
                              {state.edges.map((edge, edgeIdx) => (
                                <div key={edgeIdx} style={{ border: '1px solid #10b981', borderRadius: 8, padding: 12, marginBottom: 10, background: '#f0fdf4', marginLeft: 20 }}>
                                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                    <input 
                                      value={edge.destination_state_name} 
                                      onChange={(e) => handleUpdateEdge(stateIdx, edgeIdx, 'destination_state_name', e.target.value)}
                                      placeholder="Destination state name"
                                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #10b981' }}
                                    />
                                    <button 
                                      onClick={() => handleRemoveEdge(stateIdx, edgeIdx)}
                                      style={{ color: '#ef4444', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', padding: '6px 10px' }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <textarea 
                                    value={edge.description} 
                                    onChange={(e) => handleUpdateEdge(stateIdx, edgeIdx, 'description', e.target.value)}
                                    placeholder="Edge description"
                                    style={{ width: '100%', minHeight: 50, padding: '6px 10px', borderRadius: 6, border: '1px solid #10b981', resize: 'vertical' }}
                                  />
                                </div>
                              ))}
                              <button 
                                onClick={() => handleAddEdge(stateIdx)}
                                style={{ background: '#f0fdf4', border: '1px solid #10b981', borderRadius: 8, padding: '8px 16px', color: '#10b981', cursor: 'pointer', marginLeft: 20, fontWeight: 500 }}
                              >
                                + Add Edge
                              </button>
                            </div>
                            {/* Tools */}
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ fontWeight: 600, color: '#f59e0b', marginBottom: 10, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className="fas fa-tools"></i>
                                <span>Tools for this State</span>
                              </div>
                              {state.tools.map((tool, toolIdx) => (
                                <div key={toolIdx} style={{ border: '1px solid #f59e0b', borderRadius: 8, padding: 12, marginBottom: 10, background: '#fffbeb', marginLeft: 20 }}>
                                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                    {tool.type === 'end_call' ? (
                                      <div style={{
                                        minWidth: 140,
                                        padding: '6px 10px',
                                        borderRadius: 6,
                                        border: '1px solid #f59e0b',
                                        background: '#fffbeb',
                                        color: '#222',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}>
                                        End Call
                                      </div>
                                    ) : (
                                      <select 
                                        value={tool.type} 
                                        onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'type', e.target.value)}
                                        style={{ minWidth: 140, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                      >
                                        <option value="transfer_call">Transfer Call</option>
                                        <option value="agent_transfer">Agent Transfer</option>
                                        <option value="check_calendar">Check Calendar</option>
                                        <option value="book_calendar">Book Calendar</option>
                                        <option value="press_digit">Press Digit</option>
                                        <option value="send_sms">Send SMS</option>
                                        <option value="extract_variable">Extract Variable</option>
                                        <option value="custom">Custom Tool</option>
                                      </select>
                                    )}
                                    <input 
                                      value={tool.name} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'name', e.target.value)}
                                      placeholder="Tool name"
                                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                    <button 
                                      onClick={() => handleRemoveTool(stateIdx, toolIdx)}
                                      style={{ color: '#ef4444', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', padding: '6px 10px' }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <textarea 
                                    value={tool.description} 
                                    onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'description', e.target.value)}
                                    placeholder="Tool description"
                                    style={{ width: '100%', marginBottom: 8, minHeight: 50, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b', resize: 'vertical' }}
                                  />
                                  
                                  {/* Tool-specific fields */}
                                  {tool.type === 'transfer_call' && (
                                    <input 
                                      value={tool.transfer_option?.destination_number || ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'transfer_option', { destination_number: e.target.value })}
                                      placeholder="Transfer destination number"
                                      style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                  )}
                                  {(tool.type === 'check_calendar' || tool.type === 'book_calendar') && (
                                    <input 
                                      value={tool.cal_api_key || ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'cal_api_key', e.target.value)}
                                      placeholder="Cal.com API Key"
                                      style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                  )}
                                  {tool.type === 'press_digit' && (
                                    <input 
                                      value={tool.digit || ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'digit', e.target.value)}
                                      placeholder="Digit to press (0-9, *, #)"
                                      style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                  )}
                                  {tool.type === 'send_sms' && (
                                    <textarea 
                                      value={tool.sms_content || ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'sms_content', e.target.value)}
                                      placeholder="SMS content"
                                      style={{ width: '100%', marginBottom: 8, minHeight: 40, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b', resize: 'vertical' }}
                                    />
                                  )}
                                  {tool.type === 'agent_transfer' && (
                                    <input 
                                      value={tool.agent_id || ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'agent_id', e.target.value)}
                                      placeholder="Target Agent ID"
                                      style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                  )}
                                  {tool.type === 'extract_variable' && (
                                    <input 
                                      value={Array.isArray(tool.variables) ? tool.variables.join(', ') : ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'variables', e.target.value.split(',').map(v => v.trim()))}
                                      placeholder="Variables to extract (comma separated)"
                                      style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                  )}
                                  {tool.type === 'custom' && (
                                    <input 
                                      value={tool.url || ''} 
                                      onChange={(e) => handleUpdateTool(stateIdx, toolIdx, 'url', e.target.value)}
                                      placeholder="Tool URL"
                                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #f59e0b' }}
                                    />
                                  )}
                                </div>
                              ))}
                              
                              {/* Add Tool Dropdown - Matches Functions dropdown exactly */}
                              <div 
                                ref={(el) => { toolDropdownRefs.current[stateIdx] = el; }}
                                style={{ marginLeft: 20, position: 'relative' }}
                              >
                                <button 
                                  onClick={() => setOpenIndex(openIndex === `tool-dropdown-${stateIdx}` ? null : `tool-dropdown-${stateIdx}`)}
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 8, 
                                    background: '#fff', 
                                    color: '#222', 
                                    border: '1px solid #e5e7eb', 
                                    borderRadius: 12, 
                                    padding: '14px 32px', 
                                    fontWeight: 600, 
                                    fontSize: 16, 
                                    cursor: 'pointer', 
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                  onMouseLeave={(e) => e.target.style.background = '#fff'}
                                >
                                  <i className="fas fa-plus" style={{ fontSize: 18, color: '#64748b' }}></i> 
                                  Add Tool
                                  <i className={`fas fa-chevron-${openIndex === `tool-dropdown-${stateIdx}` ? 'up' : 'down'}`} style={{ fontSize: 14, color: '#64748b', marginLeft: 8 }}></i>
                                </button>
                                
                                {openIndex === `tool-dropdown-${stateIdx}` && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    zIndex: 1000,
                                    marginTop: 4
                                  }}>
                                    {sharedDropdownOptions.map((option, index) => (
                                      <div 
                                        key={option.value}
                                        onClick={() => { 
                                          handleUnifiedItemClick(option.value, 'tool', stateIdx); 
                                          setOpenIndex(null); 
                                        }}
                                        style={{
                                          padding: '12px 16px',
                                          cursor: 'pointer',
                                          borderBottom: index < sharedDropdownOptions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                          transition: 'background 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.target.style.background = '#fff'}
                                      >
                                        <span style={{ fontSize: 14, color: '#222' }}>{option.label}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        <button 
                          onClick={handleAddState}
                          style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 20px', color: '#222', cursor: 'pointer', fontWeight: 500 }}
                        >
                          + Add State
                        </button>
                      </div>
                      {/* Save Button */}
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 20 }}>
                        <button
                          onClick={() => handleMultiPromptUpdate(multiPromptData)}
                          disabled={!agent?.agent_id || !userEmail || updatingMultiPrompt}
                          style={{ 
                            background: updatingMultiPrompt ? '#9ca3af' : '#4f46e5', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 8, 
                            padding: '12px 24px', 
                            fontWeight: 600, 
                            fontSize: 16,
                            cursor: updatingMultiPrompt ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          {updatingMultiPrompt ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save"></i>
                              Save Multi-Prompt Configuration
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Center column: main editor (now empty) */}
        <div style={{ 
          flex: assistantPanelOpen ? '0 0 12.5%' : '0 0 25%', 
          minWidth: assistantPanelOpen ? '150px' : '300px', 
          padding: assistantPanelOpen ? '16px 8px' : '32px 16px', 
          background: '#fff', 
          height: 'calc(100vh - 120px)', 
          overflowY: 'auto', 
          display: 'flex', 
          flexDirection: 'column', 
          borderRight: '1px solid #e5e7eb', 
          transition: 'all 0.3s ease',
          ...(assistantPanelOpen && {
            background: '#f8f9fa',
            borderRight: '1px solid #e1e5e9'
          })
        }}>
          {accordionItems
            .filter(item =>
              isAdmin ||
              !['Security & Fallback Settings', 'Webhook Settings'].includes(item)
            )
            .map((item, idx, arr) => (
              <React.Fragment key={item}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  background: assistantPanelOpen ? '#f1f3f4' : '#fff', 
                  borderRadius: assistantPanelOpen ? 6 : 10, 
                  fontWeight: 600, 
                  fontSize: assistantPanelOpen ? 14 : 17, 
                  color: '#222', 
                  cursor: 'pointer', 
                  padding: '0 0', 
                  marginBottom: assistantPanelOpen ? 4 : 0,
                  transition: 'all 0.3s ease'
                }} onClick={() => setOpenIndex(openIndex === idx ? null : idx)}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: assistantPanelOpen ? 8 : 16, 
                    padding: assistantPanelOpen ? '12px 16px' : '18px 24px', 
                    flex: 1 
                  }}>
                    <i className={accordionIcons[item]} style={{ 
                      color: '#64748b', 
                      fontSize: assistantPanelOpen ? 16 : 20, 
                      minWidth: assistantPanelOpen ? 18 : 22 
                    }}></i>
                    <span style={{ 
                      fontWeight: 700, 
                      fontSize: assistantPanelOpen ? 14 : 17,
                      ...(assistantPanelOpen && {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      })
                    }}>{item}</span>
                  </div>
                  <span style={{ 
                    fontSize: assistantPanelOpen ? 14 : 18, 
                    color: '#64748b', 
                    marginRight: assistantPanelOpen ? 12 : 24, 
                    transition: 'transform 0.2s', 
                    transform: openIndex === idx ? 'rotate(180deg)' : 'rotate(0deg)' 
                  }}>
                    <i className={`fas fa-chevron-down`}></i>
                  </span>
                </div>
                {openIndex === idx && (
                  <div style={{ 
                    padding: assistantPanelOpen ? '12px 16px' : '18px 24px', 
                    color: '#64748b', 
                    fontSize: assistantPanelOpen ? 13 : 15, 
                    borderTop: '1px solid #e5e7eb', 
                    background: assistantPanelOpen ? '#f8f9fa' : '#f8fafc',
                    ...(assistantPanelOpen && {
                      maxHeight: '200px',
                      overflowY: 'auto'
                    })
                  }}>
                    {/* Custom content for Functions */}
                    {item === 'Functions' && (
                      <>
                        
                        
                        {/* Add Function Button with Dropdown */}
                        <div ref={functionDropdownRef} style={{ position: 'relative', marginTop: 16 }}>
                          <button 
                            onClick={() => setFunctionDropdownOpen(!functionDropdownOpen)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 8, 
                              background: '#fff', 
                              color: '#222', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: 12, 
                              padding: '14px 32px', 
                              fontWeight: 600, 
                              fontSize: 16, 
                              cursor: 'pointer', 
                              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.background = '#fff'}
                          >
                            <i className="fas fa-plus" style={{ fontSize: 18, color: '#64748b' }}></i> 
                            Add Function
                            <i className={`fas fa-chevron-${functionDropdownOpen ? 'up' : 'down'}`} style={{ fontSize: 14, color: '#64748b', marginLeft: 8 }}></i>
                          </button>
                          
                          {functionDropdownOpen && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              zIndex: 1000,
                              marginTop: 4
                            }}>
                              {sharedDropdownOptions.map((option, index) => (
                                <div 
                                  key={option.value}
                                  onClick={() => {
                                    handleUnifiedItemClick(option.value, 'function');
                                    setFunctionDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    borderBottom: index < sharedDropdownOptions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                    transition: 'background 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                  onMouseLeave={(e) => e.target.style.background = '#fff'}
                                >
                                  <span style={{ fontSize: 14, color: '#222' }}>{option.label}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Display current functions */}
                        {multiPromptData.general_tools.length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <div style={{ color: '#222', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Current Functions:</div>
                            {multiPromptData.general_tools.map((tool, index) => (
                              <div key={index} style={{ 
                                background: '#ffffff', 
                                borderRadius: 8, 
                                padding: '16px 20px', 
                                border: '1px solid #e5e7eb', 
                                marginBottom: 12,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                                e.target.style.borderColor = '#d1d5db';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                e.target.style.borderColor = '#e5e7eb';
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                  {/* Function Icon */}
                                  <div style={{
                                    width: 24,
                                    height: 24,
                                    background: '#f3f4f6',
                                    borderRadius: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: 12,
                                    color: '#6b7280'
                                  }}>
                                    <i className="fas fa-cog" style={{ fontSize: 12 }}></i>
                                  </div>
                                  
                                  {/* Function Details */}
                                  <div style={{ flex: 1 }}>
                                    <div style={{ 
                                      fontWeight: 500, 
                                      color: '#374151', 
                                      fontSize: 14
                                    }}>
                                      {tool.name === 'end_call' ? 'End Call' :
                                       tool.name === 'transfer_call' || tool.type === 'transfer_call' ? 'Call Transfer' :
                                       tool.name === 'agent_transfer' ? 'Agent Transfer' :
                                       tool.name === 'check_calendar' ? 'Check Calendar Availability (Cal.com)' :
                                       tool.name === 'book_calendar' ? 'Book on the Calendar (Cal.com)' :
                                       tool.name === 'press_digit' ? 'Press Digit (IVR Navigation)' :
                                       tool.name === 'send_sms' ? 'Send SMS' :
                                       tool.name === 'extract_variable' ? 'Extract Dynamic Variable' :
                                       tool.name === 'custom_function' ? 'Custom Function' :
                                       tool.name}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {/* Edit Button */}
                                  <button 
                                    onClick={() => handleEditFunction(index)}
                                    style={{ 
                                      color: '#6b7280', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer',
                                      fontSize: 14,
                                      padding: '6px',
                                      borderRadius: 4,
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#f3f4f6';
                                      e.target.style.color = '#374151';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = 'none';
                                      e.target.style.color = '#6b7280';
                                    }}
                                    title="Edit Function"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  
                                  {/* Delete Button */}
                                  <button 
                                    onClick={() => handleRemoveGeneralTool(index)}
                                    style={{ 
                                      color: '#6b7280', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer',
                                      fontSize: 14,
                                      padding: '6px',
                                      borderRadius: 4,
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = '#fee2e2';
                                      e.target.style.color = '#dc2626';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = 'none';
                                      e.target.style.color = '#6b7280';
                                    }}
                                    title="Delete Function"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {/* Custom content for Knowledge Base */}
                    {item === 'Knowledge Base' && (
                      <>
                        <div style={{ color: '#222', fontSize: 16, marginBottom: 10 }}>Knowledge bases attached to this agent:</div>
                        {loadingKBs ? (
                          <div style={{ color: '#64748b', fontSize: 15 }}>Loading...</div>
                        ) : kbError ? (
                          <div style={{ color: 'red', fontSize: 15 }}>{kbError}</div>
                        ) : attachedKBs.length === 0 ? (
                          <div style={{ color: '#64748b', fontSize: 15 }}>No knowledge bases attached.</div>
                        ) : (
                          <div style={{ marginBottom: 16 }}>
                            {attachedKBs.map(kb => (
                              <div key={kb.id} style={{ 
                                background: '#ffffff', 
                                border: '1px solid #e5e7eb', 
                                borderRadius: 8, 
                                padding: 12, 
                                marginBottom: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 12,
                                  flexGrow: 1
                                }}>
                                  <i className="fas fa-file-alt" style={{ 
                                    fontSize: 16, 
                                    color: '#374151' 
                                  }}></i>
                                  <div style={{ 
                                    fontWeight: 500, 
                                    fontSize: 14, 
                                    color: '#374151',
                                    wordBreak: 'break-word'
                                  }}>
                                    {kb.topic || kb.name || 'Untitled Knowledge Base'}
                                  </div>
                                </div>
                                <button 
                                  onClick={() => handleDeleteKnowledgeBase(kb.id, kb.topic || kb.name)}
                                  style={{ 
                                    background: 'none',
                                    border: 'none',
                                    color: '#374151',
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}
                                  title="Remove Knowledge Base from Agent"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Add Button with Dropdown */}
                        <div style={{ position: 'relative', marginTop: 16, width: '100%' }}>
                          <button 
                            onClick={handleKbDropdownToggle}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 8, 
                              background: '#fff', 
                              color: '#222', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: 12, 
                              padding: '14px 32px', 
                              fontWeight: 600, 
                              fontSize: 18, 
                              cursor: 'pointer', 
                              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#f8fafc';
                              e.target.style.borderColor = '#d1d5db';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#fff';
                              e.target.style.borderColor = '#e5e7eb';
                            }}
                          >
                            <i className="fas fa-plus" style={{ fontSize: 20, color: '#64748b' }}></i> Add
                            <i className={`fas fa-chevron-${kbDropdownOpen ? 'up' : 'down'}`} style={{ fontSize: 14, color: '#64748b', marginLeft: 8 }}></i>
                          </button>
                          
                          {/* Dropdown Menu */}
                          {kbDropdownOpen && (
                            <div 
                              ref={kbDropdownRef}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                width: '100%',
                                background: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: 12,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                zIndex: 9999,
                                marginTop: 8,
                                maxHeight: '300px',
                                overflowY: 'auto'
                              }}
                            >
                              {/* Available Files */}
                              {loadingFiles ? (
                                <div style={{
                                  padding: '12px 16px',
                                  color: '#64748b',
                                  fontSize: 14,
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}>
                                  Loading files...
                                </div>
                              ) : availableFiles.length > 0 ? (
                                <>
                                  <div style={{ 
                                    padding: '8px 16px', 
                                    background: '#f8fafc', 
                                    borderBottom: '1px solid #e5e7eb',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#64748b',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Available Files
                                  </div>
                                  {availableFiles.map(file => (
                                    <div
                                      key={file.id}
                                      onClick={() => handleAddExistingFile(file.id, file.topic, file.content, file.tags, file.source_url, file.agent_id)}
                                      style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f3f4f6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        transition: 'background-color 0.2s ease',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                    >
                                      <i className="fas fa-file-alt" style={{ 
                                        color: '#7c3aed', 
                                        fontSize: 16, 
                                        flexShrink: 0,
                                        display: 'inline-block',
                                        verticalAlign: 'middle'
                                      }}></i>
                                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <div style={{ 
                                          fontWeight: 500, 
                                          color: '#374151', 
                                          fontSize: 14,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}>
                                          {file.topic || 'Untitled File'}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  <div style={{ 
                                    padding: '12px 16px',
                                    borderTop: '1px solid #e5e7eb',
                                    background: '#f8fafc'
                                  }}>
                                    <button
                                      type="button"
                                      onClick={handleCreateNewKnowledgeBase}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        background: '#10b981',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 8,
                                        padding: '10px 16px',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease',
                                        width: '100%'
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#059669'}
                                      onMouseLeave={(e) => e.target.style.background = '#10b981'}
                                    >
                                      <i className="fas fa-plus" style={{ fontSize: 14 }}></i>
                                      Add Knowledge Base
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <div style={{ 
                                  padding: '16px', 
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 12,
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}>
                                  <div style={{ 
                                    color: '#64748b', 
                                    fontSize: 14,
                                    textAlign: 'center'
                                  }}>
                                    No files available
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleCreateNewKnowledgeBase}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      background: '#10b981',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 8,
                                      padding: '10px 16px',
                                      fontWeight: 600,
                                      fontSize: 14,
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#059669'}
                                    onMouseLeave={(e) => e.target.style.background = '#10b981'}
                                  >
                                    <i className="fas fa-plus" style={{ fontSize: 14 }}></i>
                                    Add Knowledge Base
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {/* Custom content for Post-Call Analysis */}
                    {item === 'Post-Call Analysis' && (
                      <>
                        {postCallAnalysisLoading ? (
                          <div style={{ color: '#64748b' }}>Loading post-call analysis config...</div>
                        ) : postCallAnalysisError ? (
                          <div style={{ color: '#ef4444' }}>{postCallAnalysisError}</div>
                        ) : (
                          <>
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 8 }}>Post-Call Analysis Fields</div>
                            {postCallAnalysisConfig.post_call_analysis_data.length === 0 && (
                              <div style={{ color: '#64748b', marginBottom: 8 }}>No fields defined yet.</div>
                            )}
                            {postCallAnalysisConfig.post_call_analysis_data.map((field, idx) => (
                              <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                  <select value={field.type} onChange={e => handleUpdateAnalysisField(idx, { type: e.target.value })} style={{ minWidth: 90 }}>
                                    <option value="string">String</option>
                                    <option value="boolean">Boolean</option>
                                    <option value="number">Number</option>
                                    <option value="enum">Enum</option>
                                  </select>
                                  <input value={field.name} onChange={e => handleUpdateAnalysisField(idx, { name: e.target.value })} placeholder="Field name" style={{ flex: 1 }} />
                                  <button onClick={() => handleRemoveAnalysisField(idx)} style={{ color: '#ef4444', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                                </div>
                                <input value={field.description} onChange={e => handleUpdateAnalysisField(idx, { description: e.target.value })} placeholder="Description" style={{ width: '100%', marginBottom: 6 }} />
                                <input value={Array.isArray(field.examples) ? field.examples.join(', ') : ''} onChange={e => handleUpdateAnalysisField(idx, { examples: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} placeholder="Examples (comma separated)" style={{ width: '100%' }} />
                              </div>
                            ))}
                            <button onClick={handleAddAnalysisField} style={{ marginBottom: 12, background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', color: '#222', cursor: 'pointer' }}>Add Field</button>
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 8 }}>Analysis Model</div>
                            <select
                              value={postCallAnalysisConfig.post_call_analysis_model || 'gpt-4o-mini'}
                              onChange={e => setPostCallAnalysisConfig(cfg => ({ ...cfg, post_call_analysis_model: e.target.value }))}
                              style={{ width: '100%', marginBottom: 12 }}
                            >
                              <option value="gpt-4o-mini">gpt-4o-mini</option>
                              <option value="gpt-4o">gpt-4o</option>
                            </select>
                            <button
                              onClick={handleSavePostCallAnalysisConfig}
                              disabled={!agent?.agent_id || !userEmail || updatingPostCallAnalysis}
                              style={{ background: updatingPostCallAnalysis ? '#9ca3af' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: updatingPostCallAnalysis ? 'not-allowed' : 'pointer' }}
                            >
                              {updatingPostCallAnalysis ? 'Saving...' : 'Save'}
                            </button>
                          </>
                        )}
                      </>
                    )}
                    {/* Custom content for Verify Numbers */}
                    {item === 'Verify Numbers' && (
                      <>
                        <div style={{ fontWeight: 600, color: '#222', marginBottom: 8 }}>Verified Numbers</div>
                        
                        
                        <div style={{ 
                          background: '#f8fafc', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: 8, 
                          padding: '16px',
                          marginBottom: '16px'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="verifiedNumbersConnection"
                                  value="connect"
                                  checked={verifiedNumbersConnection === 'connected'}
                                  onChange={() => handleVerifiedNumbersConnection('connect')}
                                  disabled={verifiedNumbersLoading}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                  Connect
                                </span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  name="verifiedNumbersConnection"
                                  value="disconnect"
                                  checked={verifiedNumbersConnection === 'disconnected'}
                                  onChange={() => handleVerifiedNumbersConnection('disconnect')}
                                  disabled={verifiedNumbersLoading}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                  Disconnect
                                </span>
                              </label>
                            </div>
                            {verifiedNumbersLoading && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ 
                                  width: '16px', 
                                  height: '16px', 
                                  border: '2px solid #e5e7eb', 
                                  borderTop: '2px solid #3b82f6', 
                                  borderRadius: '50%', 
                                  animation: 'spin 1s linear infinite' 
                                }}></div>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Processing...</span>
                              </div>
                            )}
                            {verifiedNumbersConnection === 'connected' && !verifiedNumbersLoading && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '16px' }}></i>
                                <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '500' }}>Connected</span>
                                {verifiedPhoneNumbersList.length > 0 && (
                                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                    ({verifiedPhoneNumbersList.length} verified number{verifiedPhoneNumbersList.length !== 1 ? 's' : ''})
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                      </>
                    )}
                    {/* Custom content for Multi-Prompt Backend */}
                    {item === 'Multi-Prompt Backend' && (
                      <div style={{ color: '#64748b', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
                        <i className="fas fa-arrow-left" style={{ marginRight: 8 }}></i>
                        Multi-Prompt Backend configuration has been moved to the main editor area for a better editing experience.
                      </div>
                    )}
                    {/* Custom content for Speech Settings */}
                    {item === 'Speech Settings' && (
                      <>
                        {speechSettingsLoading ? (
                          <div style={{ color: '#64748b', fontSize: 14 }}>Loading speech settings...</div>
                        ) : speechSettingsError ? (
                          <div style={{ color: '#ef4444', fontSize: 14 }}>Error: {speechSettingsError}</div>
                        ) : (
                          <>
                            {/* <div style={{ color: '#222', fontWeight: 600, fontSize: 15, marginBottom: 10 }}>Background Sound</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
                              <select style={{ fontSize: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#222' }}>
                                <option>None</option>
                              </select>
                              <button style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer' }}><i className="fas fa-cog"></i></button>
                            </div> */}
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Responsiveness <i className="fas fa-info-circle" style={{ fontSize: 13, color: '#64748b', marginLeft: 4 }}></i></div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>Control how fast the agent responds after users finish speaking.</div>
                            <div style={{ marginBottom: 10 }}>
                              <CustomSlider
                                value={localResponsiveness}
                                min={0}
                                max={1}
                                step={0.1}
                                onChange={val => setLocalResponsiveness(val)}
                              />
                            </div>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>Current: {localResponsiveness.toFixed(1)}/1.0</div>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Interruption Sensitivity</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>Control how sensitively AI can be interrupted by human speech.</div>
                            <div style={{ marginBottom: 10 }}>
                              <CustomSlider
                                value={localInterruption}
                                min={0}
                                max={1}
                                step={0.1}
                                onChange={val => setLocalInterruption(val)}
                              />
                            </div>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>Current: {localInterruption.toFixed(1)}/1.0</div>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Enable Backchanneling</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>Enables the agent to use affirmations like 'yeah' or 'uh-huh' during conversations, indicating active listening and engagement.</div>
                            <label style={{ display: 'block', marginBottom: 12 }}>
                              <input 
                                type="checkbox" 
                                checked={localBackchanneling}
                                onChange={e => setLocalBackchanneling(e.target.checked)}
                                style={{ accentColor: '#64748b', width: 22, height: 22, marginRight: 8 }} 
                              />
                              {localBackchanneling ? 'Enabled' : 'Disabled'}
                            </label>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Enable Speech Normalization <i className="fas fa-info-circle" style={{ fontSize: 13, color: '#64748b', marginLeft: 4 }}></i></div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>It converts text elements like numbers, currency, and dates into human-like spoken forms. (Learn more)</div>
                            <label style={{ display: 'block', marginBottom: 12 }}>
                              <input 
                                type="checkbox" 
                                checked={localSpeechNormalization}
                                onChange={e => setLocalSpeechNormalization(e.target.checked)}
                                style={{ accentColor: '#64748b', width: 22, height: 22, marginRight: 8 }} 
                              />
                              {localSpeechNormalization ? 'Enabled' : 'Disabled'}
                            </label>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Reminder Message Frequency</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>Control how often AI will send a reminder message.</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                              <input 
                                type="number" 
                                value={localReminder.interval}
                                onChange={e => {
                                  const newInterval = parseInt(e.target.value) || 0;
                                  setLocalReminder(rem => ({ ...rem, interval: newInterval }));
                                  setReminderDirty(true);
                                }}
                                style={{ width: 60, padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 15 }} 
                              />
                              <span>seconds</span>
                              <input 
                                type="number" 
                                value={localReminder.times}
                                onChange={e => {
                                  const newTimes = parseInt(e.target.value) || 0;
                                  setLocalReminder(rem => ({ ...rem, times: newTimes }));
                                  setReminderDirty(true);
                                }}
                                style={{ width: 60, padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 15 }} 
                              />
                              <span>times</span>
                              <button
                                onClick={async () => {
                                  setReminderSaving(true);
                                  // æ ¡éªŒ times
                                  let timesNum = parseInt(localReminder.times, 10);
                                  if (isNaN(timesNum) || timesNum < 1) timesNum = 1;
                                  await handleSpeechSettingsUpdate({ reminder_message_frequency: { ...localReminder, times: timesNum } });
                                  setReminderSaving(false);
                                  setReminderDirty(false);
                                }}
                                disabled={!reminderDirty || reminderSaving}
                                style={{
                                  marginLeft: 12,
                                  background: reminderDirty ? '#3b82f6' : '#e5e7eb',
                                  color: reminderDirty ? '#fff' : '#64748b',
                                  border: 'none',
                                  borderRadius: 6,
                                  padding: '6px 16px',
                                  fontWeight: 600,
                                  fontSize: 15,
                                  cursor: reminderDirty && !reminderSaving ? 'pointer' : 'not-allowed',
                                  opacity: reminderSaving ? 0.7 : 1
                                }}
                              >
                                {reminderSaving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Pronunciation</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>
                              Guide the model to pronounce a word, name, or phrase in a specific way. (Learn more)
                            </div>
                            <button
                              onClick={() => setPronunciationModalOpen(true)}
                              style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', color: '#222', cursor: 'pointer', marginBottom: 12 }}
                            >
                              + Add
                            </button>
                            {/* Display all current pronunciations */}
                            {Array.isArray(speechSettings.pronunciation) && speechSettings.pronunciation.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {speechSettings.pronunciation.map((entry, idx) => (
                                  <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div><b>Word:</b> {entry.word}</div>
                                    <div><b>Alphabet:</b> {entry.alphabet}</div>
                                    <div><b>Phoneme:</b> {entry.phoneme}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ color: '#64748b', marginTop: 6 }}>No pronunciations added yet.</div>
                            )}
                            
                            {updatingSpeechSettings && (
                              <div style={{ color: '#64748b', fontSize: 14, marginTop: 10 }}>Updating speech settings...</div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {/* Custom content for Transcription Settings */}
                    {item === 'Transcription Settings' && (
                      <>
                        {transcriptionSettingsLoading ? (
                          <div style={{ color: '#64748b', fontSize: 14 }}>Loading transcription settings...</div>
                        ) : transcriptionSettingsError ? (
                          <div style={{ color: '#ef4444', fontSize: 14 }}>Error: {transcriptionSettingsError}</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {/* Denoising Mode */}
                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 18, border: '1px solid #e5e7eb' }}>
                              <div style={{ fontWeight: 700, color: '#222', fontSize: 16, marginBottom: 2 }}>Denoising Mode <span style={{ color: '#64748b', fontWeight: 400, fontSize: 13 }}>(Learn more)</span></div>
                              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 10 }}>Filter out unwanted background noise or speech.</div>
                              <div style={{ display: 'flex', gap: 24 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input 
                                    type="radio" 
                                    name="denoise" 
                                    checked={localDenoisingMode === 'noise-cancellation'}
                                    onChange={() => setLocalDenoisingMode('noise-cancellation')}
                                    style={{ accentColor: '#222' }} 
                                  />
                                  Remove noise
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input 
                                    type="radio" 
                                    name="denoise" 
                                    checked={localDenoisingMode === 'noise-and-background-speech-cancellation'}
                                    onChange={() => setLocalDenoisingMode('noise-and-background-speech-cancellation')}
                                    style={{ accentColor: '#222' }} 
                                  />
                                  Remove noise + background speech
                                </label>
                              </div>
                            </div>
                            {/* Transcription Mode */}
                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 18, border: '1px solid #e5e7eb' }}>
                              <div style={{ fontWeight: 700, color: '#222', fontSize: 16, marginBottom: 2 }}>Transcription Mode <span style={{ color: '#64748b', fontWeight: 400, fontSize: 13 }}>(Learn more)</span></div>
                              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 10 }}>Balance between speed and accuracy.</div>
                              <div style={{ display: 'flex', gap: 24 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input 
                                    type="radio" 
                                    name="transcriptionMode" 
                                    checked={localSttMode === 'fast'}
                                    onChange={() => setLocalSttMode('fast')}
                                    style={{ accentColor: '#222' }} 
                                  />
                                  Optimize for speed
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input 
                                    type="radio" 
                                    name="transcriptionMode" 
                                    checked={localSttMode === 'accurate'}
                                    onChange={() => setLocalSttMode('accurate')}
                                    style={{ accentColor: '#222' }} 
                                  />
                                  Optimize for accuracy
                                </label>
                              </div>
                            </div>
                            {/* Vocabulary Specialization */}
                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 18, border: '1px solid #e5e7eb' }}>
                              <div style={{ fontWeight: 700, color: '#222', fontSize: 16, marginBottom: 2 }}>Vocabulary Specialization</div>
                              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 10 }}>Choose the vocabulary set to use for transcription.</div>
                              <div style={{ display: 'flex', gap: 24 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input 
                                    type="radio" 
                                    name="vocab" 
                                    checked={localVocabSpecialization === 'general'}
                                    onChange={() => setLocalVocabSpecialization('general')}
                                    style={{ accentColor: '#222' }} 
                                  />
                                  General (Works well across most industries)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input 
                                    type="radio" 
                                    name="vocab" 
                                    checked={localVocabSpecialization === 'medical'}
                                    onChange={() => setLocalVocabSpecialization('medical')}
                                    style={{ accentColor: '#222' }} 
                                  />
                                  Medical (Optimized for healthcare terms)
                                </label>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Custom content for Call Settings */}
                    {item === 'Call Settings' && (
                      <>
                        {!agent?.agent_id || !userEmail ? (
                          <div style={{ color: '#64748b', fontSize: 14 }}>Loading agent information...</div>
                        ) : callSettingsLoading ? (
                          <div style={{ color: '#64748b', fontSize: 14 }}>Loading call settings...</div>
                        ) : callSettingsError ? (
                          <div style={{ color: '#ef4444', fontSize: 14 }}>Error: {callSettingsError}</div>
                        ) : (
                          <>
                            {/* <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Voicemail Detection</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>Hang up or leave a voicemail if a voicemail is detected.</div>
                            <label style={{ display: 'block', marginBottom: 12 }}>
                              <input 
                                type="checkbox" 
                                checked={localVoicemailDetection}
                                onChange={e => setLocalVoicemailDetection(e.target.checked)}
                                style={{ accentColor: '#64748b', width: 22, height: 22, marginRight: 8 }} 
                              />
                              {localVoicemailDetection ? 'Enabled' : 'Disabled'}
                            </label>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>User Keypad Input Detection</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>Enable the AI to listen for keypad input during a call.</div>
                            <label style={{ display: 'block', marginBottom: 12 }}>
                              <input 
                                type="checkbox" 
                                checked={localKeypadInputDetection}
                                onChange={e => setLocalKeypadInputDetection(e.target.checked)}
                                style={{ accentColor: '#64748b', width: 22, height: 22, marginRight: 8 }} 
                              />
                              {localKeypadInputDetection ? 'Enabled' : 'Disabled'}
                            </label>
                             */}
                            {callSettings.keypad_input_detection && (
                              <>
                                <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>The AI will respond when any of the following conditions are met:</div>
                                <div style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 16px', marginBottom: 12 }}>
                                  <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>Timeout</div>
                                  <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>The AI will respond if no keypad input is detected within the set time.</div>
                                  <input 
                                    type="range" 
                                    min="0" 
                                    max="10" 
                                    step="0.1" 
                                    value={callSettings.keypad_input_timeout}
                                    onChange={(e) => {
                                      if (agent?.agent_id && userEmail) {
                                        handleCallSettingsUpdate({ keypad_input_timeout: parseFloat(e.target.value) });
                                      }
                                    }}
                                    style={{ width: '100%', marginBottom: 6 }} 
                                  />
                                  <span style={{ color: '#222', fontWeight: 500 }}>{callSettings.keypad_input_timeout} s</span>
                                  
                                  <div style={{ fontWeight: 600, color: '#222', marginTop: 10, marginBottom: 2 }}>Termination Key</div>
                                  <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>The AI will respond when the user presses the configured termination key (0-9, #, *).</div>
                                  <label style={{ display: 'block', marginBottom: 8 }}>
                                    <input 
                                      type="checkbox" 
                                      checked={callSettings.keypad_input_termination_key}
                                      onChange={(e) => {
                                        if (agent?.agent_id && userEmail) {
                                          handleCallSettingsUpdate({ keypad_input_termination_key: e.target.checked });
                                        }
                                      }}
                                      style={{ accentColor: '#64748b', width: 22, height: 22, marginRight: 8 }} 
                                    />
                                    {callSettings.keypad_input_termination_key ? 'Enabled' : 'Disabled'}
                                  </label>
                                  
                                  <div style={{ fontWeight: 600, color: '#222', marginTop: 10, marginBottom: 2 }}>Digit Limit</div>
                                  <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>The AI will respond immediately after the caller enters the configured number of digits.</div>
                                  <label style={{ display: 'block', marginBottom: 8 }}>
                                    <input 
                                      type="checkbox" 
                                      checked={callSettings.keypad_input_digit_limit}
                                      onChange={(e) => {
                                        if (agent?.agent_id && userEmail) {
                                          handleCallSettingsUpdate({ keypad_input_digit_limit: e.target.checked });
                                        }
                                      }}
                                      style={{ accentColor: '#64748b', width: 22, height: 22, marginRight: 8 }} 
                                    />
                                    {callSettings.keypad_input_digit_limit ? 'Enabled' : 'Disabled'}
                                  </label>
                                </div>
                              </>
                            )}
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>End Call on Silence</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>End the call if user stays silent for extended period of time.</div>
                            <div style={{ marginBottom: 10 }}>
                              <CustomSlider
                                value={localSilenceTimeout}
                                min={0}
                                max={1200}
                                step={10}
                                onChange={val => setLocalSilenceTimeout(val)}
                              />
                            </div>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>Current: {Math.round(localSilenceTimeout / 60 * 10) / 10} m</div>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2, marginTop: 10 }}>Max Call Duration</div>
                            <div style={{ marginBottom: 10 }}>
                              <CustomSlider
                                value={localMaxCallDuration}
                                min={0}
                                max={7200}
                                step={60}
                                onChange={val => setLocalMaxCallDuration(val)}
                              />
                            </div>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>Current: {Math.round(localMaxCallDuration / 3600 * 100) / 100} h</div>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2, marginTop: 10 }}>Pause Before Speaking</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>The duration before the assistant starts speaking at the beginning of the call.</div>
                            <div style={{ marginBottom: 10 }}>
                              <CustomSlider
                                value={localPauseBeforeSpeaking}
                                min={0}
                                max={10}
                                step={0.1}
                                onChange={val => setLocalPauseBeforeSpeaking(val)}
                              />
                            </div>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>Current: {localPauseBeforeSpeaking} s</div>
                            
                            <div style={{ fontWeight: 600, color: '#222', marginBottom: 2, marginTop: 10 }}>Ring Duration</div>
                            <div style={{ color: '#64748b', fontSize: 14, marginBottom: 6 }}>The max ringing duration before the outbound call / transfer call is deemed no answer.</div>
                            <div style={{ marginBottom: 10 }}>
                              <CustomSlider
                                value={localRingDuration}
                                min={0}
                                max={60}
                                step={1}
                                onChange={val => setLocalRingDuration(val)}
                              />
                            </div>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>Current: {localRingDuration} s</div>
                            
                            {updatingCallSettings && (
                              <div style={{ color: '#64748b', fontSize: 14, marginTop: 10 }}>Updating call settings...</div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {/* MCP Server component check - commented out as requested */}
                    {/* {item === 'MCP Server' && (
                      <div style={{ color: '#64748b', fontSize: 14 }}>MCP Server component loaded</div>
                    )} */}
                    {/* Default placeholder for other accordions */}
                    {item !== 'Functions' && item !== 'Knowledge Base' && item !== 'Post-Call Analysis' && item !== 'Verify Numbers' && item !== 'MCP Server' && item !== 'Speech Settings' && item !== 'Transcription Settings' && item !== 'Call Settings' && (
                      <>[Placeholder for {item} content]</>
                    )}
                  </div>
                )}
                {/* Divider except after last item */}
                {idx < arr.length - 1 && <div style={{ height: 1, background: '#f3f4f6', margin: '0 24px' }} />}
              </React.Fragment>
            ))}
        </div>
        {/* Right column: Accordion settings */}
        <div style={{ 
          flex: assistantPanelOpen ? '0 0 12.5%' : '0 0 25%', 
          minWidth: assistantPanelOpen ? '150px' : '300px', 
          padding: assistantPanelOpen ? '16px 8px' : '32px 16px', 
          background: '#fff', 
          height: 'calc(100vh - 120px)', 
          overflowY: 'auto', 
          display: 'flex', 
          flexDirection: 'column', 
          transition: 'all 0.3s ease', 
          position: 'relative',
          ...(assistantPanelOpen && {
            background: '#f8f9fa',
            borderRight: '1px solid #e1e5e9'
          })
        }}>
          {/* Test UI as shown in the image, no square box */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 350 }}>
            <i className="fas fa-microphone" style={{ fontSize: 64, color: '#cbd5e1', marginBottom: 24 }}></i>
            <div style={{ fontWeight: 500, fontSize: 20, color: '#222', marginBottom: 18 }}>Test your agent</div>
            <div style={{ background: '#f1f5f9', color: '#64748b', borderRadius: 8, padding: '10px 18px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <i className="fas fa-info-circle" style={{ fontSize: 18, color: '#64748b' }}></i>
              Please note call transfer is not supported in Webcall.
            </div>
            {agentLoading && <div style={{ color: '#64748b', marginBottom: 12 }}>Loading agent info...</div>}
            {agentError && <div style={{ color: '#dc2626', marginBottom: 12 }}>{agentError}</div>}
            {callStatus && (
              <div style={{ background: isCallActive ? '#ecfdf5' : '#f0f9ff', border: `1px solid ${isCallActive ? '#a7f3d0' : '#bae6fd'}`, borderRadius: '8px', padding: '12px', marginBottom: '20px', width: '100%', textAlign: 'center' }}>
                <div style={{ color: isCallActive ? '#065f46' : '#0c4a6e', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <i className={`fas ${isCallActive ? 'fa-microphone' : 'fa-info-circle'}`}></i>
                  {callStatus}
                </div>
              </div>
            )}
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '20px', width: '100%', textAlign: 'center' }}>
                <div style={{ color: '#dc2626', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  {error}
                </div>
              </div>
            )}
            {/* Controls: Start/Connecting/End Call */}
            {!isCallActive ? (
              <button
                onClick={startCall}
                disabled={isConnecting || !agent || !userEmail}
                style={{
                  background: isConnecting ? '#9ca3af' : '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isConnecting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '140px',
                  justifyContent: 'center',
                  marginTop: 8
                }}
              >
                <i className={`fas ${isConnecting ? 'fa-spinner fa-spin' : 'fa-phone'}`}></i>
                {isConnecting ? 'Connecting...' : 'Start Call'}
              </button>
            ) : (
              <button
                onClick={endCall}
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '140px',
                  justifyContent: 'center',
                  marginTop: 8
                }}
              >
                <i className="fas fa-phone-slash"></i>
                End Call
              </button>
            )}
            {!userEmail && <div style={{ color: '#dc2626', marginTop: 12 }}>Please log in to test the agent.</div>}
            {/* Instructions */}
            <div style={{ marginTop: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>
                <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                Make sure your microphone is enabled. The call will connect through your browser.
              </div>
            </div>
          </div>
        </div>
        
        {/* Assistant Panel - Fourth Column - COMMENTED OUT */}
        {false && (
          <div style={{
            flex: '0 0 25%',
            minWidth: '250px',
            background: '#fff',
            borderLeft: '1px solid #e5e7eb',
            height: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease'
          }}>
            {assistantPanelOpen && (
              <AIAssistant 
                onClose={() => setAssistantPanelOpen(false)}
                isAgentDetails={true}
                currentSection="AgentDetails"
                currentUser={{
                  id: userId,
                  email: userEmail,
                  full_name: userName || userEmail
                }}
                formContext={{
                  agent_id: agentId,
                  agent_name: agent?.agent_name || 'Agent from URL',
                  is_agent_details: true
                }}
                selectedAgentData={{
                  agent_id: agentId,
                  agent_name: agent?.agent_name || 'Agent from URL',
                  user_id: userId,
                  user_email: userEmail,
                  selection_timestamp: new Date().toISOString()
                }}
              />
            )}
          </div>
        )}
        

      </div>
    </div>
    {/* Pronunciation Modal */}
    {pronunciationModalOpen && (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.15)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18 }}>Pronunciation</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600 }}>Word</label>
            <input
              value={pronunciationWord}
              onChange={e => setPronunciationWord(e.target.value)}
              placeholder="e.g. Retell"
              style={{ width: '100%', marginTop: 4, marginBottom: 12, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600 }}>Pronunciation</label>
            <select
              value={pronunciationAlphabet}
              onChange={e => setPronunciationAlphabet(e.target.value)}
              style={{ width: '100%', marginTop: 4, marginBottom: 12, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
            >
              <option value="ipa">IPA (11 labs and Cartesia)</option>
              <option value="cmu">CMU (ARPAbet)</option>
            </select>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600 }}>Phoneme</label>
            <input
              value={pronunciationPhoneme}
              onChange={e => setPronunciationPhoneme(e.target.value)}
              placeholder="e.g. ri:tÉ›l"
              style={{ width: '100%', marginTop: 4, marginBottom: 12, padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              onClick={() => setPronunciationModalOpen(false)}
              style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 18px', color: '#222', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleSavePronunciation({
                  word: pronunciationWord,
                  alphabet: pronunciationAlphabet,
                  phoneme: pronunciationPhoneme
                });
                setPronunciationModalOpen(false);
                setPronunciationWord('');
                setPronunciationAlphabet('ipa');
                setPronunciationPhoneme('');
              }}
              style={{ background: '#222', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 24px', fontWeight: 600, cursor: 'pointer' }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Publish Modal */}
    {publishModalOpen && (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh', 
        background: 'rgba(0,0,0,0.15)', 
        zIndex: 1000, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ 
          background: '#fff', 
          borderRadius: 16, 
          padding: 32, 
          minWidth: 500, 
          maxWidth: 600,
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)' 
        }}>
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 24 
          }}>
            <div style={{ fontWeight: 700, fontSize: 20, color: '#000' }}>Publish</div>
            <button
              onClick={() => setPublishModalOpen(false)}
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: 20, 
                color: '#64748b', 
                cursor: 'pointer',
                padding: 4
              }}
            >
              Ã—
            </button>
          </div>
          
          {/* Version Name Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, color: '#000' }}>
              Version name
            </div>
            <input
              type="text"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              placeholder="V0- add a descriptive name (optional)"
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                borderRadius: 8, 
                border: '1px solid #e5e7eb', 
                fontSize: 14,
                color: '#000'
              }}
            />
          </div>
          
          {/* Select Phone Number Section */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, color: '#000' }}>
              Select Phone Number
            </div>
            <div style={{ 
              fontSize: 14, 
              color: '#000', 
              marginBottom: 16,
              lineHeight: '1.4'
            }}>
              Phone number is optional. You can proceed without it.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12, 
                cursor: 'pointer',
                fontSize: 14,
                color: '#000'
              }}>
                <input
                  type="checkbox"
                  checked={inboundPhoneNumber}
                  onChange={(e) => setInboundPhoneNumber(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                Inbound phone number
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12, 
                cursor: 'pointer',
                fontSize: 14,
                color: '#000'
              }}>
                <input
                  type="checkbox"
                  checked={outboundPhoneNumber}
                  onChange={(e) => setOutboundPhoneNumber(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                Outbound phone number
              </label>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 12 
          }}>
            <button
              onClick={() => setPublishModalOpen(false)}
              style={{ 
                background: '#fff', 
                border: '1px solid #e5e7eb', 
                borderRadius: 8, 
                padding: '10px 20px', 
                color: '#000', 
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              onClick={async (event) => {
                try {
                  // Show loading state
                  const publishButton = event.target;
                  const originalText = publishButton.textContent;
                  publishButton.textContent = 'Publishing...';
                  publishButton.disabled = true;
                  
                  // Call the publish API
                  const response = await fetch(`${API_BASE_URL}/api/agents/${agentId}/publish`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      agent_name: agent?.agent_name || 'Agent',
                      version_name: versionName || 'V1.0',
                      inbound_phone: inboundPhoneNumber,
                      outbound_phone: outboundPhoneNumber,
                      user_email: userEmail
                    })
                  });
                  
                  if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to publish agent');
                  }
                  
                  const result = await response.json();
                  
                  // Show success message
                  alert('Agent published successfully!');
                  
                  // Close modal and reset form
                  setPublishModalOpen(false);
                  setVersionName('');
                  setInboundPhoneNumber(false);
                  setOutboundPhoneNumber(false);
                  
                  // Refresh publish status after successful publish
                  if (agentId && userEmail) {
                    // Re-check publish status to update UI
                    const statusResponse = await fetch(`${API_BASE_URL}/api/agents/${agentId}/publish-status?user_email=${encodeURIComponent(userEmail)}`);
                    const statusData = await statusResponse.json();
                    if (statusData.success) {
                      setIsAgentPublished(statusData.is_published);
                    }
                    console.log('Agent published:', result);
                  }
                  
                } catch (error) {
                  console.error('Error publishing agent:', error);
                  alert(`Failed to publish agent: ${error.message}`);
                } finally {
                  // Reset button state
                  const publishButton = event.target;
                  publishButton.textContent = 'Publish';
                  publishButton.disabled = false;
                }
              }}
              style={{ 
                background: '#000', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 8, 
                padding: '10px 20px', 
                fontWeight: 500, 
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              Publish
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Transfer Call Popup removed - no longer needed */}

    {/* Edit Function Modal */}
    {editFunctionModalOpen && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          width: '90vw',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-link" style={{ color: '#6b7280', fontSize: '18px' }}></i>
              <span style={{
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {editFunctionConfig.name === 'transfer_call' ? 'Call Transfer' :
                 editFunctionConfig.name === 'end_call' ? 'End Call' :
                 editFunctionConfig.name === 'agent_transfer' ? 'Agent Transfer' :
                 editFunctionConfig.name === 'check_calendar' ? 'Check Calendar Availability' :
                 editFunctionConfig.name === 'book_calendar' ? 'Book on the Calendar' :
                 editFunctionConfig.name === 'press_digit' ? 'Press Digit (IVR Navigation)' :
                 editFunctionConfig.name === 'send_sms' ? 'Send SMS' :
                 'Custom Function'}
              </span>
            </div>
              <button
                onClick={handleCancelEditFunction}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#1f2937'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Content */}
          <div style={{ marginBottom: '20px' }}>
            {/* Name Field */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Name
              </label>
              <input
                type="text"
                value={editFunctionConfig.name}
                onChange={(e) => setEditFunctionConfig(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter the name of the custom function"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#1f2937',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {/* Description Field */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#1f2937',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '6px'
              }}>
                Description
              </label>
              <textarea
                value={editFunctionConfig.description}
                onChange={(e) => setEditFunctionConfig(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter the description of the custom function"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#1f2937',
                  fontSize: '14px',
                  outline: 'none',
                  minHeight: '80px',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            {/* Conditional Fields based on function type - Simplified to show only Destination Phone Number for transfer_call */}
            {editFunctionConfig.name === 'transfer_call' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  color: '#1f2937',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px'
                }}>
                  Destination Phone Number
                </label>
                <input
                  type="text"
                  value={editFunctionConfig.destination_number}
                  onChange={(e) => setEditFunctionConfig(prev => ({ ...prev, destination_number: e.target.value }))}
                  placeholder="Enter phone number (e.g., +1234567890)"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#1f2937',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            )}

            {/* API Endpoint - Only show for non-call-transfer functions */}
            {editFunctionConfig.name !== 'transfer_call' && editFunctionConfig.name !== 'end_call' && editFunctionConfig.name !== 'press_digit' && editFunctionConfig.name !== 'send_sms' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  color: '#1f2937',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '6px'
                }}>
                  API Endpoint
                </label>
                <div style={{
                  color: '#6b7280',
                  fontSize: '12px',
                  marginBottom: '8px'
                }}>
                  The API Endpoint is the address of the service you are connecting to
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value="POST"
                    style={{
                      padding: '10px 12px',
                      background: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      color: '#1f2937',
                      fontSize: '14px',
                      outline: 'none',
                      minWidth: '70px',
                      width: '70px'
                    }}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Enter the URL of the custom function"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      background: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      color: '#1f2937',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '20px'
          }}>
            <button
              onClick={handleCancelEditFunction}
              style={{
                background: '#f3f4f6',
                color: '#1f2937',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
              onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEditedFunction}
              style={{
                background: '#059669',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#047857'}
              onMouseLeave={(e) => e.target.style.background = '#059669'}
            >
              Save
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Admin KB Modal */}
      {adminKBModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Knowledge Base</h2>
              <button 
                onClick={() => {
                  setAdminKBModal(false);
                  setSelectedAgentForKB('');
                  setAdminKBForm({ topic: '', content: '', tags: '', source_url: '' });
                  setAdminKBError('');
                  setAdminKBSuccess('');
                  setAdminKBUploadFiles([]);
                  setAdminKBFileUpload(false);
                  // Refresh attached knowledge bases when modal closes
                  if (agent?.agent_id && userId) {
                    fetch(`${API_BASE_URL}/api/agents/${agent.agent_id}/knowledge-bases?user_id=${userId}`)
                      .then(res => res.json())
                      .then(data => {
                        if (data.success && Array.isArray(data.knowledge_bases)) {
                          setAttachedKBs(data.knowledge_bases);
                        }
                      })
                      .catch(err => console.error('Error refreshing knowledge bases:', err));
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {/* Agent Selection */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Select Agent for KB Management</label>
              <select
                value={selectedAgentForKB}
                onChange={e => setSelectedAgentForKB(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
              >
                <option value="">Select an agent...</option>
                {userAgents.map(agent => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.agent_name || agent.agent_id}
                  </option>
                ))}
              </select>
            </div>

            {selectedAgentForKB && (
              <>
                {/* Admin KB Mode Selection */}
                <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setAdminKBFileUpload(false)}
                    style={{
                      background: !adminKBFileUpload ? '#10b981' : '#f3f4f6',
                      color: !adminKBFileUpload ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Manual Entry
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdminKBFileUpload(true)}
                    style={{
                      background: adminKBFileUpload ? '#10b981' : '#f3f4f6',
                      color: adminKBFileUpload ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Upload Documents
                  </button>
                </div>

                {!adminKBFileUpload ? (
                  /* Admin KB Form */
                  <form onSubmit={handleAdminKBSave} style={{ marginBottom: 24, padding: 20, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                    <h3 style={{ marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>Add/Update KB Entry</h3>
                    
                    <div style={{ display: 'grid', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Topic *</label>
                        <input
                          type="text"
                          value={adminKBForm.topic}
                          onChange={e => setAdminKBForm({...adminKBForm, topic: e.target.value})}
                          placeholder="Enter topic (3-120 characters)"
                          style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                          required
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Content *</label>
                        <textarea
                          value={adminKBForm.content}
                          onChange={e => setAdminKBForm({...adminKBForm, content: e.target.value})}
                          placeholder="Enter content (Markdown or plain text)"
                          rows={6}
                          style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none', resize: 'vertical' }}
                          required
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Tags</label>
                        <input
                          type="text"
                          value={adminKBForm.tags}
                          onChange={e => setAdminKBForm({...adminKBForm, tags: e.target.value})}
                          placeholder="pricing, faq, support (comma-separated, max 10 tags)"
                          style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Source URL (optional)</label>
                        <input
                          type="url"
                          value={adminKBForm.source_url}
                          onChange={e => setAdminKBForm({...adminKBForm, source_url: e.target.value})}
                          placeholder="https://example.com"
                          style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                        />
                      </div>
                      
                      <div>
                        <button 
                          type="submit" 
                          disabled={adminKBSaving}
                          style={{ 
                            background: adminKBSaving ? '#f3f4f6' : '#10b981', 
                            color: adminKBSaving ? '#b6bbc6' : '#fff', 
                            border: 'none', 
                            borderRadius: 8, 
                            padding: '12px 24px', 
                            fontWeight: 600, 
                            fontSize: '1rem', 
                            cursor: adminKBSaving ? 'not-allowed' : 'pointer' 
                          }}
                        >
                          {adminKBSaving ? 'Saving...' : 'Save KB Entry'}
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  /* File Upload Interface */
                  <form onSubmit={handleAdminKBFileUpload} style={{ marginBottom: 24, padding: 20, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                    <h3 style={{ marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>Upload Documents</h3>
                    
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Select Files * (Multiple files allowed)</label>
                      <input
                        type="file"
                        multiple
                        accept=".csv,.txt,.pdf,.doc,.docx,.xlsx,.xls"
                        onChange={handleAdminKBFileChange}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                      />
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Supported formats: CSV, TXT, PDF, DOC, DOCX, XLSX, XLS • You can select multiple files at once
                      </div>
                    </div>

                    {/* Selected Files */}
                    {adminKBUploadFiles.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <label style={{ fontWeight: 500, margin: 0 }}>Selected Files ({adminKBUploadFiles.length}):</label>
                          <button
                            type="button"
                            onClick={() => document.querySelector('input[type="file"]').click()}
                            style={{ 
                              background: '#10b981', 
                              color: '#fff', 
                              border: 'none', 
                              borderRadius: 4, 
                              padding: '4px 8px', 
                              fontSize: 12, 
                              cursor: 'pointer' 
                            }}
                          >
                            + Add More Files
                          </button>
                        </div>
                        <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                          {adminKBUploadFiles.map((file, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: '#fff', borderRadius: 4, marginBottom: 4 }}>
                              <span style={{ fontSize: 14 }}>{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeAdminKBFile(idx)}
                                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 12, cursor: 'pointer' }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <button 
                        type="submit" 
                        disabled={adminKBUploading || adminKBUploadFiles.length === 0}
                        style={{ 
                          background: (adminKBUploading || adminKBUploadFiles.length === 0) ? '#f3f4f6' : '#10b981', 
                          color: (adminKBUploading || adminKBUploadFiles.length === 0) ? '#b6bbc6' : '#fff', 
                          border: 'none', 
                          borderRadius: 8, 
                          padding: '12px 24px', 
                          fontWeight: 600, 
                          fontSize: '1rem', 
                          cursor: (adminKBUploading || adminKBUploadFiles.length === 0) ? 'not-allowed' : 'pointer' 
                        }}
                      >
                        {adminKBUploading ? 'Uploading...' : `Upload ${adminKBUploadFiles.length} File(s)`}
                      </button>
                    </div>
                  </form>
                )}

                {/* Messages */}
                {adminKBError && <div style={{ color: 'red', marginBottom: 8, padding: 8, background: '#fef2f2', borderRadius: 6 }}>{adminKBError}</div>}
                {adminKBSuccess && <div style={{ color: 'green', marginBottom: 8, padding: 8, background: '#f0fdf4', borderRadius: 6 }}>{adminKBSuccess}</div>}
              </>
            )}
          </div>
        </div>
      )}

    </>
  );
};

export default AgentDetails; 
